/**
 * Audio feedback module — synthesised card sounds using the Web Audio API.
 *
 * Generates card-handling sounds at runtime using filtered white noise
 * and short oscillator bursts. No audio files are needed — sounds are
 * pre-rendered into AudioBuffers via OfflineAudioContext at startup,
 * then peak-normalised so relative volume is controlled by a single
 * per-sound volume map.
 *
 * Respects the iOS "Ambient" audio session (via cordova-allow-background-audio)
 * so sounds mix with the user's currently-playing music.
 *
 * On iOS, the AudioContext must be resumed from within a user gesture handler.
 * Call `unlockAudio()` on the first touch to satisfy this requirement.
 *
 * Usage:
 *   import { playSound, unlockAudio } from './audioFunctions'
 *   playSound('lift')           // on card pickup
 *   playSound('place')          // on card drop
 *   playSound('draw')           // on stock tap
 *   playSound('flip')           // on face-down card reveal
 *   playSound('autoFoundation') // on double-tap to foundation
 *   playSound('undo')           // on undo
 *   playSound('recycle')        // on waste → stock recycle
 *   playSound('shuffle')        // on new game deal
 *   playSound('autocomplete')   // on autocomplete start
 *   playSound('win')            // on victory
 */ var $7629cc56581bca5c$export$812e1401122ad355 = /*#__PURE__*/ function(CurrentAppState) {
    /** The main game board — cards are interactive. */ CurrentAppState[CurrentAppState["Playing"] = 0] = "Playing";
    /** Settings/theme menu overlay is open. */ CurrentAppState[CurrentAppState["Menu"] = 1] = "Menu";
    /** Win animation screen after all foundations are filled. */ CurrentAppState[CurrentAppState["Completed"] = 2] = "Completed";
    return CurrentAppState;
}({});


const $0e92ad87971fbdf7$export$d9a33280f07116d9 = {
    HighContrastTheme: {
        displayName: 'Midnight',
        backgroundColor: 'black',
        buttonNames: 'DefaultButtons',
        cardFaceColor: '#E8E8E8',
        cardBackColor: '#2A2A3A',
        cardBackName: 'midnight-geometric',
        placeholderColor: '#FFFFFF33',
        highlightColor: '#FFFFFF',
        fillColor: '#FFFFFF',
        textColor: '#FFFFFF',
        textColorShadow: '#FFFFFF33',
        redCardColor: '#6080A8',
        blackCardColor: '#1A1A2E',
        cardBorderColor: 'rgba(0, 0, 0, 0.30)'
    },
    ClassicGreenTheme: {
        displayName: 'Classic',
        backgroundColor: '#1B5E20',
        buttonNames: 'DefaultButtons',
        cardFaceColor: '#FFF8E7',
        cardBackColor: '#6B1C2A',
        cardBackName: 'classic-plaid',
        placeholderColor: '#FFFFFF22',
        highlightColor: '#E0D89A',
        fillColor: '#E0D89A',
        textColor: '#E0D89A',
        textColorShadow: '#E0D89A33',
        redCardColor: '#8B1A2B',
        blackCardColor: '#1B4420',
        cardBorderColor: 'rgba(0, 0, 0, 0.25)'
    },
    PleasedPinkTheme: {
        displayName: 'Coral',
        backgroundColor: '#C12849',
        buttonNames: 'PleasedPinkTheme',
        cardFaceColor: '#FFFFFF',
        cardBackColor: '#8B2040',
        cardBackName: 'coral-elegant',
        placeholderColor: '#F8E5EB77',
        highlightColor: '#F8E5EB',
        fillColor: '#F8E5EB',
        textColor: '#F8E5EB',
        textColorShadow: '#F8E5EB77',
        redCardColor: '#B91252',
        blackCardColor: '#3A3A4A',
        cardBorderColor: 'rgba(0, 0, 0, 0.25)'
    },
    SoftBlueTheme: {
        displayName: 'Ocean',
        backgroundColor: '#268bd2',
        buttonNames: 'SoftBlueTheme',
        cardFaceColor: '#F0F4F8',
        cardBackColor: '#1A3A5C',
        cardBackName: 'ocean-waves',
        placeholderColor: '#DFDCECAA',
        highlightColor: '#DFDCEC',
        fillColor: '#DFDCEC',
        textColor: '#DFDCEC',
        textColorShadow: '#DFDCECAA',
        redCardColor: '#C05030',
        blackCardColor: '#1A3050',
        cardBorderColor: 'rgba(0, 0, 0, 0.25)'
    },
    HappyPurpleTheme: {
        displayName: 'Twilight',
        backgroundColor: '#292D3E',
        buttonNames: 'DefaultButtons',
        cardFaceColor: '#F5F0FA',
        cardBackColor: '#5E35A0',
        cardBackName: 'twilight-celestial',
        placeholderColor: '#B996E633',
        highlightColor: '#B996E6',
        fillColor: '#B996E6',
        textColor: '#B996E6',
        textColorShadow: '#B996E633',
        redCardColor: '#6A3E96',
        blackCardColor: '#2A2A40',
        cardBorderColor: 'rgba(0, 0, 0, 0.25)'
    },
    ForestTheme: {
        displayName: 'Forest',
        backgroundColor: '#33402E',
        buttonNames: 'DefaultButtons',
        cardFaceColor: '#FBF5EC',
        cardBackColor: '#6B3A28',
        cardBackName: 'forest-nature',
        placeholderColor: '#D4A56A33',
        highlightColor: '#D4A56A',
        fillColor: '#D4A56A',
        textColor: '#D4A56A',
        textColorShadow: '#D4A56A33',
        redCardColor: '#C45A30',
        blackCardColor: '#2A3820',
        cardBorderColor: 'rgba(0, 0, 0, 0.25)'
    }
};


/**
 * User-configurable game settings, persisted to localStorage.
 *
 * Loaded on startup by `settingsManager.loadSettings()` and saved
 * whenever the user changes a setting through the menu.
 */ const $544aeff89210c458$export$62680b65e63e14ec = {
    drawCount: 1,
    themeName: 'HappyPurpleTheme',
    handedness: 'right',
    cardBackDesign: 'pattern',
    autoFlipCards: true,
    hapticsEnabled: true,
    soundEnabled: true,
    animationsEnabled: true
};


const $b66bebf5d76faeef$var$SETTINGS_KEY = 'solitaire-settings';
/**
 * Validate that a parsed object has the expected shape for GameSettings.
 * Returns a sanitised settings object merged with defaults, or null if
 * the input is clearly not a settings object (e.g. wrong type at the root).
 */ function $b66bebf5d76faeef$var$validateSettings(parsed) {
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const obj = parsed;
    // Sanitise individual fields — fall back to defaults for invalid values
    const drawCount = obj.drawCount === 1 || obj.drawCount === 3 ? obj.drawCount : (0, $544aeff89210c458$export$62680b65e63e14ec).drawCount;
    const handedness = obj.handedness === 'left' || obj.handedness === 'right' ? obj.handedness : (0, $544aeff89210c458$export$62680b65e63e14ec).handedness;
    const cardBackDesign = obj.cardBackDesign === 'pattern' || obj.cardBackDesign === 'solid' ? obj.cardBackDesign : (0, $544aeff89210c458$export$62680b65e63e14ec).cardBackDesign;
    const themeName = typeof obj.themeName === 'string' ? obj.themeName : (0, $544aeff89210c458$export$62680b65e63e14ec).themeName;
    return {
        ...(0, $544aeff89210c458$export$62680b65e63e14ec),
        ...parsed,
        drawCount: // Overwrite with explicitly validated values
        drawCount,
        handedness: handedness,
        cardBackDesign: cardBackDesign,
        themeName: themeName,
        // Booleans — coerce to boolean, defaulting if missing/wrong type
        autoFlipCards: typeof obj.autoFlipCards === 'boolean' ? obj.autoFlipCards : (0, $544aeff89210c458$export$62680b65e63e14ec).autoFlipCards,
        hapticsEnabled: typeof obj.hapticsEnabled === 'boolean' ? obj.hapticsEnabled : (0, $544aeff89210c458$export$62680b65e63e14ec).hapticsEnabled,
        soundEnabled: typeof obj.soundEnabled === 'boolean' ? obj.soundEnabled : (0, $544aeff89210c458$export$62680b65e63e14ec).soundEnabled,
        animationsEnabled: typeof obj.animationsEnabled === 'boolean' ? obj.animationsEnabled : (0, $544aeff89210c458$export$62680b65e63e14ec).animationsEnabled
    };
}
function $b66bebf5d76faeef$export$4b80e08960f15b4f(settings) {
    try {
        localStorage.setItem($b66bebf5d76faeef$var$SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
        console.error('Failed to save settings:', error);
    }
}
function $b66bebf5d76faeef$export$54453bb1ffd2fd71() {
    try {
        const stored = localStorage.getItem($b66bebf5d76faeef$var$SETTINGS_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            const validated = $b66bebf5d76faeef$var$validateSettings(parsed);
            if (validated) return validated;
            console.warn('Stored settings failed validation, using defaults');
        }
    } catch (error) {
        console.warn('Failed to load settings, using defaults:', error);
    }
    return {
        ...(0, $544aeff89210c458$export$62680b65e63e14ec)
    };
}


const $d7e89e4a237bfcb6$var$STATE_KEY = 'solitaire-game-state';
// ─── Debounced save ─────────────────────────────────────────────────────────
let $d7e89e4a237bfcb6$var$_saveTimer = null;
let $d7e89e4a237bfcb6$var$_pendingState = null;
const $d7e89e4a237bfcb6$var$SAVE_DEBOUNCE_MS = 300;
/** Internal: perform the actual localStorage write. */ function $d7e89e4a237bfcb6$var$writeToDisk(state) {
    try {
        if (state && state.length > 0) localStorage.setItem($d7e89e4a237bfcb6$var$STATE_KEY, JSON.stringify(state));
    } catch (error) {
        console.error('Failed to save game state:', error);
    }
}
function $d7e89e4a237bfcb6$export$384cd953322e3851(state) {
    $d7e89e4a237bfcb6$var$_pendingState = state;
    if ($d7e89e4a237bfcb6$var$_saveTimer !== null) clearTimeout($d7e89e4a237bfcb6$var$_saveTimer);
    $d7e89e4a237bfcb6$var$_saveTimer = setTimeout(()=>{
        $d7e89e4a237bfcb6$var$_saveTimer = null;
        if ($d7e89e4a237bfcb6$var$_pendingState) {
            $d7e89e4a237bfcb6$var$writeToDisk($d7e89e4a237bfcb6$var$_pendingState);
            $d7e89e4a237bfcb6$var$_pendingState = null;
        }
    }, $d7e89e4a237bfcb6$var$SAVE_DEBOUNCE_MS);
}
function $d7e89e4a237bfcb6$export$44d8bb90d8f68c33() {
    if ($d7e89e4a237bfcb6$var$_saveTimer !== null) {
        clearTimeout($d7e89e4a237bfcb6$var$_saveTimer);
        $d7e89e4a237bfcb6$var$_saveTimer = null;
    }
    if ($d7e89e4a237bfcb6$var$_pendingState) {
        $d7e89e4a237bfcb6$var$writeToDisk($d7e89e4a237bfcb6$var$_pendingState);
        $d7e89e4a237bfcb6$var$_pendingState = null;
    }
}
function $d7e89e4a237bfcb6$export$756e5d66bdd28395() {
    try {
        const stored = localStorage.getItem($d7e89e4a237bfcb6$var$STATE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Validate the loaded state is an array with at least one entry
            if (!Array.isArray(parsed) || parsed.length === 0) return null;
            // Spot-check the last entry (current state) for expected properties
            const last = parsed[parsed.length - 1];
            if (!last || !Array.isArray(last.foundation) || last.foundation.length !== 4 || !Array.isArray(last.stock) || !Array.isArray(last.waste) || !Array.isArray(last.tableau) || last.tableau.length !== 7) {
                console.warn('Saved game state failed structural validation, discarding');
                return null;
            }
            return parsed;
        }
    } catch (error) {
        console.warn('Failed to load game state:', error);
    }
    return null;
}
function $d7e89e4a237bfcb6$export$4e3d892664daf908() {
    try {
        localStorage.removeItem($d7e89e4a237bfcb6$var$STATE_KEY);
    } catch (error) {
        console.error('Failed to clear game state:', error);
    }
}




/**
 * Centralized card position resolution.
 *
 * This module is the SINGLE SOURCE OF TRUTH for where a card should be rendered.
 * Instead of multiple systems (animation, drag, assignCardCoordinates) all
 * mutating card.x/y directly, they write to separate overlay maps here.
 *
 * Resolution priority (highest wins):
 *   1. Drag overlay  — card is being dragged by the user
 *   2. Animation overlay — card is mid-animation
 *   3. card.x / card.y — the logical "home" position from assignCardCoordinates
 *
 * This eliminates the bounce bug: card.x/y are always the correct final position,
 * and transient states (drag, animation) are layered on top without mutation.
 */ // ─── Overlay Maps ───────────────────────────────────────────────────
// Use the Card object reference as key (WeakMap) so entries are GC'd
// when cards are discarded.
const $f08e1321bb9509a9$var$animationOverlay = new Map();
const $f08e1321bb9509a9$var$dragOverlay = new Map();
function $f08e1321bb9509a9$export$1bbf3d74e37df777(card) {
    return $f08e1321bb9509a9$var$animationOverlay.has(card);
}
function $f08e1321bb9509a9$export$2fe829b149c28e00(card, x, y) {
    $f08e1321bb9509a9$var$animationOverlay.set(card, {
        x: x,
        y: y
    });
}
function $f08e1321bb9509a9$export$af62a74095e6d36a(card) {
    $f08e1321bb9509a9$var$animationOverlay.delete(card);
}
function $f08e1321bb9509a9$export$60916053becc6a4() {
    $f08e1321bb9509a9$var$animationOverlay.clear();
}
function $f08e1321bb9509a9$export$7b7576d93270f13a(card, x, y) {
    $f08e1321bb9509a9$var$dragOverlay.set(card, {
        x: x,
        y: y
    });
}
function $f08e1321bb9509a9$export$ee060b92c30dc31a() {
    $f08e1321bb9509a9$var$dragOverlay.clear();
}
function $f08e1321bb9509a9$export$1a73458087cc41c4(card) {
    // Priority 1: Drag
    const dragPos = $f08e1321bb9509a9$var$dragOverlay.get(card);
    if (dragPos) return dragPos;
    // Priority 2: Animation
    const animPos = $f08e1321bb9509a9$var$animationOverlay.get(card);
    if (animPos) return animPos;
    // Priority 3: Logical home position
    return {
        x: card.x,
        y: card.y
    };
}



function $0f5b71ede2b50336$export$e79bc7cac697f199() {
    return (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness === 'left';
}
function $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() {
    return (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth > (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
}
function $0f5b71ede2b50336$export$370b3d6f888e2af4(i) {
    return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardYOffset + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing) * i);
}
/**
 * Y position for button row i (0-indexed).
 * Uses a tighter step (cardWidth + cardSpacing) so Menu, New Game, and Undo
 * cluster together without the large gaps of the full card-height grid.
 */ function $0f5b71ede2b50336$var$buttonRowY(i) {
    const baseY = $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() ? $0f5b71ede2b50336$export$370b3d6f888e2af4(1) : (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardYOffset;
    return Math.floor(baseY + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing) * i);
}
// ─── Landscape helpers ───────────────────────────────────────────────────
function $0f5b71ede2b50336$var$landscapeTopRowY() {
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(0);
}
function $0f5b71ede2b50336$var$landscapeTableauRowY() {
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(2);
}
let $0f5b71ede2b50336$var$_lscCacheKey = '';
let $0f5b71ede2b50336$var$_lscCachedResult = null;
function $0f5b71ede2b50336$var$landscapeClusterMetrics() {
    // Build a cache key from all inputs that affect the result
    const key = `${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth}|${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing}|${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth}|${$0f5b71ede2b50336$export$e79bc7cac697f199()}`;
    if ($0f5b71ede2b50336$var$_lscCachedResult && key === $0f5b71ede2b50336$var$_lscCacheKey) return $0f5b71ede2b50336$var$_lscCachedResult;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const spacing = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing;
    const gap = Math.max(spacing, Math.floor(cw * 0.2));
    const middleGap = Math.max(cw, Math.floor(cw * 1.1));
    const foundationWidth = cw * (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_FOUNDATIONS + gap * ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_FOUNDATIONS - 1);
    const stockWidth = cw * 2 + gap;
    const stockOnLeft = $0f5b71ede2b50336$export$e79bc7cac697f199();
    const centerX = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth / 2;
    const leftWidth = stockOnLeft ? stockWidth : foundationWidth;
    const rightWidth = stockOnLeft ? foundationWidth : stockWidth;
    let leftX = Math.floor(centerX - middleGap / 2 - leftWidth);
    let rightX = Math.floor(centerX + middleGap / 2);
    const padding = Math.max(4, Math.floor(cw * 0.1));
    const minX = Math.min(leftX, rightX);
    const maxX = Math.max(leftX + leftWidth, rightX + rightWidth);
    let shift = 0;
    if (minX < padding) shift = padding - minX;
    if (maxX + shift > (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - padding) shift -= maxX + shift - ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - padding);
    leftX += shift;
    rightX += shift;
    const stockClusterX = stockOnLeft ? leftX : rightX;
    const foundationClusterX = stockOnLeft ? rightX : leftX;
    const stockX = stockOnLeft ? stockClusterX : stockClusterX + cw + gap;
    const wasteX = stockOnLeft ? stockClusterX + cw + gap : stockClusterX;
    const result = {
        gap: gap,
        stockOnLeft: stockOnLeft,
        stockX: stockX,
        wasteX: wasteX,
        foundationClusterX: foundationClusterX
    };
    $0f5b71ede2b50336$var$_lscCacheKey = key;
    $0f5b71ede2b50336$var$_lscCachedResult = result;
    return result;
}
function $0f5b71ede2b50336$var$landscapeTableauMetrics() {
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const spacing = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing;
    const sidePadding = Math.max(spacing, Math.floor(cw * 0.35));
    const availableWidth = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - sidePadding * 2;
    const rawGap = Math.floor((availableWidth - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU * cw) / ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU - 1));
    const gap = Math.max(2, rawGap);
    const totalWidth = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU * cw + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU - 1) * gap;
    const startX = Math.floor(((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - totalWidth) / 2);
    return {
        startX: startX,
        gap: gap
    };
}
// ─── X columns ──────────────────────────────────────────────────────────
/** X for the side column (thumb side): menu, undo, autocomplete, foundations, stock */ function $0f5b71ede2b50336$var$sideColumnX() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) {
        const { stockX: stockX } = $0f5b71ede2b50336$var$landscapeClusterMetrics();
        return stockX;
    }
    if ($0f5b71ede2b50336$export$e79bc7cac697f199()) return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.35);
    return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 1.35);
}
/** X for the main column: tableau base cards, waste */ function $0f5b71ede2b50336$var$mainColumnX() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) {
        const { wasteX: wasteX } = $0f5b71ede2b50336$var$landscapeClusterMetrics();
        return wasteX;
    }
    if ($0f5b71ede2b50336$export$e79bc7cac697f199()) return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 1.7);
    return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 2.7);
}
function $0f5b71ede2b50336$export$7a8b0cb957188ec0() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - $0f5b71ede2b50336$var$sideColumnX() - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
    if ($0f5b71ede2b50336$export$e79bc7cac697f199()) return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 1.35);
    return Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.35);
}
/** X to center a card-width element horizontally on screen */ function $0f5b71ede2b50336$var$centerColumnX() {
    return Math.floor(((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth - (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth) / 2);
}
function $0f5b71ede2b50336$export$889074c399efa9cf() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeClusterMetrics().stockX;
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$72029e8b50497416() {
    return $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() ? $0f5b71ede2b50336$var$landscapeTopRowY() : $0f5b71ede2b50336$export$370b3d6f888e2af4(8);
}
function $0f5b71ede2b50336$export$6fbb8003e6abbb0d() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeClusterMetrics().stockX;
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$1334760d304d1528() {
    return $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() ? $0f5b71ede2b50336$var$landscapeTopRowY() : $0f5b71ede2b50336$export$370b3d6f888e2af4(8);
}
function $0f5b71ede2b50336$export$5a7a218ad4c46c78() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeClusterMetrics().wasteX;
    return $0f5b71ede2b50336$var$mainColumnX();
}
function $0f5b71ede2b50336$export$9964e256ead13668() {
    return $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() ? $0f5b71ede2b50336$var$landscapeTopRowY() : $0f5b71ede2b50336$export$370b3d6f888e2af4(8);
}
function $0f5b71ede2b50336$export$c7bd02130ef655ae() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeClusterMetrics().wasteX;
    return $0f5b71ede2b50336$var$mainColumnX();
}
function $0f5b71ede2b50336$export$2453d778905123db() {
    return $0f5b71ede2b50336$export$72f5d9a44bd9a7f0() ? $0f5b71ede2b50336$var$landscapeTopRowY() : $0f5b71ede2b50336$export$370b3d6f888e2af4(8);
}
function $0f5b71ede2b50336$export$f8ddd168b43d671a(_i) {
    if (!$0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$mainColumnX();
    const { startX: startX, gap: gap } = $0f5b71ede2b50336$var$landscapeTableauMetrics();
    const columnIndex = $0f5b71ede2b50336$export$e79bc7cac697f199() ? (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU - 1 - _i : _i;
    return Math.floor(startX + columnIndex * ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth + gap));
}
function $0f5b71ede2b50336$export$38cc5b0bc1d504c6(i) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeTableauRowY();
    // Tableau column i sits on grid row (i + 1)
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(i + 1);
}
function $0f5b71ede2b50336$export$3b61ad18d9d8d80f(_i) {
    if (!$0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$mainColumnX();
    const { startX: startX, gap: gap } = $0f5b71ede2b50336$var$landscapeTableauMetrics();
    const columnIndex = $0f5b71ede2b50336$export$e79bc7cac697f199() ? (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU - 1 - _i : _i;
    return Math.floor(startX + columnIndex * ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth + gap));
}
function $0f5b71ede2b50336$export$bb9e88494fba5611(i) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeTableauRowY();
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(i + 1);
}
function $0f5b71ede2b50336$export$77ae4c367ef625c2(isFaceDown) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight * (isFaceDown ? 0.12 : 0.28);
    return (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * (isFaceDown ? 0.08 : 0.42);
}
function $0f5b71ede2b50336$export$b20e22ecc0dfefa9(cards) {
    let offset = 0;
    for(let i = 0; i < cards.length - 1; i++)offset += $0f5b71ede2b50336$export$77ae4c367ef625c2(cards[i].isFaceDown);
    return offset;
}
function $0f5b71ede2b50336$export$84ce64f2330f553a(index = 0) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) {
        const { foundationClusterX: foundationClusterX, gap: gap } = $0f5b71ede2b50336$var$landscapeClusterMetrics();
        return Math.floor(foundationClusterX + index * ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth + gap));
    }
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$7cb2c3b01d2fdd74(i) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeTopRowY();
    // Foundation i sits on grid row (i + 3)
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(i + 3);
}
function $0f5b71ede2b50336$export$6df8801043afdfc1(index = 0) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) {
        const { foundationClusterX: foundationClusterX, gap: gap } = $0f5b71ede2b50336$var$landscapeClusterMetrics();
        return Math.floor(foundationClusterX + index * ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth + gap));
    }
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$24ba40c7ccbb801d(i) {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$landscapeTopRowY();
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(i + 3);
}
function $0f5b71ede2b50336$export$1f614aa49af839fa() {
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$6d939bb5033370b0() {
    return $0f5b71ede2b50336$var$buttonRowY(0);
}
function $0f5b71ede2b50336$export$833ed0fe1212d409() {
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$f81ed66b3906beb3() {
    return $0f5b71ede2b50336$var$buttonRowY(1);
}
function $0f5b71ede2b50336$export$1ecdc1be7bd1a749() {
    return $0f5b71ede2b50336$export$7a8b0cb957188ec0();
}
function $0f5b71ede2b50336$export$d50ecd2fde22a762() {
    return $0f5b71ede2b50336$var$buttonRowY(1);
}
function $0f5b71ede2b50336$export$26fe8f1cbe2626e5() {
    return $0f5b71ede2b50336$var$sideColumnX();
}
function $0f5b71ede2b50336$export$70c3227cdbebd1b8() {
    return $0f5b71ede2b50336$var$buttonRowY(2);
}
function $0f5b71ede2b50336$export$51646a58b06b5693() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$sideColumnX();
    return $0f5b71ede2b50336$export$7a8b0cb957188ec0();
}
function $0f5b71ede2b50336$export$ff0ff2fb1fcb2a95() {
    if ($0f5b71ede2b50336$export$72f5d9a44bd9a7f0()) return $0f5b71ede2b50336$var$buttonRowY(3);
    return $0f5b71ede2b50336$export$370b3d6f888e2af4(8); // same row as stock/waste — bottom of screen, opposite side from deck
}


function $b3d87dc43fab0d75$export$302696430f74365c() {
    const cardH = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight || 100;
    return Math.max(6, Math.min(30, Math.floor(cardH * 0.13)));
}
function $b3d87dc43fab0d75$export$2489d32780f587c9(startX, startY, width, height, ctx) {
    // Minimal placeholder — filled rounded rect, no border, low visual weight
    ctx.beginPath();
    ctx.roundRect(startX, startY, width, height, $b3d87dc43fab0d75$export$302696430f74365c() * 0.6);
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.placeholderColor;
    ctx.fill();
}
function $b3d87dc43fab0d75$var$drawCardBack(startX, startY, width, height, ctx) {
    const radius = $b3d87dc43fab0d75$export$302696430f74365c();
    // Draw rounded rectangle background — no shadowBlur (expensive on iOS Safari)
    ctx.beginPath();
    ctx.roundRect(startX, startY, width, height, radius);
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.cardBackColor;
    ctx.fill();
    // Draw card back image only if pattern mode is enabled
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.cardBackDesign === 'pattern' && (0, $13e781a56ac394f5$export$58162e8b0e9a665e) && (0, $13e781a56ac394f5$export$58162e8b0e9a665e).complete) {
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(startX, startY, width, height, radius);
        ctx.clip();
        ctx.drawImage((0, $13e781a56ac394f5$export$58162e8b0e9a665e), startX, startY, width, height);
        ctx.restore();
    }
    // Border stroke for edge definition (V3: face-down cards were just solid blobs)
    ctx.beginPath();
    ctx.roundRect(startX, startY, width, height, radius);
    ctx.lineWidth = Math.max(1.5, width * 0.03);
    ctx.strokeStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.cardBorderColor;
    ctx.stroke();
}
function $b3d87dc43fab0d75$export$1ed0c0bc398b6246(ctx, cx, cy, size) {
    const h = size * 0.46;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.beginPath();
    ctx.moveTo(0, h * 0.95);
    ctx.bezierCurveTo(-h * 0.08, h * 0.7, -h * 0.85, h * 0.15, -h * 0.85, -h * 0.18);
    ctx.bezierCurveTo(-h * 0.85, -h * 0.7, -h * 0.42, -h * 0.9, 0, -h * 0.52);
    ctx.bezierCurveTo(h * 0.42, -h * 0.9, h * 0.85, -h * 0.7, h * 0.85, -h * 0.18);
    ctx.bezierCurveTo(h * 0.85, h * 0.15, h * 0.08, h * 0.7, 0, h * 0.95);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}
function $b3d87dc43fab0d75$var$drawDiamond(ctx, cx, cy, size) {
    const hw = size * 0.32, hh = size * 0.48;
    ctx.beginPath();
    ctx.moveTo(cx, cy - hh);
    ctx.bezierCurveTo(cx + hw * 0.15, cy - hh * 0.55, cx + hw * 0.7, cy - hh * 0.12, cx + hw, cy);
    ctx.bezierCurveTo(cx + hw * 0.7, cy + hh * 0.12, cx + hw * 0.15, cy + hh * 0.55, cx, cy + hh);
    ctx.bezierCurveTo(cx - hw * 0.15, cy + hh * 0.55, cx - hw * 0.7, cy + hh * 0.12, cx - hw, cy);
    ctx.bezierCurveTo(cx - hw * 0.7, cy - hh * 0.12, cx - hw * 0.15, cy - hh * 0.55, cx, cy - hh);
    ctx.closePath();
    ctx.fill();
}
function $b3d87dc43fab0d75$var$drawSpade(ctx, cx, cy, size) {
    const h = size * 0.46;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.beginPath();
    ctx.moveTo(0, -h * 0.95);
    ctx.bezierCurveTo(-h * 0.08, -h * 0.6, -h * 0.78, -h * 0.15, -h * 0.78, h * 0.18);
    ctx.bezierCurveTo(-h * 0.78, h * 0.52, -h * 0.35, h * 0.56, -h * 0.05, h * 0.35);
    ctx.quadraticCurveTo(-h * 0.15, h * 0.65, -h * 0.25, h * 0.88);
    ctx.lineTo(h * 0.25, h * 0.88);
    ctx.quadraticCurveTo(h * 0.15, h * 0.65, h * 0.05, h * 0.35);
    ctx.bezierCurveTo(h * 0.35, h * 0.56, h * 0.78, h * 0.52, h * 0.78, h * 0.18);
    ctx.bezierCurveTo(h * 0.78, -h * 0.15, h * 0.08, -h * 0.6, 0, -h * 0.95);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}
function $b3d87dc43fab0d75$var$drawClub(ctx, cx, cy, size) {
    const h = size * 0.46, r = h * 0.3;
    ctx.save();
    ctx.translate(cx, cy);
    // Fill center triangle to eliminate gap between lobes
    ctx.beginPath();
    ctx.moveTo(0, -h * 0.32);
    ctx.lineTo(-h * 0.3, h * 0.1);
    ctx.lineTo(h * 0.3, h * 0.1);
    ctx.closePath();
    ctx.fill();
    // Each lobe drawn separately to avoid canvas connecting arcs with lines
    ctx.beginPath();
    ctx.arc(0, -h * 0.32, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(-h * 0.3, h * 0.1, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(h * 0.3, h * 0.1, r, 0, Math.PI * 2);
    ctx.fill();
    // Stem
    ctx.beginPath();
    ctx.moveTo(-h * 0.08, h * 0.1);
    ctx.quadraticCurveTo(-h * 0.1, h * 0.55, -h * 0.25, h * 0.88);
    ctx.lineTo(h * 0.25, h * 0.88);
    ctx.quadraticCurveTo(h * 0.1, h * 0.55, h * 0.08, h * 0.1);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}
function $b3d87dc43fab0d75$var$drawSuitSymbol(ctx, suitType, cx, cy, size) {
    switch(suitType){
        case 'H':
            $b3d87dc43fab0d75$export$1ed0c0bc398b6246(ctx, cx, cy, size);
            break;
        case 'D':
            $b3d87dc43fab0d75$var$drawDiamond(ctx, cx, cy, size);
            break;
        case 'S':
            $b3d87dc43fab0d75$var$drawSpade(ctx, cx, cy, size);
            break;
        case 'C':
            $b3d87dc43fab0d75$var$drawClub(ctx, cx, cy, size);
            break;
    }
}
const $b3d87dc43fab0d75$export$7967f0b16ea92252 = '"Avenir Next", "Avenir", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif';
function $b3d87dc43fab0d75$var$drawCardFace(card, x, y, width, height, ctx, isTopCard) {
    const radius = $b3d87dc43fab0d75$export$302696430f74365c();
    const isRed = card.color === 'red';
    // 1. Card face background — theme-aware to avoid white-on-white with white card backs
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.cardFaceColor;
    ctx.fill();
    // 2. Heavier border for card edge definition (V3: much more visible than V2's 0.12 alpha)
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.lineWidth = Math.max(1.5, width * 0.03);
    ctx.strokeStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.cardBorderColor;
    ctx.stroke();
    // Clip to card bounds so text doesn't bleed outside rounded corners
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, radius);
    ctx.clip();
    // Ink color: red for ♥♦, near-black for ♠♣
    const inkColor = isRed ? (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.redCardColor : (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.blackCardColor;
    // 3. Large centered suit symbol — only on top/fully-visible cards (V4/V5)
    //    Partially hidden cards in cascades only show the corner value.
    //    This prevents the suit from peeking out of the exposed sliver.
    //    V5: Canvas vector paths replace Unicode characters for pixel-perfect rendering.
    if (isTopCard) {
        const suitSize = Math.floor(height * 0.65);
        ctx.fillStyle = inkColor;
        $b3d87dc43fab0d75$var$drawSuitSymbol(ctx, card.type, x + width * 0.5, y + height * 0.54, suitSize);
    }
    // 4. Corner: value text + small suit pip below it
    //    Position mirrors with handedness — always on the exposed cascade edge
    //    Right-handed: cascades left → right edge exposed → top-right corner
    //    Left-handed: cascades right → left edge exposed → top-left corner
    //    Text is centered in the exposed sliver (step/2 from the card edge)
    //    A mirrored (rotated 180°) rank+pip pair appears on the opposite bottom corner
    const leftCorner = (0, $0f5b71ede2b50336$export$e79bc7cac697f199)();
    const cascadeStep = (0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(false);
    const padX = cascadeStep / 2;
    const padY = height * 0.06;
    const cornerX = leftCorner ? x + padX : x + width - padX;
    const oppositeCornerX = leftCorner ? x + width - padX : x + padX;
    const cornerAlign = 'center';
    // Value text — heavy weight for legibility at small sizes
    const valueFontSize = Math.floor(height * 0.18);
    ctx.font = `800 ${valueFontSize}px ${$b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.fillStyle = inkColor;
    ctx.textAlign = cornerAlign;
    ctx.textBaseline = 'top';
    ctx.fillText(card.value, cornerX, y + padY);
    // Suit pip + mirrored rank+pip — only on partially covered cards whose center suit is hidden
    if (!isTopCard) {
        const pipSize = Math.floor(valueFontSize * 1.0);
        const pipY = y + padY + valueFontSize + Math.floor(height * 0.04);
        $b3d87dc43fab0d75$var$drawSuitSymbol(ctx, card.type, cornerX, pipY + pipSize * 0.4, pipSize);
        // Mirrored rank + pip on opposite bottom corner (rotated 180°)
        ctx.save();
        ctx.translate(oppositeCornerX, y + height - padY);
        ctx.rotate(Math.PI);
        ctx.font = `800 ${valueFontSize}px ${$b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.fillStyle = inkColor;
        ctx.textAlign = cornerAlign;
        ctx.textBaseline = 'top';
        ctx.fillText(card.value, 0, 0);
        $b3d87dc43fab0d75$var$drawSuitSymbol(ctx, card.type, 0, valueFontSize + Math.floor(height * 0.04) + pipSize * 0.4, pipSize);
        ctx.restore();
    }
    ctx.restore();
}
function $b3d87dc43fab0d75$export$ebab430dd53d463d(card, ctx, isTopCard = true) {
    // Resolve the card's render position through the centralized position system.
    // This consults drag overlay, then animation overlay, then card.x/y (home position).
    const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
    const { x: x, y: y } = pos;
    if (card.isFaceDown) $b3d87dc43fab0d75$var$drawCardBack(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight, ctx);
    else $b3d87dc43fab0d75$var$drawCardFace(card, x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight, ctx, isTopCard);
}
function $b3d87dc43fab0d75$export$78a64eb29b8df76c(card, ctx, x, y) {
    if (card.isFaceDown) $b3d87dc43fab0d75$var$drawCardBack(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight, ctx);
    else $b3d87dc43fab0d75$var$drawCardFace(card, x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight, ctx, true);
}
function $b3d87dc43fab0d75$var$drawText(text, startX, startY, fontSize, ctx) {
    ctx.save();
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.textColor;
    // Create responsive font size that scales with screen but has reasonable limits
    // Use min() to cap the maximum size, and scale based on smaller dimension for consistency
    const minDimension = Math.min((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
    const scaledFontSize = Math.min(Math.floor(fontSize * minDimension / 100), // Scale based on smaller dimension
    32 // Cap maximum font size at 32px
    );
    ctx.font = `${scaledFontSize}px Avenir Book`;
    // No shadowBlur — expensive on iOS Safari. Use thin stroke for text contrast if needed.
    ctx.fillText(text, startX, startY);
    ctx.restore();
}



function $299339de955f290d$export$bdc75d649a90bd24(card, foundationPile) {
    if (foundationPile.length === 0) return card.numValue === 1;
    const top = foundationPile[foundationPile.length - 1];
    return !top.isFaceDown && card.type === top.type && card.color === top.color && card.numValue === top.numValue + 1;
}
function $299339de955f290d$export$a0464b001ab0a988(card, tableauPile) {
    if (tableauPile.length === 0) return card.numValue === 13;
    const top = tableauPile[tableauPile.length - 1];
    return !top.isFaceDown && card.color !== top.color && card.numValue === top.numValue - 1;
}
function $299339de955f290d$export$52061974e8d1b67c(state) {
    return [
        ...state.stock,
        ...state.waste,
        ...state.tableau.flat(),
        ...state.foundation.flat()
    ];
}
function $299339de955f290d$export$1c0015b827d9aee(state, fallback) {
    return state.wasteVisibleCount !== undefined ? state.wasteVisibleCount : fallback;
}
function $299339de955f290d$export$101d5269b8ad7ea4(state, fallback) {
    const current = $299339de955f290d$export$1c0015b827d9aee(state, fallback);
    state.wasteVisibleCount = state.waste.length > 0 ? Math.max(1, current - 1) : 0;
}



function $30a068696fab8a07$export$34c0ba4aa7183cc5(ctx) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for (const foundation of currentState.foundation)// Foundation cards are stacked — only draw the top one (always fully visible)
    if (foundation.length > 0) {
        const topCard = foundation[foundation.length - 1];
        if ((0, $f08e1321bb9509a9$export$1bbf3d74e37df777)(topCard)) {
            // While the new top card is animating from source -> foundation,
            // keep the previous top visible so the foundation slot never appears empty.
            if (foundation.length > 1) (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(foundation[foundation.length - 2], ctx, true);
            continue;
        }
        (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(topCard, ctx, true);
    }
}
function $30a068696fab8a07$export$637f9f751eccb893(ctx) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for (const foundation of currentState.foundation){
        if (foundation.length === 0) continue;
        const topCard = foundation[foundation.length - 1];
        if ((0, $f08e1321bb9509a9$export$1bbf3d74e37df777)(topCard)) (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(topCard, ctx, true);
    }
}
function $30a068696fab8a07$export$559b888e12de6444(ctx) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const maxVisible = (0, $299339de955f290d$export$1c0015b827d9aee)(currentState, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED);
    const firstVisibleIndex = Math.max(0, currentState.waste.length - maxVisible);
    const wasteCards = currentState.waste.slice(firstVisibleIndex, currentState.waste.length);
    for(let i = 0; i < wasteCards.length; i++){
        const isTop = i === wasteCards.length - 1;
        (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(wasteCards[i], ctx, isTop);
    }
}
function $30a068696fab8a07$export$575c2a44bd34199e(ctx) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    // Stock cards are all stacked on top of each other — only draw the top one
    if (currentState.stock.length > 0) (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(currentState.stock[currentState.stock.length - 1], ctx, true);
}
function $30a068696fab8a07$export$b49875388cc6c6c3(ctx) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for (const tableau of currentState.tableau)for(let i = 0; i < tableau.length; i++){
        const isTop = i === tableau.length - 1;
        (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(tableau[i], ctx, isTop);
    }
}
function $30a068696fab8a07$export$75581f0c030577ec(ctx) {
    const draggedCards = (0, $13e781a56ac394f5$export$7f3c49b37340bfce)();
    for(let i = 0; i < draggedCards.length; i++)// When dragging a stack, only the bottom card (first) is fully visible;
    // but since they're being dragged freely, show suit on all of them
    (0, $b3d87dc43fab0d75$export$ebab430dd53d463d)(draggedCards[i], ctx, true);
}





function $35a1d072802c98cd$export$f8739f0d354a251(ctx) {
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU; i++){
        const x = (0, $0f5b71ede2b50336$export$3b61ad18d9d8d80f)(i);
        const y = (0, $0f5b71ede2b50336$export$bb9e88494fba5611)(i);
        const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.4;
        (0, $b3d87dc43fab0d75$export$2489d32780f587c9)(x + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth - size) / 2, y + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight - size) / 2, size, size, ctx);
    }
}
function $35a1d072802c98cd$export$9e532f80718cf410(ctx) {
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_FOUNDATIONS; i++){
        const x = (0, $0f5b71ede2b50336$export$6df8801043afdfc1)(i);
        const y = (0, $0f5b71ede2b50336$export$24ba40c7ccbb801d)(i);
        const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.4;
        (0, $b3d87dc43fab0d75$export$2489d32780f587c9)(x + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth - size) / 2, y + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight - size) / 2, size, size, ctx);
    }
}
function $35a1d072802c98cd$export$644db5322f7be988(ctx) {
    const x = (0, $0f5b71ede2b50336$export$c7bd02130ef655ae)();
    const y = (0, $0f5b71ede2b50336$export$2453d778905123db)();
    const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.4;
    (0, $b3d87dc43fab0d75$export$2489d32780f587c9)(x + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth - size) / 2, y + ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight - size) / 2, size, size, ctx);
}
function $35a1d072802c98cd$export$b80754d417af4fc(ctx) {
    const x = (0, $0f5b71ede2b50336$export$6fbb8003e6abbb0d)();
    const y = (0, $0f5b71ede2b50336$export$1334760d304d1528)();
    const radius = (0, $b3d87dc43fab0d75$export$302696430f74365c)();
    // Hollow outline at full card size — visually distinct from the filled
    // placeholders used for foundations/tableau/waste, but still reads as
    // a tappable empty slot.
    ctx.beginPath();
    ctx.roundRect(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight, radius);
    ctx.lineWidth = Math.max(1.5, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth * 0.025);
    ctx.strokeStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.placeholderColor;
    ctx.stroke();
}





/**
 * Shared color utility functions.
 *
 * Used by drawMenu.ts, drawTimer.ts, and drawButtons.ts for
 * luminance-aware rendering (light vs dark text/icons on theme backgrounds).
 */ /**
 * Parse a CSS color string to RGB values. Supports hex (#RGB, #RRGGBB),
 * named colors ('black', 'white'), and returns [0,0,0] for unparseable input.
 */ function $f9eed79712445f27$export$cb557c8471d45d6e(color) {
    const c = color.trim().toLowerCase();
    if (c === 'black') return [
        0,
        0,
        0
    ];
    if (c === 'white') return [
        255,
        255,
        255
    ];
    if (c.startsWith('#')) {
        const hex = c.slice(1);
        if (hex.length === 3) return [
            parseInt(hex[0] + hex[0], 16),
            parseInt(hex[1] + hex[1], 16),
            parseInt(hex[2] + hex[2], 16)
        ];
        if (hex.length >= 6) return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
        ];
    }
    return [
        0,
        0,
        0
    ];
}
/**
 * Returns relative luminance (0–1) per WCAG formula.
 * Values < 0.5 are "dark", >= 0.5 are "light".
 */ function $f9eed79712445f27$var$relativeLuminance(color) {
    const [r, g, b] = $f9eed79712445f27$export$cb557c8471d45d6e(color).map((c)=>{
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function $f9eed79712445f27$export$6b84b5f288b224da(color) {
    return $f9eed79712445f27$var$relativeLuminance(color) > 0.35;
}


// ─── Pressed state tracking ─────────────────────────────────────────────────
let $80cf1bdd1366d994$var$_pressedButtonId = null;
function $80cf1bdd1366d994$export$c892ad6354b65f11(id) {
    $80cf1bdd1366d994$var$_pressedButtonId = id;
}
function $80cf1bdd1366d994$export$250a0b6d55b74a2f() {
    $80cf1bdd1366d994$var$_pressedButtonId = null;
}
// Helper function to get icon color based on background
function $80cf1bdd1366d994$var$getIconColor() {
    const bgColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor;
    return (0, $f9eed79712445f27$export$6b84b5f288b224da)(bgColor) ? '#000000' : '#FFFFFF';
}
// Helper function to draw custom icon buttons with consistent style
function $80cf1bdd1366d994$var$drawIconButton(x, y, size, iconType, ctx, buttonId) {
    // Draw press highlight overlay if this button is pressed
    if ($80cf1bdd1366d994$var$_pressedButtonId === buttonId) {
        const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
        const overlayColor = lightBg ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)';
        const r = Math.floor(size * 0.22);
        ctx.save();
        ctx.fillStyle = overlayColor;
        ctx.beginPath();
        ctx.roundRect(x, y, size, size, r);
        ctx.fill();
        ctx.restore();
    }
    ctx.save();
    // Get icon color based on background brightness
    const iconColor = $80cf1bdd1366d994$var$getIconColor();
    // Thinner, more minimal stroke style
    ctx.fillStyle = iconColor;
    ctx.strokeStyle = iconColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(2, size * 0.05);
    const padding = size * 0.2;
    const iconSize = size - padding * 2;
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    switch(iconType){
        case 'menu':
            {
                const isMenuOpen = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Menu;
                if (isMenuOpen) {
                    // X icon — two diagonal lines crossing at center
                    const armLen = iconSize * 0.28;
                    ctx.beginPath();
                    ctx.moveTo(centerX - armLen, centerY - armLen);
                    ctx.lineTo(centerX + armLen, centerY + armLen);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(centerX + armLen, centerY - armLen);
                    ctx.lineTo(centerX - armLen, centerY + armLen);
                    ctx.stroke();
                } else {
                    // Three horizontal lines — hamburger menu
                    const lineSpacing = iconSize * 0.28;
                    const lineWidth = iconSize * 0.55;
                    const startX = centerX - lineWidth / 2;
                    ctx.beginPath();
                    ctx.moveTo(startX, centerY - lineSpacing);
                    ctx.lineTo(startX + lineWidth, centerY - lineSpacing);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(startX, centerY);
                    ctx.lineTo(startX + lineWidth, centerY);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(startX, centerY + lineSpacing);
                    ctx.lineTo(startX + lineWidth, centerY + lineSpacing);
                    ctx.stroke();
                }
                break;
            }
        case 'undo':
            {
                // Reply-style undo arrow as stroked path (consistent weight with other icons).
                // Horizontal line going left → 90° curved corner → vertical line going down,
                // with a chevron arrowhead at the left tip.
                // Path dimensions within the standard icon area
                const topY = centerY - iconSize * 0.15;
                const leftX = centerX - iconSize * 0.38;
                const rightX = centerX + iconSize * 0.35;
                const bottomY = centerY + iconSize * 0.38;
                const cr = iconSize * 0.22; // corner radius
                // Main path: horizontal left → curve down → vertical
                ctx.beginPath();
                ctx.moveTo(leftX, topY);
                ctx.lineTo(rightX - cr, topY);
                ctx.arcTo(rightX, topY, rightX, topY + cr, cr);
                ctx.lineTo(rightX, bottomY);
                ctx.stroke();
                // Chevron arrowhead at left end, pointing left
                const aLen = iconSize * 0.22;
                ctx.beginPath();
                ctx.moveTo(leftX + aLen, topY - aLen);
                ctx.lineTo(leftX, topY);
                ctx.lineTo(leftX + aLen, topY + aLen);
                ctx.stroke();
                break;
            }
        case 'newGame':
            {
                // "+" sign — universally understood, reads perfectly at any size
                const armLen = iconSize * 0.28;
                // Horizontal bar
                ctx.beginPath();
                ctx.moveTo(centerX - armLen, centerY);
                ctx.lineTo(centerX + armLen, centerY);
                ctx.stroke();
                // Vertical bar
                ctx.beginPath();
                ctx.moveTo(centerX, centerY - armLen);
                ctx.lineTo(centerX, centerY + armLen);
                ctx.stroke();
                break;
            }
        case 'check':
            {
                // Checkmark icon for auto-complete
                const checkSize = iconSize * 0.6;
                const startCheckX = centerX - checkSize * 0.4;
                const startCheckY = centerY + checkSize * 0.05;
                const midCheckX = centerX - checkSize * 0.1;
                const midCheckY = centerY + checkSize * 0.3;
                const endCheckX = centerX + checkSize * 0.4;
                const endCheckY = centerY - checkSize * 0.25;
                ctx.beginPath();
                ctx.moveTo(startCheckX, startCheckY);
                ctx.lineTo(midCheckX, midCheckY);
                ctx.lineTo(endCheckX, endCheckY);
                ctx.stroke();
                break;
            }
        case 'list':
            {
                // Simplified podium icon for leaderboard (no tiny numbers, monoline style).
                const baseY = centerY + iconSize * 0.3;
                const sideTopY = centerY - iconSize * 0.02;
                const centerTopY = centerY - iconSize * 0.28;
                const sideW = iconSize * 0.24;
                const centerW = iconSize * 0.26;
                const centerBlockX = centerX - centerW / 2;
                const leftBlockX = centerBlockX - sideW * 0.96;
                const rightBlockX = centerBlockX + centerW - sideW * 0.04;
                const cornerR = iconSize * 0.07;
                // Slightly thinner than other icons to keep the small shape airy.
                ctx.lineWidth = Math.max(1.6, size * 0.04);
                ctx.beginPath();
                ctx.roundRect(leftBlockX, sideTopY, sideW, baseY - sideTopY, cornerR);
                ctx.stroke();
                ctx.beginPath();
                ctx.roundRect(centerBlockX, centerTopY, centerW, baseY - centerTopY, cornerR);
                ctx.stroke();
                ctx.beginPath();
                ctx.roundRect(rightBlockX, sideTopY, sideW, baseY - sideTopY, cornerR);
                ctx.stroke();
                break;
            }
    }
    ctx.restore();
}
function $80cf1bdd1366d994$export$1be5916984d004f(ctx) {
    const x = (0, $0f5b71ede2b50336$export$26fe8f1cbe2626e5)();
    const y = (0, $0f5b71ede2b50336$export$70c3227cdbebd1b8)();
    $80cf1bdd1366d994$var$drawIconButton(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, 'undo', ctx, 'btn-undo');
}
function $80cf1bdd1366d994$export$726d21e4aad94310(ctx) {
    const x = (0, $0f5b71ede2b50336$export$1f614aa49af839fa)();
    const y = (0, $0f5b71ede2b50336$export$6d939bb5033370b0)();
    $80cf1bdd1366d994$var$drawIconButton(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, 'menu', ctx, 'btn-menu');
}
function $80cf1bdd1366d994$export$7ee7977b8f9a883(ctx) {
    const x = (0, $0f5b71ede2b50336$export$833ed0fe1212d409)();
    const y = (0, $0f5b71ede2b50336$export$f81ed66b3906beb3)();
    $80cf1bdd1366d994$var$drawIconButton(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, 'newGame', ctx, 'btn-newgame');
}
function $80cf1bdd1366d994$export$6e9db4b4118e074a(ctx) {
    const x = (0, $0f5b71ede2b50336$export$51646a58b06b5693)();
    const y = (0, $0f5b71ede2b50336$export$ff0ff2fb1fcb2a95)();
    $80cf1bdd1366d994$var$drawIconButton(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, 'check', ctx, 'btn-autocomplete');
}
function $80cf1bdd1366d994$export$9130244d9f40b66d(ctx) {
    const x = (0, $0f5b71ede2b50336$export$1ecdc1be7bd1a749)();
    const y = (0, $0f5b71ede2b50336$export$d50ecd2fde22a762)();
    $80cf1bdd1366d994$var$drawIconButton(x, y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, 'list', ctx, 'btn-records');
}


/**
 * Canvas-rendered menu with sub-screen navigation (Goal 27).
 *
 * Main Menu Layout:
 * ─────────────────────────────────────────────────────────────
 *   Row 0:  [X close] + "New Game" (center) + [Records/Fullscreen]
 *   Row 1:  [Themes] [Stats] [Game Center] — action button row
 *   Stats summary (compact)
 *   ── GAMEPLAY ──────────────────
 *        Draw   [1] [3]
 *        Flip   [Auto] [Tap]
 *   ── APPEARANCE ────────────────
 *        Hand   [Left] [Right]
 *        Back   [Pattern] [Solid]
 *   ── SOUND & FEEDBACK ──────────
 *        Sound   [On] [Off]
 *        Haptics [On] [Off]
 *
 * Themes Sub-Screen:
 * ─────────────────────────────────────────────────────────────
 *   Row 0:  [← Back] + "Themes" (center)
 *   Row 1:  Theme swatch row 1 (Midnight, Classic, Coral)
 *   Row 2:  Theme swatch row 2 (Ocean, Twilight, Forest)
 *
 * Hit areas are tracked in a module-level array, recomputed each paint.
 * Touch handling in canvasMouseMethods.ts reads these via getMenuHitAreas().
 */ 

/**
 * Statistics manager — persists game statistics to localStorage.
 *
 * Follows the same pattern as settingsManager.ts and stateManager.ts.
 */ // Statistics data structure for tracking game performance
const $8f8fdd62c4a2ad33$export$33b6538a13edafc5 = {
    gamesPlayed: 0,
    gamesWon: 0,
    bestTime: null,
    totalPlayTime: 0,
    currentStreak: 0,
    longestStreak: 0,
    averageWinTime: null
};


const $4115af5d1675323d$var$STATS_KEY = 'solitaire-statistics';
// ─── In-memory cache ────────────────────────────────────────────────────────
// Avoids re-parsing localStorage on every menu paint frame (~60fps).
// Invalidated whenever saveStatistics writes new data.
let $4115af5d1675323d$var$_cachedStats = null;
function $4115af5d1675323d$export$d5aab87327c50f1c(stats) {
    try {
        localStorage.setItem($4115af5d1675323d$var$STATS_KEY, JSON.stringify(stats));
        // Update in-memory cache so subsequent reads are instant
        $4115af5d1675323d$var$_cachedStats = {
            ...stats
        };
    } catch (error) {
        console.error('Failed to save statistics:', error);
    }
}
function $4115af5d1675323d$export$ef8a82cde02bb06f() {
    if ($4115af5d1675323d$var$_cachedStats) return $4115af5d1675323d$var$_cachedStats;
    try {
        const stored = localStorage.getItem($4115af5d1675323d$var$STATS_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                console.warn('Stored statistics failed validation, using defaults');
                $4115af5d1675323d$var$_cachedStats = {
                    ...(0, $8f8fdd62c4a2ad33$export$33b6538a13edafc5)
                };
                return $4115af5d1675323d$var$_cachedStats;
            }
            // Merge with defaults to handle new fields added in updates
            $4115af5d1675323d$var$_cachedStats = {
                ...(0, $8f8fdd62c4a2ad33$export$33b6538a13edafc5),
                ...parsed
            };
            return $4115af5d1675323d$var$_cachedStats;
        }
    } catch (error) {
        console.warn('Failed to load statistics, using defaults:', error);
    }
    $4115af5d1675323d$var$_cachedStats = {
        ...(0, $8f8fdd62c4a2ad33$export$33b6538a13edafc5)
    };
    return $4115af5d1675323d$var$_cachedStats;
}
function $4115af5d1675323d$export$4ad85e644dc7b0a2(timeMs) {
    const stats = $4115af5d1675323d$export$ef8a82cde02bb06f();
    stats.gamesPlayed++;
    stats.gamesWon++;
    stats.totalPlayTime += timeMs;
    // Best time
    if (stats.bestTime === null || timeMs < stats.bestTime) stats.bestTime = timeMs;
    // Average win time
    if (stats.averageWinTime === null) stats.averageWinTime = timeMs;
    else // Running average: ((old_avg * (wins-1)) + new_time) / wins
    stats.averageWinTime = Math.round((stats.averageWinTime * (stats.gamesWon - 1) + timeMs) / stats.gamesWon);
    // Streaks
    stats.currentStreak++;
    if (stats.currentStreak > stats.longestStreak) stats.longestStreak = stats.currentStreak;
    $4115af5d1675323d$export$d5aab87327c50f1c(stats);
}
function $4115af5d1675323d$export$da739c209ac971a2() {
    const stats = $4115af5d1675323d$export$ef8a82cde02bb06f();
    stats.gamesPlayed++;
    stats.currentStreak = 0;
    $4115af5d1675323d$export$d5aab87327c50f1c(stats);
}


/**
 * Records manager — persists game leaderboard to localStorage.
 *
 * Stores the top 10 game records sorted by penalized time (ascending).
 * Follows the same pattern as statisticsManager.ts and settingsManager.ts.
 */ /**
 * Data model for a single game record entry in the leaderboard.
 *
 * Each record captures the raw time, undo count, penalized time, and date
 * for a completed (won) game.
 */ const $b1c4622a6095546e$export$32529dd2809e488c = 15000;
const $b1c4622a6095546e$export$577bcc10b71ea5e0 = 10;


const $15b077d75e975a2b$var$RECORDS_KEY = 'solitaire-records';
function $15b077d75e975a2b$export$8008240685e80d26() {
    try {
        const stored = localStorage.getItem($15b077d75e975a2b$var$RECORDS_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (!Array.isArray(parsed)) return [];
            // Filter out any entries that don't have the required numeric fields
            const valid = parsed.filter((r)=>typeof r === 'object' && r !== null && typeof r.rawTime === 'number' && typeof r.penalizedTime === 'number' && typeof r.undoCount === 'number');
            // Ensure sorted by penalized time (ascending) — defensive
            return valid.sort((a, b)=>a.penalizedTime - b.penalizedTime).slice(0, (0, $b1c4622a6095546e$export$577bcc10b71ea5e0));
        }
    } catch (error) {
        console.warn('Failed to load records, using empty list:', error);
    }
    return [];
}
/** Save records to localStorage. */ function $15b077d75e975a2b$var$saveRecords(records) {
    try {
        localStorage.setItem($15b077d75e975a2b$var$RECORDS_KEY, JSON.stringify(records));
    } catch (error) {
        console.error('Failed to save records:', error);
    }
}
function $15b077d75e975a2b$export$60e52208036581dd(rawTime, undoCount) {
    const penalizedTime = rawTime + undoCount * (0, $b1c4622a6095546e$export$32529dd2809e488c);
    const record = {
        rawTime: rawTime,
        undoCount: undoCount,
        penalizedTime: penalizedTime,
        date: new Date().toISOString()
    };
    const records = $15b077d75e975a2b$export$8008240685e80d26();
    // Find insertion index (sorted ascending by penalized time)
    let insertIndex = records.length;
    for(let i = 0; i < records.length; i++)if (penalizedTime < records[i].penalizedTime) {
        insertIndex = i;
        break;
    }
    // If we're full and this record doesn't beat any existing one, skip it
    if (insertIndex >= (0, $b1c4622a6095546e$export$577bcc10b71ea5e0)) return null;
    // Insert and trim to max size
    records.splice(insertIndex, 0, record);
    const trimmed = records.slice(0, (0, $b1c4622a6095546e$export$577bcc10b71ea5e0));
    $15b077d75e975a2b$var$saveRecords(trimmed);
    // Return 1-based rank
    return insertIndex + 1;
}


/**
 * Game timer — tracks elapsed time during gameplay.
 *
 * Design:
 * - Starts on the player's first move (not on deal)
 * - Pauses when the menu opens
 * - Resumes when the menu closes
 * - Stops when the game is won
 * - Resets on new game
 * - Periodically triggers a repaint so the display updates every second
 * - Persists elapsed time to localStorage so it survives page refreshes
 *
 * Uses performance.now() for sub-millisecond accuracy within a session.
 */ 
const $d639f68dfbcef401$var$TIMER_STORAGE_KEY = 'solitaire-timer';
// ─── State ──────────────────────────────────────────────────────────────────
/** Accumulated elapsed time from previous sessions (ms). */ let $d639f68dfbcef401$var$_accumulated = 0;
/** Timestamp (performance.now()) when the current running session started, or null if paused/stopped. */ let $d639f68dfbcef401$var$_sessionStart = null;
/** Whether the timer has been started at all (first move made). */ let $d639f68dfbcef401$var$_started = false;
/** setInterval handle for periodic repaint while running. */ let $d639f68dfbcef401$var$_tickInterval = null;
function $d639f68dfbcef401$export$e63e1201ca009ae6() {
    if ($d639f68dfbcef401$var$_started) return;
    $d639f68dfbcef401$var$_started = true;
    $d639f68dfbcef401$var$_sessionStart = performance.now();
    $d639f68dfbcef401$var$_startTick();
    $d639f68dfbcef401$var$_persist();
}
function $d639f68dfbcef401$export$8b6a3625bd310ba() {
    if (!$d639f68dfbcef401$var$_started || $d639f68dfbcef401$var$_sessionStart === null) return;
    $d639f68dfbcef401$var$_accumulated += performance.now() - $d639f68dfbcef401$var$_sessionStart;
    $d639f68dfbcef401$var$_sessionStart = null;
    $d639f68dfbcef401$var$_stopTick();
    $d639f68dfbcef401$var$_persist();
}
function $d639f68dfbcef401$export$1cd74d15b99f2af2() {
    if (!$d639f68dfbcef401$var$_started || $d639f68dfbcef401$var$_sessionStart !== null) return; // not started, or already running
    $d639f68dfbcef401$var$_sessionStart = performance.now();
    $d639f68dfbcef401$var$_startTick();
}
function $d639f68dfbcef401$export$b4fb6bfe0bb0bbf1() {
    if ($d639f68dfbcef401$var$_sessionStart !== null) {
        $d639f68dfbcef401$var$_accumulated += performance.now() - $d639f68dfbcef401$var$_sessionStart;
        $d639f68dfbcef401$var$_sessionStart = null;
    }
    $d639f68dfbcef401$var$_started = false;
    $d639f68dfbcef401$var$_stopTick();
    $d639f68dfbcef401$var$_persist();
    return $d639f68dfbcef401$var$_accumulated;
}
function $d639f68dfbcef401$export$81b8579df16e1cc0() {
    $d639f68dfbcef401$var$_accumulated = 0;
    $d639f68dfbcef401$var$_sessionStart = null;
    $d639f68dfbcef401$var$_started = false;
    $d639f68dfbcef401$var$_stopTick();
    $d639f68dfbcef401$var$_clearPersisted();
}
function $d639f68dfbcef401$export$611ce66014572f0c() {
    if ($d639f68dfbcef401$var$_sessionStart !== null) return $d639f68dfbcef401$var$_accumulated + (performance.now() - $d639f68dfbcef401$var$_sessionStart);
    return $d639f68dfbcef401$var$_accumulated;
}
function $d639f68dfbcef401$export$58037dfed0af18fd() {
    return $d639f68dfbcef401$var$_started;
}
function $d639f68dfbcef401$export$3203edd9e5edd663(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor(totalSeconds % 3600 / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
// ─── Periodic repaint tick ──────────────────────────────────────────────────
function $d639f68dfbcef401$var$_startTick() {
    if ($d639f68dfbcef401$var$_tickInterval !== null) return;
    $d639f68dfbcef401$var$_tickInterval = setInterval(()=>{
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    }, 1000);
}
function $d639f68dfbcef401$var$_stopTick() {
    if ($d639f68dfbcef401$var$_tickInterval !== null) {
        clearInterval($d639f68dfbcef401$var$_tickInterval);
        $d639f68dfbcef401$var$_tickInterval = null;
    }
}
function $d639f68dfbcef401$var$_persist() {
    try {
        const data = {
            accumulated: $d639f68dfbcef401$var$_accumulated,
            started: $d639f68dfbcef401$var$_started
        };
        localStorage.setItem($d639f68dfbcef401$var$TIMER_STORAGE_KEY, JSON.stringify(data));
    } catch  {
    // localStorage unavailable — ignore
    }
}
function $d639f68dfbcef401$var$_clearPersisted() {
    try {
        localStorage.removeItem($d639f68dfbcef401$var$TIMER_STORAGE_KEY);
    } catch  {
    // ignore
    }
}
function $d639f68dfbcef401$export$7cdbdaf1080d01f1() {
    try {
        const stored = localStorage.getItem($d639f68dfbcef401$var$TIMER_STORAGE_KEY);
        if (stored) {
            const data = JSON.parse(stored);
            $d639f68dfbcef401$var$_accumulated = data.accumulated ?? 0;
            $d639f68dfbcef401$var$_started = data.started ?? false;
            // Don't auto-resume — the timer stays paused until the player makes a move
            // or the game detects it should resume
            $d639f68dfbcef401$var$_sessionStart = null;
        }
    } catch  {
    // ignore — start fresh
    }
}
function $d639f68dfbcef401$export$4f359d78fc926e81() {
    if ($d639f68dfbcef401$var$_started && $d639f68dfbcef401$var$_sessionStart === null) {
        $d639f68dfbcef401$var$_sessionStart = performance.now();
        $d639f68dfbcef401$var$_startTick();
    }
}





/**
 * Tip Jar manager — bridges JavaScript to native StoreKit 2 via WKWebView.
 *
 * Handles fetching product info (localized prices) and processing purchases
 * for the 3 consumable tip tiers. All functions gracefully no-op when the
 * native bridge is unavailable (browser, Android, iOS < 15).
 */ 
// ─── Constants ──────────────────────────────────────────────────────────────
const $baf55d86e4270add$var$TIP_PRODUCT_IDS = [
    'tip_small',
    'tip_medium',
    'tip_large'
];
/** Fallback display info used when native product fetch hasn't completed. */ const $baf55d86e4270add$var$FALLBACK_PRODUCTS = [
    {
        id: 'tip_small',
        displayName: 'Small Tip',
        displayPrice: '$0.99',
        price: 0.99
    },
    {
        id: 'tip_medium',
        displayName: 'Nice Tip',
        displayPrice: '$2.99',
        price: 2.99
    },
    {
        id: 'tip_large',
        displayName: 'Generous Tip',
        displayPrice: '$4.99',
        price: 4.99
    }
];
// ─── Module state ───────────────────────────────────────────────────────────
let $baf55d86e4270add$var$_products = [];
let $baf55d86e4270add$var$_productsLoaded = false;
let $baf55d86e4270add$var$_purchaseStatus = 'idle';
let $baf55d86e4270add$var$_purchaseMessage = null;
function $baf55d86e4270add$export$3fe0e1811c20398b() {
    if (!$baf55d86e4270add$var$_productsLoaded) return $baf55d86e4270add$var$FALLBACK_PRODUCTS;
    return $baf55d86e4270add$var$FALLBACK_PRODUCTS.map((fallback)=>{
        const fetched = $baf55d86e4270add$var$_products.find((p)=>p.id === fallback.id);
        return fetched ?? fallback;
    });
}
function $baf55d86e4270add$export$445c0453fe5e89e9() {
    return !!$baf55d86e4270add$var$getNativeBridge();
}
function $baf55d86e4270add$export$d23e1eb32c42ea03() {
    return $baf55d86e4270add$var$_purchaseStatus;
}
/** Optional message from last purchase attempt (e.g. error description). */ function $baf55d86e4270add$var$getPurchaseMessage() {
    return $baf55d86e4270add$var$_purchaseMessage;
}
function $baf55d86e4270add$export$d8d61ef40d973c27() {
    $baf55d86e4270add$var$_purchaseStatus = 'idle';
    $baf55d86e4270add$var$_purchaseMessage = null;
}
// ─── Native bridge helpers ──────────────────────────────────────────────────
function $baf55d86e4270add$var$getNativeBridge() {
    try {
        var _wk$messageHandlers;
        const wk = window.webkit;
        return wk === null || wk === void 0 ? void 0 : (_wk$messageHandlers = wk.messageHandlers) === null || _wk$messageHandlers === void 0 ? void 0 : _wk$messageHandlers.storeKit;
    } catch  {
        return undefined;
    }
}
function $baf55d86e4270add$var$postToNative(message) {
    const bridge = $baf55d86e4270add$var$getNativeBridge();
    if (bridge) bridge.postMessage(message);
}
function $baf55d86e4270add$export$cdd97182bd92cfbe() {
    if (!$baf55d86e4270add$var$getNativeBridge()) return; // Set up callback for native → JS product info
    window.__onStoreKitProducts = (products)=>{
        $baf55d86e4270add$var$_products = products.map((p)=>({
                id: p.id,
                displayName: p.displayName,
                displayPrice: p.displayPrice,
                price: parseFloat(p.price) || 0
            }));
        // Sort by price ascending to match our expected order
        $baf55d86e4270add$var$_products.sort((a, b)=>a.price - b.price);
        $baf55d86e4270add$var$_productsLoaded = $baf55d86e4270add$var$_products.length > 0;
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    };
    $baf55d86e4270add$var$postToNative({
        action: 'fetchProducts',
        productIds: [
            ...$baf55d86e4270add$var$TIP_PRODUCT_IDS
        ]
    });
}
function $baf55d86e4270add$export$4a246440fe9ec9da(productId) {
    if (!$baf55d86e4270add$var$getNativeBridge()) return;
    $baf55d86e4270add$var$_purchaseStatus = 'purchasing';
    $baf55d86e4270add$var$_purchaseMessage = null;
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    window.__onStoreKitPurchase = (result)=>{
        $baf55d86e4270add$var$_purchaseStatus = result.status;
        $baf55d86e4270add$var$_purchaseMessage = result.message;
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    };
    $baf55d86e4270add$var$postToNative({
        action: 'purchase',
        productId: productId
    });
}


/**
 * Game Center integration manager.
 *
 * Bridges JavaScript to the native GameCenterHandler in ViewController.swift
 * via WKWebView message handlers. All functions gracefully no-op when the
 * native bridge is unavailable (browser, Android, unauthenticated).
 */ // ─── Constants ──────────────────────────────────────────────────────────────
const $7b135972b663a872$var$LEADERBOARD_ID = 'best_computed_time';
/** Achievement definitions: id → evaluation function returning percentComplete (0-100), or 0 to skip. */ const $7b135972b663a872$var$ACHIEVEMENTS = [
    // Win milestones (incremental — report progress %)
    {
        id: 'first_win',
        evaluate: (s)=>s.gamesWon >= 1 ? 100 : 0
    },
    {
        id: 'wins_10',
        evaluate: (s)=>Math.min(100, s.gamesWon / 10 * 100)
    },
    {
        id: 'wins_50',
        evaluate: (s)=>Math.min(100, s.gamesWon / 50 * 100)
    },
    {
        id: 'wins_100',
        evaluate: (s)=>Math.min(100, s.gamesWon / 100 * 100)
    },
    // Streak milestones
    {
        id: 'streak_3',
        evaluate: (s)=>s.currentStreak >= 3 ? 100 : 0
    },
    {
        id: 'streak_5',
        evaluate: (s)=>s.currentStreak >= 5 ? 100 : 0
    },
    {
        id: 'streak_10',
        evaluate: (s)=>s.currentStreak >= 10 ? 100 : 0
    },
    // Special conditions (this game only)
    {
        id: 'speed_demon',
        evaluate: (_s, _d, _u, rawTime)=>rawTime < 180000 ? 100 : 0
    },
    {
        id: 'draw3_victor',
        evaluate: (_s, drawCount)=>drawCount === 3 ? 100 : 0
    },
    {
        id: 'perfect_game',
        evaluate: (_s, _d, undoCount)=>undoCount === 0 ? 100 : 0
    }
];
// ─── Module state ───────────────────────────────────────────────────────────
let $7b135972b663a872$var$_isAuthenticated = false;
// ─── Native bridge helpers ──────────────────────────────────────────────────
/** Returns the native Game Center message handler, or undefined if unavailable. */ function $7b135972b663a872$var$getNativeBridge() {
    try {
        var _wk$messageHandlers;
        const wk = window.webkit;
        return wk === null || wk === void 0 ? void 0 : (_wk$messageHandlers = wk.messageHandlers) === null || _wk$messageHandlers === void 0 ? void 0 : _wk$messageHandlers.gameCenter;
    } catch  {
        return undefined;
    }
}
function $7b135972b663a872$export$8eac741249a73d46() {
    return !!$7b135972b663a872$var$getNativeBridge();
}
/** Post a message to the native Game Center handler. */ function $7b135972b663a872$var$postToNative(message) {
    const bridge = $7b135972b663a872$var$getNativeBridge();
    if (bridge) bridge.postMessage(message);
}
function $7b135972b663a872$export$1cb815828ffae716() {
    if (!$7b135972b663a872$var$getNativeBridge()) return;
    window.__onGameCenterAuth = (result)=>{
        $7b135972b663a872$var$_isAuthenticated = result.authenticated;
        if ($7b135972b663a872$var$_isAuthenticated) console.log('[GameCenter] Authenticated');
        else console.log('[GameCenter] Not authenticated');
    };
    $7b135972b663a872$var$postToNative({
        action: 'authenticate'
    });
}
function $7b135972b663a872$export$71e03d193ca0545c(computedTimeMs) {
    if (!$7b135972b663a872$var$_isAuthenticated) return;
    $7b135972b663a872$var$postToNative({
        action: 'submitScore',
        leaderboardId: $7b135972b663a872$var$LEADERBOARD_ID,
        score: Math.round(computedTimeMs)
    });
}
function $7b135972b663a872$export$7adf517cbe323318(stats, drawCount, undoCount, rawTimeMs) {
    if (!$7b135972b663a872$var$_isAuthenticated) return;
    for (const achievement of $7b135972b663a872$var$ACHIEVEMENTS){
        const percent = achievement.evaluate(stats, drawCount, undoCount, rawTimeMs);
        if (percent > 0) $7b135972b663a872$var$postToNative({
            action: 'reportAchievement',
            achievementId: achievement.id,
            percentComplete: percent
        });
    }
}
function $7b135972b663a872$export$62e0ffbff3af272d() {
    if (!$7b135972b663a872$var$_isAuthenticated) return;
    $7b135972b663a872$var$postToNative({
        action: 'showDashboard'
    });
}


var $41c329ef74cc3b7d$export$14dc87dc5a0cc6ba = /*#__PURE__*/ function(MenuScreen) {
    MenuScreen["Main"] = "Main";
    MenuScreen["Themes"] = "Themes";
    MenuScreen["TipJar"] = "TipJar";
    return MenuScreen;
}({});
// ─── Module state ───────────────────────────────────────────────────────────
let $41c329ef74cc3b7d$var$_menuHitAreas = [];
let $41c329ef74cc3b7d$var$_currentMenuScreen = "Main";
function $41c329ef74cc3b7d$export$2b9dad546685a10e() {
    return $41c329ef74cc3b7d$var$_menuHitAreas;
}
/** Get the current menu sub-screen. */ function $41c329ef74cc3b7d$var$getMenuScreen() {
    return $41c329ef74cc3b7d$var$_currentMenuScreen;
}
function $41c329ef74cc3b7d$export$fb5e9769ad3cff77(screen) {
    $41c329ef74cc3b7d$var$_currentMenuScreen = screen;
}
function $41c329ef74cc3b7d$export$e8dbc87925066f13() {
    $41c329ef74cc3b7d$var$_currentMenuScreen = "Main";
}
/** Currently "pressed" hit area ID — triggers a highlight overlay on next paint. */ let $41c329ef74cc3b7d$var$_pressedHitAreaId = null;
function $41c329ef74cc3b7d$export$ea7c355b59511c15(id) {
    $41c329ef74cc3b7d$var$_pressedHitAreaId = id;
}
function $41c329ef74cc3b7d$export$6733d6bdb345d597() {
    $41c329ef74cc3b7d$var$_pressedHitAreaId = null;
}
// ─── Layout helpers ─────────────────────────────────────────────────────────
/**
 * Y position for settings row n (0 = first control in first section).
 *
 * Layout after action buttons:
 *   sectionHeaderHeight  — "GAMEPLAY" header
 *   control 0: Draw
 *   control 1: Flip
 *   sectionHeaderHeight  — "APPEARANCE" header
 *   control 2: Hand
 *   control 3: Back
 *   sectionHeaderHeight  — "SOUND & FEEDBACK" header
 *   control 4: Sound
 *   control 5: Haptics
 */ function $41c329ef74cc3b7d$var$settingsRowY(n) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const spacing = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing;
    const segH = Math.floor(ch * 0.65);
    const sectionHeaderH = Math.floor(ch * 0.35);
    // First control starts below the action button row (Row 1)
    const actionRowBottom = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) + ch + spacing;
    const firstCenterY = actionRowBottom + sectionHeaderH + spacing;
    // Fixed step: segment height + spacing between each control
    const step = segH + spacing;
    // Add extra space for section headers before controls 2 and 4
    let sectionOffset = 0;
    if (n >= 2) sectionOffset += sectionHeaderH + Math.floor(spacing * 0.5);
    if (n >= 4) sectionOffset += sectionHeaderH + Math.floor(spacing * 0.5);
    // Return Y such that drawSegmentedControl's centering math (y + ch/2) hits the target center
    return Math.floor(firstCenterY + step * n + sectionOffset - ch / 2);
}
/** Y position for a section header above control row n. */ function $41c329ef74cc3b7d$var$sectionHeaderY(controlIndex) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const sectionHeaderH = Math.floor(ch * 0.35);
    // The header sits just above the control's top edge
    const controlY = $41c329ef74cc3b7d$var$settingsRowY(controlIndex);
    const segH = Math.floor(ch * 0.65);
    const controlTopY = controlY + Math.floor((ch - segH) / 2);
    return controlTopY - sectionHeaderH;
}
// ─── Drawing primitives ─────────────────────────────────────────────────────
/** Helper: draw a rounded rectangle (fill + optional stroke). */ function $41c329ef74cc3b7d$var$drawRoundedRect(ctx, x, y, w, h, r, fillStyle, strokeStyle, lineWidth) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    ctx.fillStyle = fillStyle;
    ctx.fill();
    if (strokeStyle) {
        ctx.lineWidth = lineWidth ?? 2;
        ctx.strokeStyle = strokeStyle;
        ctx.stroke();
    }
}
// ─── Row 0 components ───────────────────────────────────────────────────────
/** Draw the fullscreen toggle icon at Row 0, opposite side from close button. */ function $41c329ef74cc3b7d$var$drawFullscreenIcon(ctx, borderRadius) {
    const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const x = (0, $0f5b71ede2b50336$export$7a8b0cb957188ec0)();
    const y = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0);
    const isFullscreen = !!document.fullscreenElement;
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)';
    ctx.save();
    ctx.strokeStyle = iconColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const padding = size * 0.2;
    const iconSize = size - padding * 2;
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    const extent = iconSize * 0.32;
    const arm = iconSize * 0.18;
    ctx.lineWidth = Math.max(2, size * 0.055);
    // Four corners: top-left, top-right, bottom-right, bottom-left
    const corners = [
        {
            cx: centerX - extent,
            cy: centerY - extent,
            dx: 1,
            dy: 1
        },
        {
            cx: centerX + extent,
            cy: centerY - extent,
            dx: -1,
            dy: 1
        },
        {
            cx: centerX + extent,
            cy: centerY + extent,
            dx: -1,
            dy: -1
        },
        {
            cx: centerX - extent,
            cy: centerY + extent,
            dx: 1,
            dy: -1
        }
    ];
    for (const c of corners){
        ctx.beginPath();
        if (isFullscreen) {
            ctx.moveTo(c.cx + c.dx * arm, c.cy);
            ctx.lineTo(c.cx, c.cy);
            ctx.lineTo(c.cx, c.cy + c.dy * arm);
        } else {
            ctx.moveTo(c.cx - c.dx * arm, c.cy);
            ctx.lineTo(c.cx, c.cy);
            ctx.lineTo(c.cx, c.cy - c.dy * arm);
        }
        ctx.stroke();
    }
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: x,
        y: y,
        width: size,
        height: size,
        id: 'fullscreen'
    });
}
/** Draw the records/leaderboard toggle icon at Row 0, opposite side from close button. */ function $41c329ef74cc3b7d$var$drawMenuRecordsToggleIcon(ctx) {
    const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const x = (0, $0f5b71ede2b50336$export$7a8b0cb957188ec0)();
    const y = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0);
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)';
    ctx.save();
    ctx.strokeStyle = iconColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const padding = size * 0.2;
    const iconSize = size - padding * 2;
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    // Podium icon
    const baseY = centerY + iconSize * 0.3;
    const sideTopY = centerY - iconSize * 0.02;
    const centerTopY = centerY - iconSize * 0.28;
    const sideW = iconSize * 0.24;
    const centerW = iconSize * 0.26;
    const centerBlockX = centerX - centerW / 2;
    const leftBlockX = centerBlockX - sideW * 0.96;
    const rightBlockX = centerBlockX + centerW - sideW * 0.04;
    const cornerR = iconSize * 0.07;
    ctx.lineWidth = Math.max(1.6, size * 0.04);
    ctx.beginPath();
    ctx.roundRect(leftBlockX, sideTopY, sideW, baseY - sideTopY, cornerR);
    ctx.stroke();
    ctx.beginPath();
    ctx.roundRect(centerBlockX, centerTopY, centerW, baseY - centerTopY, cornerR);
    ctx.stroke();
    ctx.beginPath();
    ctx.roundRect(rightBlockX, sideTopY, sideW, baseY - sideTopY, cornerR);
    ctx.stroke();
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: x,
        y: y,
        width: size,
        height: size,
        id: 'menu-records-toggle'
    });
}
/**
 * Draw a "New Game" text button in the menu at Row 0, centered horizontally.
 */ function $41c329ef74cc3b7d$var$drawNewGameTextButton(ctx, borderRadius) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const y = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0);
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    const fontSize = Math.floor(ch * 0.22);
    const segH = Math.floor(ch * 0.65);
    const pillRadius = Math.floor(segH / 2);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    const textW = ctx.measureText('New Game').width;
    ctx.restore();
    const hPad = Math.floor(cw * 0.45);
    const pillW = Math.floor(textW + hPad * 2);
    const pillX = Math.floor((winW - pillW) / 2);
    const pillY = y + Math.floor((cw - segH) / 2);
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
    $41c329ef74cc3b7d$var$drawRoundedRect(ctx, pillX, pillY, pillW, segH, pillRadius, pillBg);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.7;
    }
    ctx.fillText('New Game', Math.floor(winW / 2), pillY + segH / 2);
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: pillX,
        y: pillY,
        width: pillW,
        height: segH,
        id: 'new-game',
        radius: pillRadius
    });
}
// ─── Action button row (Row 1) ──────────────────────────────────────────────
/**
 * Draw three action buttons in Row 1: Themes, Stats, Game Center.
 * Pill-shaped buttons evenly distributed across the row.
 */ function $41c329ef74cc3b7d$var$drawActionButtonRow(ctx, borderRadius) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const y = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1);
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    const fontSize = Math.floor(ch * 0.19);
    const segH = Math.floor(ch * 0.65);
    const pillRadius = Math.floor(segH / 2);
    const buttons = [
        {
            label: 'Themes',
            id: 'menu-themes'
        }
    ];
    if ((0, $7b135972b663a872$export$8eac741249a73d46)()) buttons.push({
        label: 'Game Center',
        id: 'menu-gamecenter'
    });
    // Calculate button widths based on text
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    const hPad = Math.floor(cw * 0.35);
    const buttonWidths = buttons.map((b)=>Math.floor(ctx.measureText(b.label).width + hPad * 2));
    ctx.restore();
    const gap = Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing * 1.5);
    const totalW = buttonWidths.reduce((sum, w)=>sum + w, 0) + gap * (buttons.length - 1);
    let startX = Math.floor((winW - totalW) / 2);
    const pillY = y + Math.floor((ch - segH) / 2);
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
    for(let i = 0; i < buttons.length; i++){
        const btn = buttons[i];
        const pillW = buttonWidths[i];
        $41c329ef74cc3b7d$var$drawRoundedRect(ctx, startX, pillY, pillW, segH, pillRadius, pillBg);
        ctx.save();
        ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
        else {
            ctx.fillStyle = highlightColor;
            ctx.globalAlpha = 0.6;
        }
        ctx.fillText(btn.label, startX + pillW / 2, pillY + segH / 2);
        ctx.restore();
        $41c329ef74cc3b7d$var$_menuHitAreas.push({
            x: startX,
            y: pillY,
            width: pillW,
            height: segH,
            id: btn.id,
            radius: pillRadius
        });
        startX += pillW + gap;
    }
}
// ─── Section headers ────────────────────────────────────────────────────────
/** Draw a subtle all-caps section header label. */ function $41c329ef74cc3b7d$var$drawSectionHeader(ctx, text, y) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    const fontSize = Math.floor(ch * 0.14);
    ctx.save();
    ctx.font = `700 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.35;
    }
    ctx.letterSpacing = '2px';
    ctx.fillText(text, Math.floor(winW / 2), y + Math.floor(ch * 0.35) / 2);
    ctx.restore();
}
// ─── Records overlay ────────────────────────────────────────────────────────
/**
 * Draw a records/leaderboard overlay on top of the menu.
 * Shows top 10 best times with rank, penalized time, and undo count.
 */ function $41c329ef74cc3b7d$var$drawMenuRecordsOverlay(ctx, borderRadius) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const winH = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    // Dark backdrop over menu content
    const panelPadding = Math.floor(ch * 0.3);
    const panelY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) - Math.floor(ch * 0.15);
    const panelX = panelPadding;
    const panelW = winW - panelPadding * 2;
    const panelH = winH - panelY - panelPadding;
    const panelR = Math.max(8, Math.floor(ch * 0.15));
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, panelR);
    ctx.fill();
    ctx.restore();
    const primaryColor = 'rgba(255, 255, 255, 0.9)';
    const secondaryColor = 'rgba(255, 255, 255, 0.6)';
    const mutedColor = 'rgba(255, 255, 255, 0.35)';
    const centerX = Math.floor(winW / 2);
    const records = (0, $15b077d75e975a2b$export$8008240685e80d26)();
    if (records.length === 0) {
        const msgFontSize = Math.floor(ch * 0.22);
        ctx.save();
        ctx.font = `500 ${msgFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = secondaryColor;
        ctx.fillText('No completed games yet', centerX, (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(4) + Math.floor(ch / 2));
        ctx.restore();
        // Still show stats even with no records
        $41c329ef74cc3b7d$var$drawOverlayStats(ctx, panelY, panelH, ch, centerX, secondaryColor, mutedColor);
        return;
    }
    // "BEST TIMES" header
    const headerY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) + Math.floor(ch * 0.5);
    const headerFontSize = Math.floor(ch * 0.18);
    ctx.save();
    ctx.font = `700 ${headerFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = mutedColor;
    ctx.letterSpacing = '2px';
    ctx.fillText('BEST TIMES', centerX, headerY);
    ctx.restore();
    // Records list
    const listStartY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) + Math.floor(ch * 0.85);
    const listEndY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(8) + Math.floor(ch * 0.5);
    const availableHeight = listEndY - listStartY;
    const recordFontSize = Math.floor(ch * 0.17);
    const lineHeight = Math.min(Math.floor(recordFontSize * 2.2), Math.floor(availableHeight / Math.min(records.length, 10)));
    const colWidth = Math.floor(winW * 0.8);
    const colStartX = Math.floor((winW - colWidth) / 2);
    const rankX = colStartX + Math.floor(colWidth * 0.08);
    const timeX = colStartX + Math.floor(colWidth * 0.35);
    const undoX = colStartX + Math.floor(colWidth * 0.72);
    for(let i = 0; i < records.length; i++){
        const record = records[i];
        const y = listStartY + i * lineHeight;
        if (y > listEndY) break;
        const isTopThree = i < 3;
        ctx.save();
        ctx.font = `${isTopThree ? '700' : '500'} ${recordFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isTopThree ? highlightColor : mutedColor;
        ctx.fillText(`${i + 1}.`, rankX, y);
        ctx.font = `${isTopThree ? '600' : '500'} ${recordFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isTopThree ? primaryColor : secondaryColor;
        ctx.fillText((0, $d639f68dfbcef401$export$3203edd9e5edd663)(record.penalizedTime), timeX, y);
        ctx.font = `400 ${Math.floor(recordFontSize * 0.85)}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (record.undoCount > 0) {
            ctx.fillStyle = mutedColor;
            ctx.fillText(`${record.undoCount} undo${record.undoCount !== 1 ? 's' : ''}`, undoX, y);
        } else {
            ctx.fillStyle = mutedColor;
            ctx.fillText('clean', undoX, y);
        }
        ctx.restore();
    }
    // Stats summary at bottom of overlay
    $41c329ef74cc3b7d$var$drawOverlayStats(ctx, panelY, panelH, ch, centerX, secondaryColor, mutedColor);
}
/** Draw win/loss stats at the bottom of the records overlay. */ function $41c329ef74cc3b7d$var$drawOverlayStats(ctx, panelY, panelH, ch, centerX, secondaryColor, mutedColor) {
    const stats = (0, $4115af5d1675323d$export$ef8a82cde02bb06f)();
    if (stats.gamesPlayed === 0) return;
    const statsFontSize = Math.floor(ch * 0.16);
    const statsLineHeight = Math.floor(statsFontSize * 1.6);
    const statsBottomY = panelY + panelH - Math.floor(ch * 0.3);
    const winRate = Math.round(stats.gamesWon / stats.gamesPlayed * 100);
    const line1 = `${stats.gamesWon} won / ${stats.gamesPlayed} played  (${winRate}%)`;
    let line2 = '';
    if (stats.bestTime !== null) {
        line2 = `Best: ${(0, $d639f68dfbcef401$export$3203edd9e5edd663)(stats.bestTime)}`;
        if (stats.averageWinTime !== null) line2 += `   Avg: ${(0, $d639f68dfbcef401$export$3203edd9e5edd663)(stats.averageWinTime)}`;
    }
    ctx.save();
    ctx.font = `500 ${statsFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = secondaryColor;
    if (line2) {
        ctx.fillText(line1, centerX, statsBottomY - statsLineHeight);
        ctx.fillStyle = mutedColor;
        ctx.fillText(line2, centerX, statsBottomY);
    } else ctx.fillText(line1, centerX, statsBottomY);
    ctx.restore();
}
// ─── Main menu drawing ──────────────────────────────────────────────────────
/**
 * Draw the main menu screen (action buttons, stats, grouped settings).
 */ function $41c329ef74cc3b7d$var$drawMainMenu(ctx, borderRadius) {
    // ── Row 0: "New Game" text button (center) ──
    $41c329ef74cc3b7d$var$drawNewGameTextButton(ctx, borderRadius);
    // ── Row 0: Fullscreen icon (browser-only) ──
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).isStandalone && document.fullscreenEnabled) $41c329ef74cc3b7d$var$drawFullscreenIcon(ctx, borderRadius);
    // ── Row 1: Action buttons ──
    $41c329ef74cc3b7d$var$drawActionButtonRow(ctx, borderRadius);
    // ── GAMEPLAY section ──
    $41c329ef74cc3b7d$var$drawSectionHeader(ctx, 'GAMEPLAY', $41c329ef74cc3b7d$var$sectionHeaderY(0));
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(0), 'Draw', [
        {
            label: '1',
            id: 'draw-1',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount === 1
        },
        {
            label: '3',
            id: 'draw-3',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount === 3
        }
    ], borderRadius);
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(1), 'Flip', [
        {
            label: 'Auto',
            id: 'flip-auto',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.autoFlipCards
        },
        {
            label: 'Tap',
            id: 'flip-tap',
            active: !(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.autoFlipCards
        }
    ], borderRadius);
    // ── APPEARANCE section ──
    $41c329ef74cc3b7d$var$drawSectionHeader(ctx, 'APPEARANCE', $41c329ef74cc3b7d$var$sectionHeaderY(2));
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(2), 'Hand', [
        {
            label: 'Left',
            id: 'hand-left',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness === 'left'
        },
        {
            label: 'Right',
            id: 'hand-right',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness === 'right'
        }
    ], borderRadius);
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(3), 'Back', [
        {
            label: 'Pattern',
            id: 'back-pattern',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.cardBackDesign === 'pattern'
        },
        {
            label: 'Solid',
            id: 'back-solid',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.cardBackDesign === 'solid'
        }
    ], borderRadius);
    // ── SOUND & FEEDBACK section ──
    $41c329ef74cc3b7d$var$drawSectionHeader(ctx, 'SOUND & FEEDBACK', $41c329ef74cc3b7d$var$sectionHeaderY(4));
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(4), 'Sound', [
        {
            label: 'On',
            id: 'sound-on',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.soundEnabled
        },
        {
            label: 'Off',
            id: 'sound-off',
            active: !(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.soundEnabled
        }
    ], borderRadius);
    $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, $41c329ef74cc3b7d$var$settingsRowY(5), 'Haptics', [
        {
            label: 'On',
            id: 'haptic-on',
            active: (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.hapticsEnabled
        },
        {
            label: 'Off',
            id: 'haptic-off',
            active: !(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.hapticsEnabled
        }
    ], borderRadius);
    // ── Records toggle icon (always visible) ──
    $41c329ef74cc3b7d$var$drawMenuRecordsToggleIcon(ctx);
    // ── Records overlay (when toggled on) ──
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords) $41c329ef74cc3b7d$var$drawMenuRecordsOverlay(ctx, borderRadius);
    // ── Tip jar icon (bottom corner, iOS only, drawn on top of overlay) ──
    if ((0, $baf55d86e4270add$export$445c0453fe5e89e9)()) $41c329ef74cc3b7d$var$drawTipJarIcon(ctx);
}
// ─── Themes sub-screen ──────────────────────────────────────────────────────
/**
 * Draw the Themes sub-screen: back button + title + theme swatch grid.
 */ function $41c329ef74cc3b7d$var$drawThemesSubScreen(ctx, borderRadius) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const y0 = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0);
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    // ── Row 0: Back button (left side) ──
    const fontSize = Math.floor(ch * 0.22);
    const segH = Math.floor(ch * 0.65);
    const pillRadius = Math.floor(segH / 2);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    const backTextW = ctx.measureText("\u2190 Back").width;
    ctx.restore();
    const hPad = Math.floor(cw * 0.35);
    const backPillW = Math.floor(backTextW + hPad * 2);
    const backPillX = Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing);
    const backPillY = y0 + Math.floor((cw - segH) / 2);
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
    $41c329ef74cc3b7d$var$drawRoundedRect(ctx, backPillX, backPillY, backPillW, segH, pillRadius, pillBg);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.7;
    }
    ctx.fillText("\u2190 Back", backPillX + backPillW / 2, backPillY + segH / 2);
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: backPillX,
        y: backPillY,
        width: backPillW,
        height: segH,
        id: 'menu-back',
        radius: pillRadius
    });
    // ── Row 0: "Themes" title (centered) ──
    const titleFontSize = Math.floor(ch * 0.26);
    ctx.save();
    ctx.font = `700 ${titleFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.8;
    }
    ctx.fillText('Themes', Math.floor(winW / 2), y0 + Math.floor(cw / 2));
    ctx.restore();
    // ── Row 1-2: Theme swatches (2×3 grid) ──
    const themeEntries = Object.entries((0, $0e92ad87971fbdf7$export$d9a33280f07116d9));
    const swatchesRow1 = themeEntries.slice(0, 3);
    const swatchesRow2 = themeEntries.slice(3, 6);
    $41c329ef74cc3b7d$var$drawThemeSwatchRow(ctx, swatchesRow1, (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1), borderRadius);
    $41c329ef74cc3b7d$var$drawThemeSwatchRow(ctx, swatchesRow2, (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(2), borderRadius);
}
// ─── Tip jar icon (bottom-left of menu) ─────────────────────────────────────
/** Draw a small "$" icon in the bottom-left corner of the menu screen. */ function $41c329ef74cc3b7d$var$drawTipJarIcon(ctx) {
    const size = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const spacing = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing;
    // Align horizontally with the leaderboard podium icon
    const x = (0, $0f5b71ede2b50336$export$7a8b0cb957188ec0)();
    // Mirror the podium's top offset — same distance from the bottom edge
    const y = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight - (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0) - size;
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.55)';
    const lw = Math.max(1.8, size * 0.04);
    const centerX = x + size / 2;
    const centerY = y + size / 2;
    const padding = size * 0.18;
    const iconW = size - padding * 2;
    const iconH = iconW * 0.8;
    ctx.save();
    ctx.strokeStyle = iconColor;
    ctx.fillStyle = iconColor;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // ── Jar body (rounded rectangle) ──
    const jarW = iconW * 0.7;
    const jarH = iconH * 0.7;
    const jarX = centerX - jarW / 2;
    const jarY = centerY - jarH / 2 + iconH * 0.15;
    const jarR = jarW * 0.15;
    ctx.beginPath();
    ctx.roundRect(jarX, jarY, jarW, jarH, jarR);
    ctx.stroke();
    // ── Jar lid (wider rectangle on top) ──
    const lidW = jarW * 1.1;
    const lidH = jarH * 0.15;
    const lidX = centerX - lidW / 2;
    const lidY = jarY - lidH;
    const lidR = lidH * 0.35;
    ctx.beginPath();
    ctx.roundRect(lidX, lidY, lidW, lidH, lidR);
    ctx.stroke();
    // ── Coin slot (small rectangle in lid) ──
    const slotW = lidW * 0.3;
    const slotH = lw * 1.2;
    ctx.beginPath();
    ctx.roundRect(centerX - slotW / 2, lidY - slotH / 2, slotW, slotH, slotH / 2);
    ctx.fill();
    // ── Coin (circle above the slot, tilted) ──
    const coinR = iconW * 0.13;
    const coinCX = centerX + jarW * 0.22;
    const coinCY = lidY - coinR * 1.3;
    ctx.beginPath();
    ctx.arc(coinCX, coinCY, coinR, 0, Math.PI * 2);
    ctx.stroke();
    // "$" on the coin
    const coinFontSize = Math.floor(coinR * 1.3);
    ctx.font = `700 ${coinFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('$', coinCX, coinCY);
    // ── "TIP" text on the jar body ──
    const tipFontSize = Math.floor(jarH * 0.3);
    ctx.font = `700 ${tipFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('TIP', centerX, jarY + jarH * 0.52);
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: x,
        y: y,
        width: size,
        height: size,
        id: 'menu-tipjar'
    });
}
// ─── Tip Jar sub-screen ─────────────────────────────────────────────────────
/**
 * Draw the Tip Jar sub-screen: back button + title + 3 purchase buttons + status.
 */ function $41c329ef74cc3b7d$var$drawTipJarSubScreen(ctx, borderRadius) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const y0 = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0);
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    // ── Row 0: Back button (left side) ──
    const fontSize = Math.floor(ch * 0.22);
    const segH = Math.floor(ch * 0.65);
    const pillRadius = Math.floor(segH / 2);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    const backTextW = ctx.measureText("\u2190 Back").width;
    ctx.restore();
    const hPad = Math.floor(cw * 0.35);
    const backPillW = Math.floor(backTextW + hPad * 2);
    const backPillX = Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing);
    const backPillY = y0 + Math.floor((cw - segH) / 2);
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
    $41c329ef74cc3b7d$var$drawRoundedRect(ctx, backPillX, backPillY, backPillW, segH, pillRadius, pillBg);
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.7;
    }
    ctx.fillText("\u2190 Back", backPillX + backPillW / 2, backPillY + segH / 2);
    ctx.restore();
    $41c329ef74cc3b7d$var$_menuHitAreas.push({
        x: backPillX,
        y: backPillY,
        width: backPillW,
        height: segH,
        id: 'menu-back',
        radius: pillRadius
    });
    // ── Row 0: "Tip Jar" title (centered) ──
    const titleFontSize = Math.floor(ch * 0.26);
    ctx.save();
    ctx.font = `700 ${titleFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.8;
    }
    ctx.fillText('Tip Jar', Math.floor(winW / 2), y0 + Math.floor(cw / 2));
    ctx.restore();
    // ── Subtitle text ──
    const subtitleFontSize = Math.floor(ch * 0.17);
    const subtitleY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) + Math.floor(ch * 0.15);
    const lineH = Math.floor(subtitleFontSize * 1.5);
    ctx.save();
    ctx.font = `500 ${subtitleFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.55;
    }
    ctx.fillText('Thank you for playing!', Math.floor(winW / 2), subtitleY);
    ctx.fillText('This app will always be free.', Math.floor(winW / 2), subtitleY + lineH);
    ctx.fillText('All tips are appreciated and help fund development.', Math.floor(winW / 2), subtitleY + lineH * 2);
    ctx.restore();
    // ── Purchase buttons (3 tiers) ──
    const products = (0, $baf55d86e4270add$export$3fe0e1811c20398b)();
    const purchaseStatus = (0, $baf55d86e4270add$export$d23e1eb32c42ea03)();
    const buttonFontSize = Math.floor(ch * 0.2);
    const buttonH = Math.floor(ch * 0.75);
    const buttonW = Math.floor(winW * 0.65);
    const buttonX = Math.floor((winW - buttonW) / 2);
    const buttonRadius = Math.floor(buttonH / 2);
    const buttonGap = Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing * 1.5);
    const firstButtonY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(2) + Math.floor(ch * 0.4);
    const tierLabels = [
        'Small Tip',
        'Nice Tip',
        'Generous Tip'
    ];
    const tierEmojis = [
        "\u2615",
        "\uD83C\uDF89",
        "\uD83D\uDC8E"
    ];
    for(let i = 0; i < products.length && i < 3; i++){
        const product = products[i];
        const bY = firstButtonY + i * (buttonH + buttonGap);
        const isPurchasing = purchaseStatus === 'purchasing';
        // Button background
        const btnBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
        $41c329ef74cc3b7d$var$drawRoundedRect(ctx, buttonX, bY, buttonW, buttonH, buttonRadius, btnBg);
        // Button text: "emoji Label — $X.XX"
        ctx.save();
        ctx.font = `600 ${buttonFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (lightBg) ctx.fillStyle = isPurchasing ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)';
        else {
            ctx.fillStyle = highlightColor;
            ctx.globalAlpha = isPurchasing ? 0.35 : 0.7;
        }
        const label = `${tierEmojis[i]}  ${tierLabels[i]}  \u{2014}  ${product.displayPrice}`;
        ctx.fillText(label, buttonX + buttonW / 2, bY + buttonH / 2);
        ctx.restore();
        if (!isPurchasing) $41c329ef74cc3b7d$var$_menuHitAreas.push({
            x: buttonX,
            y: bY,
            width: buttonW,
            height: buttonH,
            id: `tip-${product.id}`,
            radius: buttonRadius
        });
    }
    // ── Status message (below buttons) ──
    if (purchaseStatus !== 'idle' && purchaseStatus !== 'purchasing') {
        const statusY = firstButtonY + 3 * (buttonH + buttonGap) + Math.floor(ch * 0.1);
        const statusFontSize = Math.floor(ch * 0.19);
        ctx.save();
        ctx.font = `600 ${statusFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (purchaseStatus === 'success') {
            ctx.fillStyle = highlightColor;
            ctx.fillText('Thank you for your support!', Math.floor(winW / 2), statusY);
        } else if (purchaseStatus === 'cancelled') {
            if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
            else ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.fillText('Purchase cancelled', Math.floor(winW / 2), statusY);
        } else if (purchaseStatus === 'error') {
            ctx.fillStyle = 'rgba(255, 100, 100, 0.8)';
            ctx.fillText('Something went wrong', Math.floor(winW / 2), statusY);
        } else if (purchaseStatus === 'pending') {
            if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            else ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.fillText('Purchase pending...', Math.floor(winW / 2), statusY);
        }
        ctx.restore();
    }
}
function $41c329ef74cc3b7d$export$aa84b2f9522188a7(ctx) {
    $41c329ef74cc3b7d$var$_menuHitAreas = [];
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const borderRadius = Math.max(4, Math.floor(ch * 0.1));
    switch($41c329ef74cc3b7d$var$_currentMenuScreen){
        case "Themes":
            $41c329ef74cc3b7d$var$drawThemesSubScreen(ctx, borderRadius);
            break;
        case "TipJar":
            $41c329ef74cc3b7d$var$drawTipJarSubScreen(ctx, borderRadius);
            break;
        case "Main":
        default:
            $41c329ef74cc3b7d$var$drawMainMenu(ctx, borderRadius);
            break;
    }
    // ── Press highlight overlay ──
    if ($41c329ef74cc3b7d$var$_pressedHitAreaId) {
        const hitArea = $41c329ef74cc3b7d$var$_menuHitAreas.find((a)=>a.id === $41c329ef74cc3b7d$var$_pressedHitAreaId);
        if (hitArea) {
            const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
            const overlayColor = lightBg ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)';
            const r = hitArea.radius ?? borderRadius;
            $41c329ef74cc3b7d$var$drawRoundedRect(ctx, hitArea.x, hitArea.y, hitArea.width, hitArea.height, r, overlayColor);
        }
    }
}
// ─── Theme swatch row ───────────────────────────────────────────────────────
function $41c329ef74cc3b7d$var$drawThemeSwatchRow(ctx, entries, y, borderRadius) {
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const gap = Math.floor((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing * 1.5);
    const swatchW = cw;
    const swatchH = ch;
    const totalW = entries.length * swatchW + (entries.length - 1) * gap;
    const startX = Math.floor((winW - totalW) / 2);
    const miniCardW = Math.floor(swatchW * 0.38);
    const miniCardH = Math.floor(miniCardW / 0.75);
    const miniRadius = Math.max(2, Math.floor(borderRadius * 0.5));
    const labelFontSize = Math.max(8, Math.floor(ch * 0.13));
    for(let i = 0; i < entries.length; i++){
        const [themeName, theme] = entries[i];
        const sx = startX + i * (swatchW + gap);
        const isActive = themeName === (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.themeName;
        // Active highlight ring
        if (isActive) {
            const pad = 3;
            $41c329ef74cc3b7d$var$drawRoundedRect(ctx, sx - pad, y - pad, swatchW + pad * 2, swatchH + pad * 2, borderRadius + pad, 'transparent', (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor, 3);
        }
        // Swatch background
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(sx, y, swatchW, swatchH, borderRadius);
        ctx.clip();
        ctx.fillStyle = theme.backgroundColor;
        ctx.fillRect(sx, y, swatchW, swatchH);
        // Mini card back (left)
        const cardsY = y + Math.floor((swatchH - miniCardH) * 0.42);
        const cardBackX = sx + Math.floor(swatchW * 0.12);
        const cardFaceX = sx + swatchW - miniCardW - Math.floor(swatchW * 0.12);
        ctx.beginPath();
        ctx.roundRect(cardBackX, cardsY, miniCardW, miniCardH, miniRadius);
        ctx.fillStyle = theme.cardBackColor;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.stroke();
        // Mini card face (right)
        ctx.beginPath();
        ctx.roundRect(cardFaceX, cardsY, miniCardW, miniCardH, miniRadius);
        ctx.fillStyle = theme.cardFaceColor;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.stroke();
        // Mini heart
        const heartSize = Math.floor(miniCardH * 0.45);
        ctx.fillStyle = theme.redCardColor;
        (0, $b3d87dc43fab0d75$export$1ed0c0bc398b6246)(ctx, cardFaceX + miniCardW * 0.5, cardsY + miniCardH * 0.48, heartSize);
        // Theme name label
        const swatchBgLight = (0, $f9eed79712445f27$export$6b84b5f288b224da)(theme.backgroundColor);
        const labelColor = swatchBgLight ? 'rgba(0, 0, 0, 0.7)' : theme.highlightColor;
        ctx.font = `600 ${labelFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.fillStyle = labelColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(theme.displayName, sx + swatchW / 2, y + swatchH - Math.floor(swatchH * 0.04));
        ctx.restore();
        // Border
        const menuBgLight = (0, $f9eed79712445f27$export$6b84b5f288b224da)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
        const defaultBorderAlpha = menuBgLight && swatchBgLight ? 0.25 : 0.3;
        ctx.beginPath();
        ctx.roundRect(sx, y, swatchW, swatchH, borderRadius);
        ctx.lineWidth = isActive ? 2.5 : 1;
        ctx.strokeStyle = isActive ? (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor : `rgba(0, 0, 0, ${defaultBorderAlpha})`;
        ctx.stroke();
        // Hit area
        $41c329ef74cc3b7d$var$_menuHitAreas.push({
            x: sx - 3,
            y: y - 3,
            width: swatchW + 6,
            height: swatchH + 6,
            id: `theme-${themeName}`
        });
    }
}
function $41c329ef74cc3b7d$var$drawSegmentedControl(ctx, y, labelText, segments, borderRadius) {
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    const bgColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor;
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)(bgColor);
    const fontSize = Math.floor(ch * 0.22);
    const segH = Math.floor(ch * 0.65);
    const segW = Math.floor(cw * 1.3);
    const totalSegW = segments.length * segW;
    const labelW = Math.floor(cw * 1.2);
    const controlGap = Math.floor(cw * 0.3);
    const totalControlW = labelW + controlGap + totalSegW;
    const controlStartX = Math.floor((winW - totalControlW) / 2);
    const segStartX = controlStartX + labelW + controlGap;
    const segY = y + Math.floor((ch - segH) / 2);
    const pillRadius = Math.floor(segH / 2);
    // Label
    ctx.save();
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    else {
        ctx.fillStyle = highlightColor;
        ctx.globalAlpha = 0.6;
    }
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(labelText, controlStartX + labelW, y + Math.floor(ch / 2));
    ctx.restore();
    // Pill background
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)';
    $41c329ef74cc3b7d$var$drawRoundedRect(ctx, segStartX, segY, totalSegW, segH, pillRadius, pillBg);
    // Segments
    for(let i = 0; i < segments.length; i++){
        const seg = segments[i];
        const sx = segStartX + i * segW;
        if (seg.active) {
            $41c329ef74cc3b7d$var$drawRoundedRect(ctx, sx + 2, segY + 2, segW - 4, segH - 4, pillRadius - 2, highlightColor);
            const hlLight = (0, $f9eed79712445f27$export$6b84b5f288b224da)(highlightColor);
            ctx.save();
            ctx.font = `700 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
            ctx.fillStyle = hlLight ? 'rgba(0, 0, 0, 0.85)' : '#FFFFFF';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(seg.label, sx + segW / 2, segY + segH / 2);
            ctx.restore();
        } else {
            ctx.save();
            ctx.font = `500 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
            if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
            else {
                ctx.fillStyle = highlightColor;
                ctx.globalAlpha = 0.4;
            }
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(seg.label, sx + segW / 2, segY + segH / 2);
            ctx.restore();
        }
        $41c329ef74cc3b7d$var$_menuHitAreas.push({
            x: sx,
            y: segY,
            width: segW,
            height: segH,
            id: seg.id,
            radius: pillRadius
        });
    }
}




let $de009be455ddb921$var$_frameCounter = 0;
let $de009be455ddb921$var$_lastSnapshot = null;
let $de009be455ddb921$var$_enabled = false;
function $de009be455ddb921$var$enableRenderDebug() {
    $de009be455ddb921$var$_enabled = true;
}
function $de009be455ddb921$var$disableRenderDebug() {
    $de009be455ddb921$var$_enabled = false;
}
function $de009be455ddb921$var$isRenderDebugEnabled() {
    return $de009be455ddb921$var$_enabled;
}
function $de009be455ddb921$export$ba8727a6700971b9() {
    $de009be455ddb921$var$_frameCounter = 0;
    $de009be455ddb921$var$_enabled = true;
}
function $de009be455ddb921$export$c4bbdd756e8fe93b() {
    return $de009be455ddb921$var$_lastSnapshot;
}
function $de009be455ddb921$export$19727ae3e1f89c42() {
    if (!$de009be455ddb921$var$_enabled) return;
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    if (!state || (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight == null || (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight == null) return;
    $de009be455ddb921$var$_frameCounter++;
    const foundationsWithCards = state.foundation.filter((pile)=>pile.length > 0).length;
    const foundationRenderPresence = state.foundation.map((pile)=>{
        if (pile.length === 0) return 0;
        const top = pile[pile.length - 1];
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(top);
        return Number(Number.isFinite(pos.x) && Number.isFinite(pos.y));
    });
    const animatedFoundationCards = state.foundation.reduce((sum, pile)=>{
        return sum + pile.filter((card)=>(0, $f08e1321bb9509a9$export$1bbf3d74e37df777)(card)).length;
    }, 0);
    const topFoundationOverlays = state.foundation.reduce((sum, pile)=>{
        if (pile.length === 0) return sum;
        return sum + ((0, $f08e1321bb9509a9$export$1bbf3d74e37df777)(pile[pile.length - 1]) ? 1 : 0);
    }, 0);
    const nonTopFoundationVisibleCount = state.foundation.reduce((sum, pile)=>{
        if (pile.length < 2) return sum;
        const secondTop = pile[pile.length - 2];
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(secondTop);
        const hasCoords = Number(Number.isFinite(pos.x) && Number.isFinite(pos.y));
        return sum + hasCoords;
    }, 0);
    let tableauBottom = 0;
    for (const pile of state.tableau){
        if (pile.length === 0) continue;
        const last = pile[pile.length - 1];
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(last);
        tableauBottom = Math.max(tableauBottom, pos.y + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight);
    }
    const viewportBottom = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const tableauOverflowPx = Math.max(0, Math.round(tableauBottom - viewportBottom));
    $de009be455ddb921$var$_lastSnapshot = {
        frame: $de009be455ddb921$var$_frameCounter,
        foundationsWithCards: foundationsWithCards,
        foundationRenderPresence: foundationRenderPresence,
        animatedFoundationCards: animatedFoundationCards,
        topFoundationOverlays: topFoundationOverlays,
        nonTopFoundationVisibleCount: nonTopFoundationVisibleCount,
        tableauBottom: tableauBottom,
        viewportBottom: viewportBottom,
        tableauOverflowPx: tableauOverflowPx
    };
}


/**
 * Canvas-rendered game timer display.
 *
 * Positioned in Row 0, centered horizontally between the menu and new game buttons.
 * Shows "M:SS" or "H:MM:SS" during gameplay and on the completion screen.
 */ 




function $2bce7c604cfb2ebb$export$93745e833f4da357(ctx) {
    // Only show if the timer has been started (player made at least one move)
    if (!(0, $d639f68dfbcef401$export$58037dfed0af18fd)()) return;
    const elapsed = (0, $d639f68dfbcef401$export$611ce66014572f0c)();
    const timeStr = (0, $d639f68dfbcef401$export$3203edd9e5edd663)(elapsed);
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const bgColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor;
    const lightBg = (0, $f9eed79712445f27$export$6b84b5f288b224da)(bgColor);
    // Font size: proportional to card height, slightly smaller than button icons
    const fontSize = Math.floor(ch * 0.28);
    ctx.save();
    ctx.font = `500 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Color: subtle but readable against the background
    if (lightBg) ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    else ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    // Position: centered in Row 0
    const y = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0) + Math.floor(ch / 2);
    const x = Math.floor(winW / 2);
    ctx.fillText(timeStr, x, y);
    ctx.restore();
}
function $2bce7c604cfb2ebb$export$dcaa803537069682(ctx, overrideElapsed) {
    const elapsed = overrideElapsed ?? (0, $d639f68dfbcef401$export$611ce66014572f0c)();
    const timeStr = (0, $d639f68dfbcef401$export$3203edd9e5edd663)(elapsed);
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    // Larger font for completion
    const fontSize = Math.floor(ch * 0.35);
    // Position: centered in Row 0
    const centerY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0) + Math.floor(ch / 2);
    const x = Math.floor(winW / 2);
    ctx.save();
    // Timer value
    ctx.font = `600 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText(timeStr, x, centerY - Math.floor(fontSize * 0.35));
    // "You Win!" subtitle in theme highlight color
    const subFontSize = Math.floor(ch * 0.2);
    ctx.font = `700 ${subFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.fillStyle = highlightColor;
    ctx.fillText('You Win!', x, centerY + Math.floor(fontSize * 0.45));
    ctx.restore();
}


/**
 * Canvas-rendered leaderboard display for the completion screen.
 *
 * Shows the top 10 game records below the completion timer,
 * with rank, penalized time, raw time, and undo count for each entry.
 *
 * Layout on the completion screen:
 *   Row 0: Timer + New Game button (drawn by drawTimer.ts / drawButtons.ts)
 *   Row 1: Current game summary (time, undos, penalty)
 *   Row 2: "BEST TIMES" header
 *   Rows 2.5–8: Up to 10 records as compact lines
 */ 





let $d7974594ecddd420$var$_debugDrawRecordsCallCount = 0;
function $d7974594ecddd420$export$55eda3bb7df9efea() {
    return $d7974594ecddd420$var$_debugDrawRecordsCallCount;
}
function $d7974594ecddd420$export$83718a232b40ec35() {
    $d7974594ecddd420$var$_debugDrawRecordsCallCount = 0;
}
function $d7974594ecddd420$export$81da61b567794d85(ctx) {
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const winH = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    // Panel spans from above row 0 to the bottom of the screen
    const panelPadding = Math.floor(ch * 0.3);
    const panelX = panelPadding;
    const panelY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(0) - Math.floor(ch * 0.15);
    const panelW = winW - panelPadding * 2;
    const panelH = winH - panelY - panelPadding;
    const panelR = Math.max(8, Math.floor(ch * 0.15));
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.82)';
    ctx.beginPath();
    ctx.roundRect(panelX, panelY, panelW, panelH, panelR);
    ctx.fill();
    ctx.restore();
}
function $d7974594ecddd420$export$e716d0fbb0101e0d(ctx) {
    $d7974594ecddd420$var$_debugDrawRecordsCallCount++;
    // Keep backward compatibility with e2e checks that assert this function
    // runs immediately after wins (Issue #19).
    // Actual visibility is controlled by appState.showWinRecords.
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords) return;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const winW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const highlightColor = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor;
    // ── Color palette (designed for the dark completion backdrop) ──────
    const primaryColor = 'rgba(255, 255, 255, 0.9)';
    const secondaryColor = 'rgba(255, 255, 255, 0.6)';
    const mutedColor = 'rgba(255, 255, 255, 0.35)';
    const centerX = Math.floor(winW / 2);
    // ── Row 1: Current game summary ─────────────────────────────────────
    const summaryY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(1) + Math.floor(ch / 2);
    const undoCount = (0, $13e781a56ac394f5$export$41b9add8aee40b4e)();
    const rawTime = (0, $d639f68dfbcef401$export$611ce66014572f0c)();
    const summaryFontSize = Math.floor(ch * 0.2);
    ctx.save();
    ctx.font = `500 ${summaryFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = secondaryColor;
    if (undoCount > 0) {
        const penalty = undoCount * (0, $b1c4622a6095546e$export$32529dd2809e488c);
        const penalizedTime = rawTime + penalty;
        const line1 = `${(0, $d639f68dfbcef401$export$3203edd9e5edd663)(rawTime)} + ${undoCount} undo${undoCount !== 1 ? 's' : ''} (+${(0, $d639f68dfbcef401$export$3203edd9e5edd663)(penalty)})`;
        const line2 = `Final: ${(0, $d639f68dfbcef401$export$3203edd9e5edd663)(penalizedTime)}`;
        const lineSpacing = Math.floor(summaryFontSize * 1.7);
        ctx.fillText(line1, centerX, summaryY - Math.floor(lineSpacing * 0.45));
        ctx.font = `600 ${summaryFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.fillStyle = primaryColor;
        ctx.fillText(line2, centerX, summaryY + Math.floor(lineSpacing * 0.55));
    } else ctx.fillText('No undos used', centerX, summaryY);
    ctx.restore();
    // ── Load records ────────────────────────────────────────────────────
    const records = (0, $15b077d75e975a2b$export$8008240685e80d26)();
    if (records.length === 0) return;
    // ── Row 2: "BEST TIMES" header ──────────────────────────────────────
    const headerY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(2) + Math.floor(ch * 0.35);
    const headerFontSize = Math.floor(ch * 0.18);
    ctx.save();
    ctx.font = `700 ${headerFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = mutedColor;
    ctx.letterSpacing = '2px';
    ctx.fillText('BEST TIMES', centerX, headerY);
    ctx.restore();
    // ── Records list ────────────────────────────────────────────────────
    // Start below the header, fit up to 10 records between rows 2.5 and 8
    const listStartY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(2) + Math.floor(ch * 0.65);
    const listEndY = (0, $0f5b71ede2b50336$export$370b3d6f888e2af4)(8) + Math.floor(ch * 0.5);
    const availableHeight = listEndY - listStartY;
    const recordFontSize = Math.floor(ch * 0.17);
    const lineHeight = Math.min(Math.floor(recordFontSize * 2.2), Math.floor(availableHeight / Math.min(records.length, 10)));
    // Column layout: rank | time | undos
    // All centered around the middle of the screen
    const colWidth = Math.floor(winW * 0.8);
    const colStartX = Math.floor((winW - colWidth) / 2);
    const rankX = colStartX + Math.floor(colWidth * 0.08);
    const timeX = colStartX + Math.floor(colWidth * 0.35);
    const undoX = colStartX + Math.floor(colWidth * 0.72);
    for(let i = 0; i < records.length; i++){
        const record = records[i];
        const y = listStartY + i * lineHeight;
        // Don't render if we've overflowed
        if (y > listEndY) break;
        const isTopThree = i < 3;
        ctx.save();
        // Rank number
        ctx.font = `${isTopThree ? '700' : '500'} ${recordFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isTopThree ? highlightColor : mutedColor;
        ctx.fillText(`${i + 1}.`, rankX, y);
        // Penalized time (main value)
        ctx.font = `${isTopThree ? '600' : '500'} ${recordFontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = isTopThree ? primaryColor : secondaryColor;
        ctx.fillText((0, $d639f68dfbcef401$export$3203edd9e5edd663)(record.penalizedTime), timeX, y);
        // Undo count (if any)
        ctx.font = `400 ${Math.floor(recordFontSize * 0.85)}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (record.undoCount > 0) {
            ctx.fillStyle = mutedColor;
            ctx.fillText(`${record.undoCount} undo${record.undoCount !== 1 ? 's' : ''}`, undoX, y);
        } else {
            ctx.fillStyle = mutedColor;
            ctx.fillText('clean', undoX, y);
        }
        ctx.restore();
    }
}





/**
 * Win celebration animation — canvas-rendered card cascade + confetti.
 *
 * Classic Windows Solitaire-style: cards launch from foundations with random
 * velocity, fall under gravity, and bounce off screen edges leaving trails.
 * Confetti particles shoot from bottom corners.
 *
 * Architecture:
 *   - Self-contained RAF loop (like AnimationManagerClass)
 *   - Does NOT use the card position overlay system — manages its own rendering
 *   - paint.ts calls drawWinAnimation() in the Completed branch
 *   - startWinAnimation() is called once when the game transitions to Completed
 *   - stopWinAnimation() cleans up when starting a new game
 */ 







// ─── Module state ───────────────────────────────────────────────────────────
let $03f1cb60f93a62c7$var$_running = false;
let $03f1cb60f93a62c7$var$_finished = false; // True after animation naturally completes (trail stamps preserved)
let $03f1cb60f93a62c7$var$_rafId = null;
let $03f1cb60f93a62c7$var$_startTime = 0;
// Card cascade state
let $03f1cb60f93a62c7$var$_cascadeCards = [];
let $03f1cb60f93a62c7$var$_trailCanvas = null; // Offscreen buffer for permanent stamps
let $03f1cb60f93a62c7$var$_trailCtx = null;
let $03f1cb60f93a62c7$var$_globalFrame = 0; // Frame counter for stamp spacing
let $03f1cb60f93a62c7$var$_cardQueue = [];
let $03f1cb60f93a62c7$var$_lastLaunchTime = 0;
const $03f1cb60f93a62c7$var$LAUNCH_INTERVAL = 100; // ms between card launches
// Confetti state
let $03f1cb60f93a62c7$var$_confettiParticles = [];
let $03f1cb60f93a62c7$var$_lastConfettiBurst = 0;
const $03f1cb60f93a62c7$var$CONFETTI_BURST_INTERVAL = 150; // ms between bursts
const $03f1cb60f93a62c7$var$TRAIL_FADE_ALPHA = 0.012; // per-frame opacity reduction on trail canvas (~4s full fade)
// Bob Ross easter egg
let $03f1cb60f93a62c7$var$_showBobRoss = false;
// Text fade-in
const $03f1cb60f93a62c7$var$TEXT_FADE_START = 1500; // ms after start to begin fading in text
const $03f1cb60f93a62c7$var$TEXT_FADE_DURATION = 800; // ms to fully fade in
function $03f1cb60f93a62c7$export$3aad2ad743dc566c() {
    return $03f1cb60f93a62c7$var$_running;
}
function $03f1cb60f93a62c7$export$9a105ec5ce69446b() {
    return $03f1cb60f93a62c7$var$_showBobRoss;
}
function $03f1cb60f93a62c7$export$f595596231ffb238() {
    if ($03f1cb60f93a62c7$var$_running) return;
    // Skip animation entirely if animations are disabled (e.g. during tests)
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.animationsEnabled) return;
    $03f1cb60f93a62c7$var$_running = true;
    $03f1cb60f93a62c7$var$_startTime = performance.now();
    $03f1cb60f93a62c7$var$_cascadeCards = [];
    $03f1cb60f93a62c7$var$_confettiParticles = [];
    $03f1cb60f93a62c7$var$_lastLaunchTime = 0;
    $03f1cb60f93a62c7$var$_lastConfettiBurst = 0;
    $03f1cb60f93a62c7$var$_globalFrame = 0;
    // Create offscreen canvas for permanent trail stamps
    const dpr = window.devicePixelRatio || 1;
    $03f1cb60f93a62c7$var$_trailCanvas = document.createElement('canvas');
    $03f1cb60f93a62c7$var$_trailCanvas.width = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth * dpr;
    $03f1cb60f93a62c7$var$_trailCanvas.height = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight * dpr;
    $03f1cb60f93a62c7$var$_trailCtx = $03f1cb60f93a62c7$var$_trailCanvas.getContext('2d');
    if ($03f1cb60f93a62c7$var$_trailCtx) $03f1cb60f93a62c7$var$_trailCtx.scale(dpr, dpr);
    // Bob Ross: 1% chance
    $03f1cb60f93a62c7$var$_showBobRoss = Math.random() < 0.01;
    // Build the card queue from foundations — King on top, Ace at bottom
    // Launch order: cycle through foundations, top card first (King → Queen → ... → Ace)
    $03f1cb60f93a62c7$var$_cardQueue = [];
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const maxLen = Math.max(...state.foundation.map((f)=>f.length));
    for(let i = maxLen - 1; i >= 0; i--){
        for(let f = 0; f < 4; f++)if (i < state.foundation[f].length) $03f1cb60f93a62c7$var$_cardQueue.push({
            card: state.foundation[f][i],
            foundationIndex: f
        });
    }
    // Start the RAF loop
    $03f1cb60f93a62c7$var$_rafId = requestAnimationFrame($03f1cb60f93a62c7$var$tick);
}
function $03f1cb60f93a62c7$export$cf37c3ca56de35b0() {
    $03f1cb60f93a62c7$var$_running = false;
    $03f1cb60f93a62c7$var$_finished = false;
    if ($03f1cb60f93a62c7$var$_rafId !== null) {
        cancelAnimationFrame($03f1cb60f93a62c7$var$_rafId);
        $03f1cb60f93a62c7$var$_rafId = null;
    }
    $03f1cb60f93a62c7$var$_cascadeCards = [];
    $03f1cb60f93a62c7$var$_trailCanvas = null;
    $03f1cb60f93a62c7$var$_trailCtx = null;
    $03f1cb60f93a62c7$var$_globalFrame = 0;
    $03f1cb60f93a62c7$var$_cardQueue = [];
    $03f1cb60f93a62c7$var$_confettiParticles = [];
    $03f1cb60f93a62c7$var$_showBobRoss = false;
}
// ─── Core loop ──────────────────────────────────────────────────────────────
function $03f1cb60f93a62c7$var$tick() {
    if (!$03f1cb60f93a62c7$var$_running) return;
    const now = performance.now();
    const elapsed = now - $03f1cb60f93a62c7$var$_startTime;
    // Launch queued cards at intervals
    if ($03f1cb60f93a62c7$var$_cardQueue.length > 0 && elapsed - $03f1cb60f93a62c7$var$_lastLaunchTime >= $03f1cb60f93a62c7$var$LAUNCH_INTERVAL) {
        $03f1cb60f93a62c7$var$launchNextCard();
        $03f1cb60f93a62c7$var$_lastLaunchTime = elapsed;
    }
    // Spawn confetti bursts
    if (elapsed - $03f1cb60f93a62c7$var$_lastConfettiBurst >= $03f1cb60f93a62c7$var$CONFETTI_BURST_INTERVAL && elapsed < 6000) {
        $03f1cb60f93a62c7$var$spawnConfettiBurst();
        $03f1cb60f93a62c7$var$_lastConfettiBurst = elapsed;
    }
    // Physics step
    $03f1cb60f93a62c7$var$updateCascadeCards();
    $03f1cb60f93a62c7$var$updateConfetti();
    // Increment frame counter (used for stamp spacing)
    $03f1cb60f93a62c7$var$_globalFrame++;
    // Render
    const canvas = document.getElementById('canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Clear and redraw background
    ctx.clearRect(0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor;
    ctx.fillRect(0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
    // Draw trail stamps (oldest first, so newest are on top)
    $03f1cb60f93a62c7$var$drawTrailStamps(ctx);
    // Draw active cascade cards
    $03f1cb60f93a62c7$var$drawCascadeCards(ctx);
    // Draw confetti
    $03f1cb60f93a62c7$var$drawConfetti(ctx);
    // Draw "You Win!" text with fade-in
    $03f1cb60f93a62c7$var$drawWinText(ctx, elapsed);
    // Draw completion UI on top of the animated background
    (0, $80cf1bdd1366d994$export$7ee7977b8f9a883)(ctx);
    (0, $80cf1bdd1366d994$export$9130244d9f40b66d)(ctx);
    (0, $2bce7c604cfb2ebb$export$dcaa803537069682)(ctx);
    // Keep drawRecords call active for diagnostics/e2e checks even when
    // the panel is hidden by default on the win screen.
    (0, $d7974594ecddd420$export$e716d0fbb0101e0d)(ctx);
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords) {
        (0, $d7974594ecddd420$export$81da61b567794d85)(ctx);
        (0, $2bce7c604cfb2ebb$export$dcaa803537069682)(ctx);
        (0, $d7974594ecddd420$export$e716d0fbb0101e0d)(ctx);
        (0, $80cf1bdd1366d994$export$7ee7977b8f9a883)(ctx);
        (0, $80cf1bdd1366d994$export$9130244d9f40b66d)(ctx);
    }
    // Continue while cards are still flying, queued, or confetti is active
    if ($03f1cb60f93a62c7$var$_cascadeCards.length > 0 || $03f1cb60f93a62c7$var$_cardQueue.length > 0 || $03f1cb60f93a62c7$var$_confettiParticles.length > 0) $03f1cb60f93a62c7$var$_rafId = requestAnimationFrame($03f1cb60f93a62c7$var$tick);
    else {
        // Everything is gone — clean end state
        $03f1cb60f93a62c7$var$_running = false;
        $03f1cb60f93a62c7$var$_finished = true;
        $03f1cb60f93a62c7$var$_rafId = null;
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    }
}
// ─── Card cascade ───────────────────────────────────────────────────────────
function $03f1cb60f93a62c7$var$launchNextCard() {
    const entry = $03f1cb60f93a62c7$var$_cardQueue.shift();
    if (!entry) return;
    const { card: card, foundationIndex: foundationIndex } = entry;
    const startX = (0, $0f5b71ede2b50336$export$84ce64f2330f553a)(foundationIndex);
    const startY = (0, $0f5b71ede2b50336$export$7cb2c3b01d2fdd74)(foundationIndex);
    // Random horizontal velocity — spread cards widely across the screen
    const centerX = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth / 2;
    const directionToCenter = centerX > startX + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2 ? 1 : -1;
    // Mix of center-biased and random direction for wide spread
    const randomDir = Math.random() < 0.3 ? -directionToCenter : directionToCenter;
    const vx = randomDir * (3 + Math.random() * 5) + (Math.random() - 0.5) * 2;
    // Initial upward velocity (negative = up) — moderate arc for good screen coverage
    const vy = -(5 + Math.random() * 5);
    // Slight random rotation
    const vr = (Math.random() - 0.5) * 0.04;
    $03f1cb60f93a62c7$var$_cascadeCards.push({
        card: card,
        x: startX,
        y: startY,
        vx: vx,
        vy: vy,
        rotation: 0,
        vr: vr,
        frameCount: 0
    });
}
function $03f1cb60f93a62c7$var$updateCascadeCards() {
    const gravity = 0.25;
    const bounceDamping = 0.75;
    const trailInterval = 30; // leave a fading trail stamp every N frames
    const w = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const h = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    const toRemove = [];
    for(let i = 0; i < $03f1cb60f93a62c7$var$_cascadeCards.length; i++){
        const cc = $03f1cb60f93a62c7$var$_cascadeCards[i];
        // Apply gravity
        cc.vy += gravity;
        // Update position
        cc.x += cc.vx;
        cc.y += cc.vy;
        cc.rotation += cc.vr;
        cc.frameCount++;
        // Leave continuous trail stamps (classic Windows Solitaire smearing effect)
        if (cc.frameCount % trailInterval === 0) $03f1cb60f93a62c7$var$stampTrailCard(cc.card, cc.x, cc.y); // permanent trail — builds up the painted screen
        // Bounce off left/right walls
        if (cc.x < 0) {
            cc.x = 0;
            cc.vx = Math.abs(cc.vx) * bounceDamping;
            cc.vr = -cc.vr;
        } else if (cc.x + cw > w) {
            cc.x = w - cw;
            cc.vx = -Math.abs(cc.vx) * bounceDamping;
            cc.vr = -cc.vr;
        }
        // Bounce off bottom
        if (cc.y + ch > h) {
            cc.y = h - ch;
            cc.vy = -Math.abs(cc.vy) * bounceDamping;
            cc.vr *= 0.8;
            // Leave a permanent stamp at each bounce point — builds up the classic layered look
            $03f1cb60f93a62c7$var$stampTrailCard(cc.card, cc.x, cc.y);
            // If barely bouncing, remove the card (it already got its permanent stamp above)
            if (Math.abs(cc.vy) < 1.5) toRemove.push(i);
        }
        // Remove if off screen (left/right after a wild bounce, or somehow up)
        if (cc.y > h + ch * 2 || cc.x < -cw * 2 || cc.x > w + cw * 2) toRemove.push(i);
    }
    // Remove in reverse order to preserve indices
    for(let i = toRemove.length - 1; i >= 0; i--)$03f1cb60f93a62c7$var$_cascadeCards.splice(toRemove[i], 1);
}
/** Bake a trail stamp onto the offscreen canvas at the current position. */ function $03f1cb60f93a62c7$var$stampTrailCard(card, x, y) {
    if ($03f1cb60f93a62c7$var$_trailCtx) {
        $03f1cb60f93a62c7$var$_trailCtx.globalAlpha = 1;
        (0, $b3d87dc43fab0d75$export$78a64eb29b8df76c)(card, $03f1cb60f93a62c7$var$_trailCtx, x, y);
    }
}
/** Blit permanent stamps from offscreen buffer. All stamps are permanent now. */ function $03f1cb60f93a62c7$var$drawTrailStamps(ctx) {
    if ($03f1cb60f93a62c7$var$_trailCanvas) ctx.drawImage($03f1cb60f93a62c7$var$_trailCanvas, 0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
}
function $03f1cb60f93a62c7$var$drawCascadeCards(ctx) {
    for (const cc of $03f1cb60f93a62c7$var$_cascadeCards)(0, $b3d87dc43fab0d75$export$78a64eb29b8df76c)(cc.card, ctx, cc.x, cc.y);
}
// ─── Confetti ───────────────────────────────────────────────────────────────
function $03f1cb60f93a62c7$var$spawnConfettiBurst() {
    const w = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const h = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const theme = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme;
    // Confetti colors: mix of theme highlight, red/black card colors, and card face/back
    const colors = [
        theme.highlightColor,
        theme.redCardColor,
        theme.cardBackColor,
        theme.cardFaceColor,
        theme.textColor
    ];
    // Spawn from both bottom corners
    for (const cornerX of [
        0,
        w
    ]){
        const count = 4 + Math.floor(Math.random() * 4);
        for(let i = 0; i < count; i++){
            const dirX = cornerX === 0 ? 1 : -1;
            $03f1cb60f93a62c7$var$_confettiParticles.push({
                x: cornerX + dirX * Math.random() * 30,
                y: h - Math.random() * 20,
                vx: dirX * (2 + Math.random() * 5),
                vy: -(6 + Math.random() * 8),
                width: 3 + Math.random() * 5,
                height: 2 + Math.random() * 4,
                color: colors[Math.floor(Math.random() * colors.length)],
                rotation: Math.random() * Math.PI * 2,
                vr: (Math.random() - 0.5) * 0.3,
                opacity: 1,
                gravity: 0.12 + Math.random() * 0.06
            });
        }
    }
}
function $03f1cb60f93a62c7$var$updateConfetti() {
    const h = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const toRemove = [];
    for(let i = 0; i < $03f1cb60f93a62c7$var$_confettiParticles.length; i++){
        const p = $03f1cb60f93a62c7$var$_confettiParticles[i];
        p.vy += p.gravity;
        p.vx *= 0.99; // air resistance
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.vr;
        // Fade out as they fall past 70% of screen height
        if (p.y > h * 0.7) p.opacity -= 0.02;
        // Remove if off screen or fully faded
        if (p.y > h + 20 || p.opacity <= 0) toRemove.push(i);
    }
    for(let i = toRemove.length - 1; i >= 0; i--)$03f1cb60f93a62c7$var$_confettiParticles.splice(toRemove[i], 1);
}
function $03f1cb60f93a62c7$var$drawConfetti(ctx) {
    for (const p of $03f1cb60f93a62c7$var$_confettiParticles){
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.opacity);
        // Parse the color and apply it
        const [r, g, b] = (0, $f9eed79712445f27$export$cb557c8471d45d6e)(p.color);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${p.opacity})`;
        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
        ctx.restore();
    }
    ctx.globalAlpha = 1;
}
// ─── Win text ───────────────────────────────────────────────────────────────
function $03f1cb60f93a62c7$var$drawWinText(ctx, elapsed) {
    if (elapsed < $03f1cb60f93a62c7$var$TEXT_FADE_START) return;
    const fadeProgress = Math.min(1, (elapsed - $03f1cb60f93a62c7$var$TEXT_FADE_START) / $03f1cb60f93a62c7$var$TEXT_FADE_DURATION);
    const theme = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme;
    const w = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth;
    const h = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    // "You Win!" in the upper third of the screen
    const fontSize = Math.floor(ch * 0.6);
    const textY = Math.floor(h * 0.35);
    ctx.save();
    ctx.globalAlpha = fadeProgress;
    ctx.font = `700 ${fontSize}px ${0, $b3d87dc43fab0d75$export$7967f0b16ea92252}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // Measure text to draw a dark pill backdrop behind it
    const metrics = ctx.measureText('You Win!');
    const textWidth = metrics.width;
    const paddingX = fontSize * 0.5;
    const paddingY = fontSize * 0.35;
    const pillX = w / 2 - textWidth / 2 - paddingX;
    const pillY = textY - fontSize / 2 - paddingY;
    const pillW = textWidth + paddingX * 2;
    const pillH = fontSize + paddingY * 2;
    const pillR = pillH / 2;
    // Semi-transparent dark backdrop pill
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillW, pillH, pillR);
    ctx.fill();
    // Main text in theme highlight color (no shadow needed with backdrop)
    ctx.fillStyle = theme.highlightColor;
    ctx.fillText('You Win!', w / 2, textY);
    ctx.restore();
}


'use-strict';
// ─── Cached references (looked up once, reused every frame) ─────────
let $71e2b16f726ebff4$var$_canvas = null;
let $71e2b16f726ebff4$var$_ctx = null;
let $71e2b16f726ebff4$var$_bobRoss = null;
// Tracks the last canvas size so we only reallocate when dimensions change
let $71e2b16f726ebff4$var$_lastCanvasW = 0;
let $71e2b16f726ebff4$var$_lastCanvasH = 0;
// RAF deduplication — prevent multiple paint() per frame
let $71e2b16f726ebff4$var$_paintScheduled = false;
let $71e2b16f726ebff4$var$_paintRafId = null;
// Track last-rendered app state to skip redundant DOM class toggling
let $71e2b16f726ebff4$var$_lastRenderedAppState = null;
function $71e2b16f726ebff4$var$ensureCachedElements() {
    if (!$71e2b16f726ebff4$var$_canvas) {
        $71e2b16f726ebff4$var$_canvas = document.getElementById('canvas');
        if (!$71e2b16f726ebff4$var$_canvas) return false;
        $71e2b16f726ebff4$var$_ctx = $71e2b16f726ebff4$var$_canvas.getContext('2d');
    }
    if (!$71e2b16f726ebff4$var$_bobRoss) $71e2b16f726ebff4$var$_bobRoss = document.querySelector('#BobRossDoingTheFlossLikeABoss');
    return $71e2b16f726ebff4$var$_canvas !== null && $71e2b16f726ebff4$var$_ctx !== null;
}
function $71e2b16f726ebff4$export$adff1037b5320216() {
    if ($71e2b16f726ebff4$var$_paintScheduled) return;
    $71e2b16f726ebff4$var$_paintScheduled = true;
    $71e2b16f726ebff4$var$_paintRafId = requestAnimationFrame(()=>{
        $71e2b16f726ebff4$var$_paintScheduled = false;
        $71e2b16f726ebff4$var$_paintRafId = null;
        $71e2b16f726ebff4$export$4e0651d2e7bf59d0();
    });
}
function $71e2b16f726ebff4$export$57b918346606b0b9() {
    if ($71e2b16f726ebff4$var$_paintRafId !== null) {
        cancelAnimationFrame($71e2b16f726ebff4$var$_paintRafId);
        $71e2b16f726ebff4$var$_paintRafId = null;
        $71e2b16f726ebff4$var$_paintScheduled = false;
    }
}
function $71e2b16f726ebff4$export$4e0651d2e7bf59d0() {
    if (!$71e2b16f726ebff4$var$ensureCachedElements()) return;
    const canvas = $71e2b16f726ebff4$var$_canvas;
    const ctx = $71e2b16f726ebff4$var$_ctx;
    const dpr = window.devicePixelRatio || 1;
    const targetW = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth * dpr;
    const targetH = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight * dpr;
    // Only reallocate the canvas buffer when dimensions actually change
    if (targetW !== $71e2b16f726ebff4$var$_lastCanvasW || targetH !== $71e2b16f726ebff4$var$_lastCanvasH) {
        canvas.width = targetW;
        canvas.height = targetH;
        canvas.style.width = `${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth}px`;
        canvas.style.height = `${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight}px`;
        $71e2b16f726ebff4$var$_lastCanvasW = targetW;
        $71e2b16f726ebff4$var$_lastCanvasH = targetH;
        // canvas.width reset clears transforms, so we must re-apply
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.scale(dpr, dpr);
    } else // Clear without reallocating the buffer — much faster
    ctx.clearRect(0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
    // Solid background fill — flat, minimal, no gradient
    ctx.fillStyle = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor;
    ctx.fillRect(0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
    switch((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState){
        case (0, $7629cc56581bca5c$export$812e1401122ad355).Playing:
            // Only toggle DOM classes when app state actually changes
            if ($71e2b16f726ebff4$var$_lastRenderedAppState !== (0, $7629cc56581bca5c$export$812e1401122ad355).Playing) {
                canvas.classList.remove('hidden');
                if ($71e2b16f726ebff4$var$_bobRoss) // @ts-ignore
                $71e2b16f726ebff4$var$_bobRoss.style.display = 'none';
                $71e2b16f726ebff4$var$_lastRenderedAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
            }
            (0, $35a1d072802c98cd$export$9e532f80718cf410)(ctx);
            (0, $35a1d072802c98cd$export$f8739f0d354a251)(ctx);
            (0, $35a1d072802c98cd$export$644db5322f7be988)(ctx);
            (0, $35a1d072802c98cd$export$b80754d417af4fc)(ctx);
            (0, $30a068696fab8a07$export$34c0ba4aa7183cc5)(ctx);
            (0, $30a068696fab8a07$export$b49875388cc6c6c3)(ctx);
            (0, $30a068696fab8a07$export$559b888e12de6444)(ctx);
            (0, $30a068696fab8a07$export$575c2a44bd34199e)(ctx);
            (0, $30a068696fab8a07$export$637f9f751eccb893)(ctx);
            // Buttons drawn AFTER cards so they appear on top when long
            // tableau cascades extend into the button area
            (0, $80cf1bdd1366d994$export$7ee7977b8f9a883)(ctx);
            (0, $80cf1bdd1366d994$export$1be5916984d004f)(ctx);
            (0, $80cf1bdd1366d994$export$726d21e4aad94310)(ctx);
            if ((0, $1a73a0108f314080$export$9a5d87b5cc49764d)()) (0, $80cf1bdd1366d994$export$6e9db4b4118e074a)(ctx);
            (0, $2bce7c604cfb2ebb$export$93745e833f4da357)(ctx);
            (0, $30a068696fab8a07$export$75581f0c030577ec)(ctx);
            break;
        case (0, $7629cc56581bca5c$export$812e1401122ad355).Completed:
            if ($71e2b16f726ebff4$var$_lastRenderedAppState !== (0, $7629cc56581bca5c$export$812e1401122ad355).Completed) {
                canvas.classList.remove('hidden');
                // Bob Ross: only show on 1% easter egg
                if ($71e2b16f726ebff4$var$_bobRoss) // @ts-ignore
                $71e2b16f726ebff4$var$_bobRoss.style.display = (0, $03f1cb60f93a62c7$export$9a105ec5ce69446b)() ? 'block' : 'none';
                $71e2b16f726ebff4$var$_lastRenderedAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Completed;
                // Start the win animation (card cascade + confetti)
                (0, $03f1cb60f93a62c7$export$f595596231ffb238)();
                // Default to a clean win view; records are optional via toggle.
                (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords = false;
            }
            // While the win animation is running, it handles its own rendering
            // via its own RAF loop. Only draw static elements here when it's done.
            if (!(0, $03f1cb60f93a62c7$export$3aad2ad743dc566c)()) {
                (0, $80cf1bdd1366d994$export$7ee7977b8f9a883)(ctx);
                (0, $80cf1bdd1366d994$export$9130244d9f40b66d)(ctx);
                (0, $2bce7c604cfb2ebb$export$dcaa803537069682)(ctx);
                // Keep drawRecords hot-path alive for existing diagnostics/tests,
                // but only render visible content when the panel is enabled.
                (0, $d7974594ecddd420$export$e716d0fbb0101e0d)(ctx);
                if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords) {
                    // Overlay records only when explicitly requested.
                    (0, $d7974594ecddd420$export$81da61b567794d85)(ctx);
                    (0, $2bce7c604cfb2ebb$export$dcaa803537069682)(ctx);
                    (0, $d7974594ecddd420$export$e716d0fbb0101e0d)(ctx);
                    (0, $80cf1bdd1366d994$export$7ee7977b8f9a883)(ctx);
                    (0, $80cf1bdd1366d994$export$9130244d9f40b66d)(ctx);
                }
            }
            break;
        case (0, $7629cc56581bca5c$export$812e1401122ad355).Menu:
            if ($71e2b16f726ebff4$var$_lastRenderedAppState !== (0, $7629cc56581bca5c$export$812e1401122ad355).Menu) {
                canvas.classList.remove('hidden');
                if ($71e2b16f726ebff4$var$_bobRoss) // @ts-ignore
                $71e2b16f726ebff4$var$_bobRoss.style.display = 'none';
                $71e2b16f726ebff4$var$_lastRenderedAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Menu;
            }
            // Dim overlay so game content is visually behind the menu
            ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
            ctx.fillRect(0, 0, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight);
            // Canvas-rendered menu: theme swatches, toggles, hit areas
            (0, $41c329ef74cc3b7d$export$aa84b2f9522188a7)(ctx);
            (0, $80cf1bdd1366d994$export$726d21e4aad94310)(ctx);
            break;
    }
    // Update per-frame render telemetry for logic-based e2e assertions.
    (0, $de009be455ddb921$export$19727ae3e1f89c42)();
}







function $f593745b1b99abdc$export$245b33143bc6e84d(state) {
    const stock = state.stock ?? [];
    const tableau = state.tableau ?? [
        [],
        [],
        [],
        [],
        [],
        [],
        []
    ];
    const waste = state.waste ?? [];
    const foundation = state.foundation ?? [
        [],
        [],
        [],
        []
    ];
    const draggedCards = state.draggedCards ?? [];
    // 1. Assign coordinates to the cards in the stock.
    for (const card of stock){
        const x = (0, $0f5b71ede2b50336$export$889074c399efa9cf)();
        const y = (0, $0f5b71ede2b50336$export$72029e8b50497416)();
        card.x = x;
        card.originalX = x;
        card.y = y;
        card.originalY = y;
    }
    // 2. Assign coordinates to each card in each tableau
    for(let i = 0; i < tableau.length; i++){
        let cumulativeOffset = 0;
        for (const card of tableau[i]){
            const baseX = (0, $0f5b71ede2b50336$export$f8ddd168b43d671a)(i);
            const baseY = (0, $0f5b71ede2b50336$export$38cc5b0bc1d504c6)(i);
            let x;
            let y;
            if ((0, $0f5b71ede2b50336$export$72f5d9a44bd9a7f0)()) {
                x = baseX;
                y = baseY + cumulativeOffset;
            } else {
                x = (0, $0f5b71ede2b50336$export$e79bc7cac697f199)() ? baseX + cumulativeOffset : baseX - cumulativeOffset;
                y = baseY;
            }
            card.x = x;
            card.originalX = x;
            card.y = y;
            card.originalY = y;
            cumulativeOffset += (0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(card.isFaceDown);
        }
    }
    // In portrait mode, compress overly long face-up cascades so bottom cards
    // remain within the viewport on smaller devices.
    if (!(0, $0f5b71ede2b50336$export$72f5d9a44bd9a7f0)()) {
        const minStep = Math.max(10, Math.floor(((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth ?? 0) * 0.12));
        for(let i = 0; i < tableau.length; i++){
            const pile = tableau[i];
            if (pile.length <= 1) continue;
            const baseX = (0, $0f5b71ede2b50336$export$f8ddd168b43d671a)(i);
            const baseY = (0, $0f5b71ede2b50336$export$38cc5b0bc1d504c6)(i);
            const start = (0, $0f5b71ede2b50336$export$e79bc7cac697f199)() ? 1 : -1;
            const steps = pile.slice(0, -1).map((c)=>(0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(c.isFaceDown));
            const totalOffset = steps.reduce((sum, s)=>sum + s, 0);
            // Determine available horizontal room so we can shrink only face-up steps.
            const maxRight = ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth ?? 0) - ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth ?? 0);
            const maxLeft = 0;
            const available = (0, $0f5b71ede2b50336$export$e79bc7cac697f199)() ? maxRight - baseX : baseX - maxLeft;
            if (available <= 0 || totalOffset <= available) continue;
            const faceUpIndices = [];
            for(let j = 0; j < pile.length - 1; j++)if (!pile[j].isFaceDown) faceUpIndices.push(j);
            if (faceUpIndices.length === 0) continue;
            // Reduce face-up spacing just enough to fit within available width.
            const overflow = totalOffset - available;
            const perStepReduction = Math.ceil(overflow / faceUpIndices.length);
            const adjustedSteps = [
                ...steps
            ];
            for (const idx of faceUpIndices)adjustedSteps[idx] = Math.max(minStep, adjustedSteps[idx] - perStepReduction);
            // Rebuild card X coordinates for this column with adjusted spacing.
            let adjustedOffset = 0;
            for(let j = 0; j < pile.length; j++){
                const card = pile[j];
                const x = baseX + start * adjustedOffset;
                card.x = x;
                card.originalX = x;
                card.y = baseY;
                card.originalY = baseY;
                if (j < adjustedSteps.length) adjustedOffset += adjustedSteps[j];
            }
        }
    }
    // 3. Assign Coordinates to each card in Foundation
    for(let i = 0; i < foundation.length; i++)for (const card of foundation[i]){
        const x = (0, $0f5b71ede2b50336$export$84ce64f2330f553a)(i);
        const y = (0, $0f5b71ede2b50336$export$7cb2c3b01d2fdd74)(i);
        card.x = x;
        card.originalX = x;
        card.y = y;
        card.originalY = y;
    }
    // 4. Assign Coordinates to each card in Waste
    const maxVisible = (0, $299339de955f290d$export$1c0015b827d9aee)(state, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED);
    const firstVisibleIndex = Math.max(0, waste.length - maxVisible);
    for(let i = 0; i < waste.length; i++){
        const card = waste[i];
        if (i >= firstVisibleIndex) {
            const visibleIndex = i - firstVisibleIndex;
            const baseX = (0, $0f5b71ede2b50336$export$5a7a218ad4c46c78)();
            const baseY = (0, $0f5b71ede2b50336$export$9964e256ead13668)();
            // Offset each visible card
            const cardWidth = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth || 80; // fallback if undefined
            const cardOffset = visibleIndex * (cardWidth * 0.3); // Spacing (30%)
            // When drawing 3 in right-handed mode, stack from right to left
            // When drawing 1 or in left-handed mode, stack from left to right
            const isRightHandedDraw3 = !(0, $0f5b71ede2b50336$export$e79bc7cac697f199)() && (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED === 3;
            const offset = isRightHandedDraw3 ? -cardOffset : cardOffset;
            card.x = baseX + offset;
            card.originalX = baseX + offset;
            card.y = baseY;
            card.originalY = baseY;
        } else {
            // Hidden waste cards stay in the same position
            const x = (0, $0f5b71ede2b50336$export$5a7a218ad4c46c78)();
            const y = (0, $0f5b71ede2b50336$export$9964e256ead13668)();
            card.x = x;
            card.originalX = x;
            card.y = y;
            card.originalY = y;
        }
    }
    return {
        stock: stock,
        waste: waste,
        foundation: foundation,
        tableau: tableau,
        draggedCards: draggedCards,
        wasteVisibleCount: state.wasteVisibleCount
    };
}




// ─── Easing Functions ────────────────────────────────────────────────
const $2b7dc820af08b312$var$easings = {
    linear: (t)=>t,
    easeOutQuad: (t)=>t * (2 - t),
    easeOutCubic: (t)=>1 - Math.pow(1 - t, 3),
    easeInOutQuad: (t)=>t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    easeOutBack: (t)=>{
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    }
};
// ─── Unique Card Identifier ─────────────────────────────────────────
/** Unique key for a card: value + type (e.g. "KS", "AH", "10D"). */ function $2b7dc820af08b312$var$cardKey(card) {
    return card.value + card.type;
}
// ─── Animation Manager ──────────────────────────────────────────────
class $2b7dc820af08b312$var$AnimationManagerClass {
    /**
   * Queue an animation for a card.
   *
   * The card's x/y (home position) should already be set to the FINAL position
   * (via assignCardCoordinates). The animation writes interpolated positions
   * to the overlay map; it never mutates card.x/y.
   */ queueAnimation(animation) {
        // If animations are disabled, just fire onComplete immediately
        if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.animationsEnabled) {
            if (animation.onComplete) animation.onComplete();
            return;
        }
        // Set the initial animation overlay so the very first paint sees the
        // start position (not the home position which is the end position).
        (0, $f08e1321bb9509a9$export$2fe829b149c28e00)(animation.card, animation.startX, animation.startY);
        this.activeAnimations.push(animation);
        // Start the loop if not already running.
        if (!this.running) {
            this.running = true;
            // Cancel any pending schedulePaint RAF to prevent it from painting
            // before tick has a chance to interpolate animation positions.
            (0, $71e2b16f726ebff4$export$57b918346606b0b9)();
            this.rafId = requestAnimationFrame(this.tick);
        }
    }
    /**
   * Animate a card from its pre-move position to its current home position.
   * Convenience helper — reads card.x/y as endX/endY (the home position
   * set by assignCardCoordinates).
   */ animateCard(card, fromX, fromY, duration = 250, easing = $2b7dc820af08b312$var$easings.easeOutCubic, onComplete) {
        this.queueAnimation({
            card: card,
            startX: fromX,
            startY: fromY,
            endX: card.x,
            endY: card.y,
            startTime: performance.now(),
            duration: duration,
            easing: easing,
            onComplete: onComplete
        });
    }
    /**
   * Animate multiple cards from saved positions to their current home positions.
   * Each entry is { card, fromX, fromY }.
   */ animateCards(cards, duration = 250, easing = $2b7dc820af08b312$var$easings.easeOutCubic, onAllComplete) {
        let completed = 0;
        const total = cards.length;
        if (total === 0) {
            if (onAllComplete) onAllComplete();
            return;
        }
        for (const entry of cards)this.animateCard(entry.card, entry.fromX, entry.fromY, duration, easing, ()=>{
            completed++;
            if (completed === total && onAllComplete) onAllComplete();
        });
    }
    /**
   * Check if any animations are currently active.
   */ isAnimating() {
        return this.activeAnimations.length > 0;
    }
    /**
   * Cancel all active animations, snapping to final positions.
   * We simply clear the overlays — card.x/y already hold the correct
   * final positions (set by assignCardCoordinates before animation started).
   */ clearAll() {
        for (const anim of this.activeAnimations){
            // Remove overlay so the card renders at its home position (card.x/y)
            (0, $f08e1321bb9509a9$export$af62a74095e6d36a)(anim.card);
            if (anim.onComplete) anim.onComplete();
        }
        this.activeAnimations = [];
        this.stop();
    }
    stop() {
        this.running = false;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        // Ensure all overlays are cleaned up when animation loop stops
        (0, $f08e1321bb9509a9$export$60916053becc6a4)();
    }
    constructor(){
        this.activeAnimations = [];
        this.rafId = null;
        this.running = false;
        /**
   * The core animation loop. Writes interpolated positions to the overlay map.
   * Never mutates card.x or card.y.
   */ this.tick = ()=>{
            if (!this.running) return;
            const now = performance.now();
            const completedAnimations = [];
            for (const anim of this.activeAnimations){
                const elapsed = now - anim.startTime;
                const progress = Math.min(elapsed / anim.duration, 1);
                const easedProgress = anim.easing(progress);
                if (progress >= 1) {
                    // Animation complete — remove overlay so card renders at home position
                    (0, $f08e1321bb9509a9$export$af62a74095e6d36a)(anim.card);
                    completedAnimations.push(anim);
                } else {
                    // Interpolate position into the overlay
                    const x = anim.startX + (anim.endX - anim.startX) * easedProgress;
                    const y = anim.startY + (anim.endY - anim.startY) * easedProgress;
                    (0, $f08e1321bb9509a9$export$2fe829b149c28e00)(anim.card, x, y);
                }
            }
            // Remove completed animations and fire callbacks
            for (const completed of completedAnimations){
                const index = this.activeAnimations.indexOf(completed);
                if (index !== -1) this.activeAnimations.splice(index, 1);
                if (completed.onComplete) completed.onComplete();
            }
            // Repaint
            (0, $71e2b16f726ebff4$export$4e0651d2e7bf59d0)();
            // Continue loop or stop
            if (this.activeAnimations.length > 0) this.rafId = requestAnimationFrame(this.tick);
            else this.stop();
        };
    }
}
const $2b7dc820af08b312$export$9c3b3a49659bb096 = new $2b7dc820af08b312$var$AnimationManagerClass();
// ─── Animated Undo ──────────────────────────────────────────────────
/**
 * Build a position map from a CardsState: maps unique card key → rendered { x, y }.
 * Uses getCardPosition() so it captures animation/drag overlays if active.
 */ function $2b7dc820af08b312$var$buildPositionMap(state) {
    const map = new Map();
    for (const card of state.stock){
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
        map.set($2b7dc820af08b312$var$cardKey(card), {
            x: pos.x,
            y: pos.y
        });
    }
    for (const card of state.waste){
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
        map.set($2b7dc820af08b312$var$cardKey(card), {
            x: pos.x,
            y: pos.y
        });
    }
    for (const pile of state.tableau)for (const card of pile){
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
        map.set($2b7dc820af08b312$var$cardKey(card), {
            x: pos.x,
            y: pos.y
        });
    }
    for (const pile of state.foundation)for (const card of pile){
        const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
        map.set($2b7dc820af08b312$var$cardKey(card), {
            x: pos.x,
            y: pos.y
        });
    }
    return map;
}
function $2b7dc820af08b312$export$161f7bf0207fa92f() {
    if ((0, $13e781a56ac394f5$export$f524c3b49520a9b3).length <= 1) return;
    // Capture rendered positions from the state we're about to pop
    const stateBeforeUndo = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const beforePositions = $2b7dc820af08b312$var$buildPositionMap(stateBeforeUndo);
    // Pop the state
    (0, $13e781a56ac394f5$export$21f930c44940fd98)();
    // Recalculate home positions on the revealed state to ensure they match
    // current screen dimensions.
    const revealedState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const recalculated = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(revealedState);
    (0, $13e781a56ac394f5$export$f524c3b49520a9b3)[(0, $13e781a56ac394f5$export$f524c3b49520a9b3).length - 1] = recalculated;
    (0, $d7e89e4a237bfcb6$export$384cd953322e3851)((0, $13e781a56ac394f5$export$f524c3b49520a9b3));
    // If animations are disabled, just repaint
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.animationsEnabled) {
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return;
    }
    // Find cards that moved — compare old rendered position to new home position
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const allCards = (0, $299339de955f290d$export$52061974e8d1b67c)(currentState);
    const animEntries = [];
    for (const card of allCards){
        const oldPos = beforePositions.get($2b7dc820af08b312$var$cardKey(card));
        if (oldPos && (Math.abs(oldPos.x - card.x) > 1 || Math.abs(oldPos.y - card.y) > 1)) animEntries.push({
            card: card,
            fromX: oldPos.x,
            fromY: oldPos.y
        });
    }
    if (animEntries.length > 0) $2b7dc820af08b312$export$9c3b3a49659bb096.animateCards(animEntries, 200);
    else (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}






/**
 * Centralized win detection and handling.
 *
 * Combines checkForWin() with the side effects that should fire once
 * when a win is detected: stop timer, record stats, save leaderboard
 * entry, play sound, and transition to the Completed state.
 *
 * Previously this block was duplicated 3x across canvasMouseMethods.ts
 * and autocompleteGame.ts.
 */ 







function $46568836d2d04244$export$d64b2f494e2adc0b() {
    if (!(0, $13e781a56ac394f5$export$7601ebd7d9b3975b)()) return false;
    const finalTime = (0, $d639f68dfbcef401$export$b4fb6bfe0bb0bbf1)();
    const undoCount = (0, $13e781a56ac394f5$export$41b9add8aee40b4e)();
    (0, $4115af5d1675323d$export$4ad85e644dc7b0a2)(finalTime);
    (0, $15b077d75e975a2b$export$60e52208036581dd)(finalTime, undoCount);
    (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('win');
    // Game Center: submit score and report achievements (no-op if not on iOS / not authenticated)
    const computedTime = finalTime + undoCount * (0, $b1c4622a6095546e$export$32529dd2809e488c);
    (0, $7b135972b663a872$export$71e03d193ca0545c)(computedTime);
    (0, $7b135972b663a872$export$7adf517cbe323318)((0, $4115af5d1675323d$export$ef8a82cde02bb06f)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount, undoCount, finalTime);
    const currentAppState = (0, $13e781a56ac394f5$export$b72e390a9ebe1648);
    currentAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Completed;
    currentAppState.showWinRecords = false;
    (0, $13e781a56ac394f5$export$8921d147d4d1b7)(currentAppState);
    return true;
}



// ─── Auto-complete snapshot for single-undo ──────────────────────────
/** Length of cardState before auto-complete started — used to truncate on undo. */ let $1a73a0108f314080$var$autoCompleteSnapshotLength = null;
// ─── Cached canAutoComplete result ───────────────────────────────────
let $1a73a0108f314080$var$_autoCompleteCacheDirty = true;
let $1a73a0108f314080$var$_autoCompleteCachedResult = false;
function $1a73a0108f314080$export$7935772db54728e6() {
    $1a73a0108f314080$var$_autoCompleteCacheDirty = true;
}
function $1a73a0108f314080$export$9a5d87b5cc49764d() {
    if (!$1a73a0108f314080$var$_autoCompleteCacheDirty) return $1a73a0108f314080$var$_autoCompleteCachedResult;
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    if (!state) {
        $1a73a0108f314080$var$_autoCompleteCachedResult = false;
        $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
        return false;
    }
    // Stock must be empty
    if (state.stock.length > 0) {
        $1a73a0108f314080$var$_autoCompleteCachedResult = false;
        $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
        return false;
    }
    // Waste must be empty
    if (state.waste.length > 0) {
        $1a73a0108f314080$var$_autoCompleteCachedResult = false;
        $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
        return false;
    }
    // All tableau cards must be face-up
    for (const pile of state.tableau){
        for (const card of pile)if (card.isFaceDown) {
            $1a73a0108f314080$var$_autoCompleteCachedResult = false;
            $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
            return false;
        }
    }
    // There must still be cards to move (not already all in foundations)
    const totalFoundationCards = state.foundation.reduce((sum, pile)=>sum + pile.length, 0);
    if (totalFoundationCards === 52) {
        $1a73a0108f314080$var$_autoCompleteCachedResult = false;
        $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
        return false;
    }
    $1a73a0108f314080$var$_autoCompleteCachedResult = true;
    $1a73a0108f314080$var$_autoCompleteCacheDirty = false;
    return true;
}
/**
 * Find the next card that can be moved to a foundation.
 * Returns { card, sourceType, sourceIndex, cardIndex, foundationIndex } or null.
 */ function $1a73a0108f314080$var$findNextAutoCompleteMove(state) {
    // Look through each tableau pile for a bottom card (top of stack = last in array)
    // that can go to a foundation
    for(let ti = 0; ti < state.tableau.length; ti++){
        const pile = state.tableau[ti];
        if (pile.length === 0) continue;
        const card = pile[pile.length - 1]; // top card of tableau pile
        for(let fi = 0; fi < state.foundation.length; fi++){
            if ((0, $299339de955f290d$export$bdc75d649a90bd24)(card, state.foundation[fi])) return {
                sourceType: 'tableau',
                sourceIndex: ti,
                cardIndex: pile.length - 1,
                foundationIndex: fi
            };
        }
    }
    return null;
}
/**
 * Execute one auto-complete step: move one card to a foundation and animate it.
 * Then recursively call itself for the next card.
 */ function $1a73a0108f314080$var$autoCompleteStep() {
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const move = $1a73a0108f314080$var$findNextAutoCompleteMove(state);
    if (!move) {
        // No more moves — check for win
        if ((0, $46568836d2d04244$export$d64b2f494e2adc0b)()) (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return;
    }
    // Save pre-move position for animation
    const card = state.tableau[move.sourceIndex][move.cardIndex];
    const fromPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
    // Clone state, perform the move, assign coordinates, and commit
    const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
        const movedCard = s.tableau[move.sourceIndex][move.cardIndex];
        s.tableau[move.sourceIndex] = s.tableau[move.sourceIndex].slice(0, move.cardIndex);
        s.foundation[move.foundationIndex].push(movedCard);
    });
    // Find the moved card in the committed state for animation
    const movedCard = committed.foundation[move.foundationIndex][committed.foundation[move.foundationIndex].length - 1];
    // Animate with a callback to trigger the next step
    (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCard(movedCard, fromPos.x, fromPos.y, 200, undefined, ()=>{
        // Small delay between cards for visual clarity
        setTimeout(()=>{
            $1a73a0108f314080$var$autoCompleteStep();
        }, 50);
    });
}
function $1a73a0108f314080$export$1ad46ac0dab7c48e() {
    if (!$1a73a0108f314080$export$9a5d87b5cc49764d()) return;
    // Record how many states exist before auto-complete adds any
    $1a73a0108f314080$var$autoCompleteSnapshotLength = (0, $13e781a56ac394f5$export$f524c3b49520a9b3).length;
    (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('autoFoundation');
    // Start the chain of moves
    $1a73a0108f314080$var$autoCompleteStep();
}
function $1a73a0108f314080$export$642b786406f37723() {
    if ($1a73a0108f314080$var$autoCompleteSnapshotLength === null) return false;
    // Cancel any in-progress animations
    (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
    // Truncate the state history back to the pre-auto-complete length
    (0, $13e781a56ac394f5$export$f524c3b49520a9b3).length = $1a73a0108f314080$var$autoCompleteSnapshotLength;
    // Persist
    (0, $d7e89e4a237bfcb6$export$384cd953322e3851)((0, $13e781a56ac394f5$export$f524c3b49520a9b3));
    // Clear the snapshot
    $1a73a0108f314080$var$autoCompleteSnapshotLength = null;
    // Repaint
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    return true;
}
function $1a73a0108f314080$export$643ffcc639722319() {
    $1a73a0108f314080$var$autoCompleteSnapshotLength = null;
}
function $1a73a0108f314080$export$73473e2cce2a1c96() {
    return $1a73a0108f314080$var$autoCompleteSnapshotLength !== null;
}


/**
 * Deep clone using the native structuredClone API.
 *
 * structuredClone handles plain objects, arrays, Date, Map, Set, ArrayBuffer,
 * and more — everything we need for CardsState and AppState cloning.
 * It's significantly faster than a manual recursive clone with for...in.
 */ function $3f8e1d176bd8e73d$export$b7d58db314e0ac27(obj) {
    return structuredClone(obj);
}







const $6cec517bcba19c3d$var$EXPECTED_CARD_COUNT = 52;
/**
 * When true, all integrity validation is bypassed.
 * E2e tests set this via `window.disableCardIntegrity()` after seeding
 * partial/synthetic states so that subsequent game actions (drag, double-tap,
 * etc.) don't trigger false-positive corruption reverts.
 */ let $6cec517bcba19c3d$var$_integrityDisabled = false;
function $6cec517bcba19c3d$export$9bce854bb9edf084() {
    $6cec517bcba19c3d$var$_integrityDisabled = true;
}
/** Re-enable integrity checking. */ function $6cec517bcba19c3d$var$enableCardIntegrity() {
    $6cec517bcba19c3d$var$_integrityDisabled = false;
}
/**
 * Build a unique key for a card based on its suit type and numeric value.
 * Each (type, numValue) pair is unique in a standard 52-card deck.
 */ function $6cec517bcba19c3d$var$cardKey(card) {
    return `${card.type}${card.numValue}`;
}
function $6cec517bcba19c3d$export$f4623a8d3552c5ba(skipValidation = false) {
    if (skipValidation || $6cec517bcba19c3d$var$_integrityDisabled) return true;
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    if (!state) return true; // No state yet (pre-deal), nothing to validate
    const stateCards = (0, $299339de955f290d$export$52061974e8d1b67c)(state);
    const dragged = (0, $13e781a56ac394f5$export$7f3c49b37340bfce)();
    const allCards = [
        ...stateCards,
        ...dragged
    ];
    const totalCount = allCards.length;
    // Build a map of card keys → count
    const seen = new Map();
    for (const card of allCards){
        const key = $6cec517bcba19c3d$var$cardKey(card);
        seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    // Check for missing and duplicate cards
    const suits = [
        'S',
        'C',
        'D',
        'H'
    ];
    const missing = [];
    const duplicates = [];
    for (const suit of suits)for(let num = 1; num <= 13; num++){
        const key = `${suit}${num}`;
        const count = seen.get(key) ?? 0;
        if (count === 0) missing.push(key);
        else if (count > 1) duplicates.push(`${key} (\xd7${count})`);
    }
    if (totalCount === $6cec517bcba19c3d$var$EXPECTED_CARD_COUNT && missing.length === 0 && duplicates.length === 0) return true;
    // ─── Integrity violation detected ───────────────────────────────
    console.error(`[CardIntegrity] VIOLATION DETECTED \u{2014} expected ${$6cec517bcba19c3d$var$EXPECTED_CARD_COUNT} cards, found ${totalCount}`, {
        stockCount: state.stock.length,
        wasteCount: state.waste.length,
        tableauCounts: state.tableau.map((pile)=>pile.length),
        foundationCounts: state.foundation.map((pile)=>pile.length),
        draggedCount: dragged.length,
        missing: missing.length > 0 ? missing : '(none)',
        duplicates: duplicates.length > 0 ? duplicates : '(none)'
    });
    // Auto-revert to the previous state
    (0, $13e781a56ac394f5$export$21f930c44940fd98)();
    // Re-assign coordinates after undo so the reverted state renders correctly
    const reverted = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    if (reverted) (0, $f593745b1b99abdc$export$245b33143bc6e84d)(reverted);
    return false;
}


// Load settings on startup
const $13e781a56ac394f5$var$initialSettings = (0, $b66bebf5d76faeef$export$54453bb1ffd2fd71)();
// Helper function to safely get theme
function $13e781a56ac394f5$var$getThemeByName(themeName) {
    return (0, $0e92ad87971fbdf7$export$d9a33280f07116d9)[themeName] ?? (0, $0e92ad87971fbdf7$export$d9a33280f07116d9).HighContrastTheme;
}
let $13e781a56ac394f5$export$f524c3b49520a9b3 = [];
let $13e781a56ac394f5$export$b72e390a9ebe1648 = {
    NUM_TABLEAU: 7,
    NUM_STOCK: 1,
    NUM_WASTE: 1,
    NUM_FOUNDATIONS: 4,
    NUM_CARDS_FLIPPED: $13e781a56ac394f5$var$initialSettings.drawCount,
    cardWidth: undefined,
    cardHeight: undefined,
    windowHeight: undefined,
    windowWidth: undefined,
    cardSpacing: undefined,
    cardYOffset: undefined,
    foundationXOffset: undefined,
    // Load theme from settings, fallback to HighContrastTheme if not found
    currentTheme: $13e781a56ac394f5$var$getThemeByName($13e781a56ac394f5$var$initialSettings.themeName),
    currentAppState: (0, $7629cc56581bca5c$export$812e1401122ad355).Playing,
    settings: $13e781a56ac394f5$var$initialSettings,
    isStandalone: false,
    // Set during pageSetup() based on environment detection
    showWinRecords: false,
    showMenuRecords: false
};
function $13e781a56ac394f5$export$c0ce2c2b7e283ee9(width) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.cardWidth = width;
}
function $13e781a56ac394f5$export$b24d7405fdca8614(height) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.cardHeight = height;
}
function $13e781a56ac394f5$export$24db6d4500eb94db(newState, opts) {
    const newCardState = [
        ...$13e781a56ac394f5$export$f524c3b49520a9b3,
        newState
    ];
    $13e781a56ac394f5$export$f524c3b49520a9b3 = newCardState;
    // Safety net: verify all 52 cards are accounted for after each state change.
    // If corruption is detected, undo() reverts to the previous state.
    // Tests that seed partial states pass skipIntegrity to bypass validation entirely.
    (0, $6cec517bcba19c3d$export$f4623a8d3552c5ba)(opts === null || opts === void 0 ? void 0 : opts.skipIntegrity);
    // Save state to localStorage
    (0, $d7e89e4a237bfcb6$export$384cd953322e3851)($13e781a56ac394f5$export$f524c3b49520a9b3);
    (0, $1a73a0108f314080$export$7935772db54728e6)();
}
function $13e781a56ac394f5$export$8921d147d4d1b7(newState) {
    $13e781a56ac394f5$export$b72e390a9ebe1648 = newState;
    // Keep window.appState in sync for debugging/testing
    if (typeof window !== 'undefined') //@ts-ignore
    window.appState = $13e781a56ac394f5$export$b72e390a9ebe1648;
}
function $13e781a56ac394f5$export$45b2d7bf302c2b62() {
    $13e781a56ac394f5$export$f524c3b49520a9b3 = [];
    $13e781a56ac394f5$var$_undoCount = 0;
    (0, $1a73a0108f314080$export$643ffcc639722319)();
    // Clear saved state from localStorage when starting new game
    (0, $d7e89e4a237bfcb6$export$4e3d892664daf908)();
    (0, $1a73a0108f314080$export$7935772db54728e6)();
}
function $13e781a56ac394f5$export$812268ebd28ecdbb() {
    const savedState = (0, $d7e89e4a237bfcb6$export$756e5d66bdd28395)();
    if (savedState && savedState.length > 0) {
        $13e781a56ac394f5$export$f524c3b49520a9b3 = savedState;
        (0, $1a73a0108f314080$export$7935772db54728e6)();
        return true;
    }
    return false;
}
let $13e781a56ac394f5$var$draggedCards = [];
function $13e781a56ac394f5$export$7f3c49b37340bfce() {
    return $13e781a56ac394f5$var$draggedCards;
}
function $13e781a56ac394f5$export$f4a51cc8ad86d45d() {
    if ($13e781a56ac394f5$export$f524c3b49520a9b3.length === 0) {
        (0, $1a73a0108f314080$export$643ffcc639722319)();
        (0, $1a73a0108f314080$export$7935772db54728e6)();
        return;
    }
    $13e781a56ac394f5$export$f524c3b49520a9b3 = [
        $13e781a56ac394f5$export$f524c3b49520a9b3[$13e781a56ac394f5$export$f524c3b49520a9b3.length - 1]
    ];
    $13e781a56ac394f5$var$draggedCards = [];
    (0, $1a73a0108f314080$export$643ffcc639722319)();
    (0, $d7e89e4a237bfcb6$export$384cd953322e3851)($13e781a56ac394f5$export$f524c3b49520a9b3);
    (0, $1a73a0108f314080$export$7935772db54728e6)();
}
// ─── Undo count tracking ────────────────────────────────────────────────────
/** Number of intentional undo button presses in the current game. */ let $13e781a56ac394f5$var$_undoCount = 0;
function $13e781a56ac394f5$export$41b9add8aee40b4e() {
    return $13e781a56ac394f5$var$_undoCount;
}
function $13e781a56ac394f5$export$5c2ef27ba87739ef() {
    $13e781a56ac394f5$var$_undoCount++;
}
function $13e781a56ac394f5$export$d27a924e80184d2a(cards) {
    const coordinatesSaved = cards.map((card)=>{
        if (card.originalX === undefined || card.originalX === null || card.originalY === undefined || card.originalY === null) {
            card.originalX = card.x;
            card.originalY = card.y;
        }
        return card;
    });
    $13e781a56ac394f5$var$draggedCards = coordinatesSaved;
}
function $13e781a56ac394f5$export$21f930c44940fd98() {
    if ($13e781a56ac394f5$export$f524c3b49520a9b3.length > 1) {
        $13e781a56ac394f5$export$f524c3b49520a9b3.pop();
        // Clear any cards being dragged
        $13e781a56ac394f5$var$draggedCards = [];
        // Persist the reverted state to localStorage
        (0, $d7e89e4a237bfcb6$export$384cd953322e3851)($13e781a56ac394f5$export$f524c3b49520a9b3);
        (0, $1a73a0108f314080$export$7935772db54728e6)();
    }
}
/**
 * Silently pops the last state without clearing dragged cards or saving.
 * Used during drag-and-drop to collapse the pickup + drop into a single history entry.
 */ function $13e781a56ac394f5$var$popState() {
    if ($13e781a56ac394f5$export$f524c3b49520a9b3.length > 1) {
        $13e781a56ac394f5$export$f524c3b49520a9b3.pop();
        (0, $1a73a0108f314080$export$7935772db54728e6)();
    }
}
function $13e781a56ac394f5$export$17eacbc67b15eb5c(mutate, opts) {
    // Start the game timer on the very first move
    if (!(0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $d639f68dfbcef401$export$e63e1201ca009ae6)();
    const newState = (0, $3f8e1d176bd8e73d$export$b7d58db314e0ac27)((opts === null || opts === void 0 ? void 0 : opts.source) ?? $13e781a56ac394f5$export$23a3c12b66b5ee22());
    mutate(newState);
    // Auto-flip any exposed face-down cards at the top of tableau columns,
    // unless explicitly skipped (e.g. during pickup — flip waits for drop)
    // or unless the user has disabled auto-flip in settings.
    if (!(opts !== null && opts !== void 0 && opts.skipAutoFlip) && $13e781a56ac394f5$export$b72e390a9ebe1648.settings.autoFlipCards) {
        for (const pile of newState.tableau)if (pile.length > 0 && pile[pile.length - 1].isFaceDown) pile[pile.length - 1].isFaceDown = false;
    }
    const committed = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(newState);
    if (opts !== null && opts !== void 0 && opts.popFirst) $13e781a56ac394f5$var$popState();
    $13e781a56ac394f5$export$24db6d4500eb94db(committed, {
        skipIntegrity: opts === null || opts === void 0 ? void 0 : opts.skipIntegrity
    });
    return committed;
}
function $13e781a56ac394f5$export$c84b80ff4ece2444(height) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.windowHeight = height;
}
function $13e781a56ac394f5$export$316dc132b4fb9db7(width) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.windowWidth = width;
}
function $13e781a56ac394f5$export$d6589dee5c518fd4(spacing) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.cardSpacing = spacing;
}
function $13e781a56ac394f5$export$c8e45da93f45c61a(offset) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.cardYOffset = offset;
}
function $13e781a56ac394f5$export$7e3aaf3eaa60fb9(offset) {
    $13e781a56ac394f5$export$b72e390a9ebe1648.foundationXOffset = offset;
}
function $13e781a56ac394f5$export$7601ebd7d9b3975b() {
    const foundations = $13e781a56ac394f5$export$23a3c12b66b5ee22().foundation;
    for (const foundation of foundations){
        if (foundation.length !== 13) return false;
        // Verify card order: Ace through King with consistent suit
        for(let j = 0; j < foundation.length; j++){
            if (foundation[j].numValue !== j + 1) return false;
            if (foundation[j].type !== foundation[0].type) return false;
        }
    }
    return true;
}
let $13e781a56ac394f5$var$undoImageSource;
function $13e781a56ac394f5$export$e799792a8af58316(src) {
    $13e781a56ac394f5$var$undoImageSource = src;
}
let $13e781a56ac394f5$var$newGameButtonImageSource;
function $13e781a56ac394f5$export$166429202d87929d(src) {
    $13e781a56ac394f5$var$newGameButtonImageSource = src;
}
let $13e781a56ac394f5$var$menuButtonImageSource;
function $13e781a56ac394f5$export$aa5277fabcde0f7c(src) {
    $13e781a56ac394f5$var$menuButtonImageSource = src;
}
let $13e781a56ac394f5$export$58162e8b0e9a665e;
function $13e781a56ac394f5$export$b56adb0cf781e000(src) {
    $13e781a56ac394f5$export$58162e8b0e9a665e = src;
}
function $13e781a56ac394f5$export$23a3c12b66b5ee22() {
    return $13e781a56ac394f5$export$f524c3b49520a9b3[$13e781a56ac394f5$export$f524c3b49520a9b3.length - 1];
}


// ─── Module state ───────────────────────────────────────────────────────────
let $0f19a74577fb54d1$var$_ctx = null;
const $0f19a74577fb54d1$var$_buffers = new Map();
let $0f19a74577fb54d1$var$_unlocked = false;
/**
 * Automated test runs should be silent.
 * - `?test=...` is used by the in-app scenario runner.
 * - `navigator.webdriver` is true under browser automation (Playwright/WebDriver).
 */ function $0f19a74577fb54d1$var$isAutomatedTestMode() {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('test') !== null || navigator.webdriver === true;
}
// ─── Per-sound volume levels ────────────────────────────────────────────────
// Applied at playback time after peak-normalisation to 1.0.
const $0f19a74577fb54d1$var$_volumes = {
    lift: 0.75,
    // very short — needs higher volume to register
    place: 0.35,
    draw: 0.35,
    flip: 0.4,
    // short — slight bump
    autoFoundation: 0.3,
    undo: 0.3,
    // longer duration — slightly lower
    recycle: 0.35,
    shuffle: 0.4,
    // bumped slightly
    autocomplete: 0.35,
    win: 0.35
};
// ─── Helpers ────────────────────────────────────────────────────────────────
/** Create a white-noise AudioBuffer of the given duration. */ function $0f19a74577fb54d1$var$createNoiseBuffer(offCtx, duration) {
    const length = Math.ceil(offCtx.sampleRate * duration);
    const buffer = offCtx.createBuffer(1, length, offCtx.sampleRate);
    const data = buffer.getChannelData(0);
    for(let i = 0; i < length; i++)data[i] = Math.random() * 2 - 1;
    return buffer;
}
/** Scale an AudioBuffer so its peak amplitude is 1.0. */ function $0f19a74577fb54d1$var$normalizeBuffer(audioBuffer) {
    const data = audioBuffer.getChannelData(0);
    let peak = 0;
    for(let i = 0; i < data.length; i++){
        const abs = Math.abs(data[i]);
        if (abs > peak) peak = abs;
    }
    if (peak > 0 && peak !== 1) {
        const scale = 1.0 / peak;
        for(let i = 0; i < data.length; i++)data[i] *= scale;
    }
    return audioBuffer;
}
// ─── Sound synthesis ────────────────────────────────────────────────────────
/** 1. PICK UP — soft click/tap, like fingernail tapping a card */ async function $0f19a74577fb54d1$var$synthesizeLift() {
    const dur = 0.025, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const bp = off.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2800;
    bp.Q.value = 1.5;
    const g = off.createGain();
    g.gain.setValueAtTime(0.35, 0);
    g.gain.exponentialRampToValueAtTime(0.001, 0.015);
    n.connect(bp).connect(g).connect(off.destination);
    n.start(0);
    return off.startRendering();
}
/** 2. PLACE — crisp tap, no bass */ async function $0f19a74577fb54d1$var$synthesizePlace() {
    const dur = 0.07, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const bp = off.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2400;
    bp.Q.value = 0.8;
    const g = off.createGain();
    g.gain.setValueAtTime(0, 0);
    g.gain.linearRampToValueAtTime(0.35, 0.002);
    g.gain.exponentialRampToValueAtTime(0.001, dur);
    n.connect(bp).connect(g).connect(off.destination);
    n.start(0);
    return off.startRendering();
}
/** 3. DRAW FROM STOCK — crisp card snap */ async function $0f19a74577fb54d1$var$synthesizeDraw() {
    const dur = 0.07, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const bp = off.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 3200;
    bp.Q.value = 1.0;
    const g = off.createGain();
    g.gain.setValueAtTime(0, 0);
    g.gain.linearRampToValueAtTime(0.4, 0.002);
    g.gain.exponentialRampToValueAtTime(0.001, dur);
    n.connect(bp).connect(g).connect(off.destination);
    n.start(0);
    return off.startRendering();
}
/** 4. FLIP CARD — quick rising flick, like cardboard snapping over */ async function $0f19a74577fb54d1$var$synthesizeFlip() {
    const dur = 0.04, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const bp = off.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(1800, 0);
    bp.frequency.exponentialRampToValueAtTime(5000, dur * 0.6);
    bp.Q.value = 1.0;
    const g = off.createGain();
    g.gain.setValueAtTime(0, 0);
    g.gain.linearRampToValueAtTime(0.35, 0.002);
    g.gain.exponentialRampToValueAtTime(0.001, dur);
    n.connect(bp).connect(g).connect(off.destination);
    n.start(0);
    return off.startRendering();
}
/** 5. AUTO-MOVE TO FOUNDATION — quick swoosh + soft landing thump */ async function $0f19a74577fb54d1$var$synthesizeAutoFoundation() {
    const dur = 0.12, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    // Swoosh
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const hp = off.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.setValueAtTime(1500, 0);
    hp.frequency.exponentialRampToValueAtTime(4000, 0.05);
    const ng = off.createGain();
    ng.gain.setValueAtTime(0, 0);
    ng.gain.linearRampToValueAtTime(0.2, 0.005);
    ng.gain.exponentialRampToValueAtTime(0.001, 0.07);
    n.connect(hp).connect(ng).connect(off.destination);
    // Landing thump
    const osc = off.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, 0.05);
    osc.frequency.exponentialRampToValueAtTime(50, 0.1);
    const og = off.createGain();
    og.gain.setValueAtTime(0, 0);
    og.gain.setValueAtTime(0.2, 0.05);
    og.gain.exponentialRampToValueAtTime(0.001, 0.12);
    osc.connect(og).connect(off.destination);
    n.start(0);
    osc.start(0);
    return off.startRendering();
}
/** 6. UNDO — place sound in reverse (swell up then cut) */ async function $0f19a74577fb54d1$var$synthesizeUndo() {
    const dur = 0.1, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const n = off.createBufferSource();
    n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const bp = off.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(800, 0);
    bp.frequency.exponentialRampToValueAtTime(2200, dur * 0.8);
    bp.Q.value = 0.6;
    const g = off.createGain();
    g.gain.setValueAtTime(0.001, 0);
    g.gain.exponentialRampToValueAtTime(0.35, dur * 0.75);
    g.gain.linearRampToValueAtTime(0, dur);
    n.connect(bp).connect(g).connect(off.destination);
    n.start(0);
    return off.startRendering();
}
/** 7. RECYCLE WASTE — soft gather, multiple tiny clicks close together */ async function $0f19a74577fb54d1$var$synthesizeRecycle() {
    const dur = 0.2, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    for(let i = 0; i < 5; i++){
        const t = i * 0.035;
        const clickDur = 0.02;
        const n = off.createBufferSource();
        n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, clickDur);
        const bp = off.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 1800 + i * 200;
        bp.Q.value = 0.6;
        const g = off.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.2, t + 0.002);
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur);
        n.connect(bp).connect(g).connect(off.destination);
        n.start(t);
    }
    return off.startRendering();
}
/** 8. SHUFFLE — card riffle */ async function $0f19a74577fb54d1$var$synthesizeShuffle() {
    const dur = 0.45, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const numClicks = 14;
    for(let i = 0; i < numClicks; i++){
        const t = i / numClicks * (dur - 0.04) + Math.random() * 0.008;
        const clickDur = 0.012 + Math.random() * 0.008;
        const n = off.createBufferSource();
        n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, clickDur);
        const bp = off.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 2200 + Math.random() * 1200;
        bp.Q.value = 0.7 + Math.random() * 0.6;
        const progress = i / numClicks;
        const envelope = 0.6 + 0.4 * Math.sin(progress * Math.PI);
        const peakVol = 0.18 * envelope;
        const g = off.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(peakVol, t + 0.001);
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur);
        n.connect(bp).connect(g).connect(off.destination);
        n.start(t);
    }
    return off.startRendering();
}
/** 9. AUTOCOMPLETE — accelerating series of soft taps */ async function $0f19a74577fb54d1$var$synthesizeAutocomplete() {
    const dur = 1.2, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    const numCards = 20;
    for(let i = 0; i < numCards; i++){
        const progress = i / numCards;
        const t = progress * progress * (dur - 0.1);
        const clickDur = 0.025;
        const n = off.createBufferSource();
        n.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, clickDur);
        const bp = off.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 2000 + progress * 1500;
        bp.Q.value = 0.8;
        const g = off.createGain();
        const vol = 0.12 + progress * 0.12;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + 0.002);
        g.gain.exponentialRampToValueAtTime(0.001, t + clickDur);
        n.connect(bp).connect(g).connect(off.destination);
        n.start(t);
    }
    // Final soft thump
    const osc = off.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(100, dur - 0.1);
    osc.frequency.exponentialRampToValueAtTime(40, dur);
    const og = off.createGain();
    og.gain.setValueAtTime(0, dur - 0.1);
    og.gain.linearRampToValueAtTime(0.15, dur - 0.08);
    og.gain.exponentialRampToValueAtTime(0.001, dur);
    osc.connect(og).connect(off.destination);
    osc.start(dur - 0.1);
    return off.startRendering();
}
/** 10. WIN — confetti popper: pop burst + sparkle rain */ async function $0f19a74577fb54d1$var$synthesizeWin() {
    const dur = 1.8, sr = 44100;
    const off = new OfflineAudioContext(1, Math.ceil(sr * dur), sr);
    // Pop — short percussive burst
    const pop = off.createBufferSource();
    pop.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, 0.06);
    const popBP = off.createBiquadFilter();
    popBP.type = 'bandpass';
    popBP.frequency.setValueAtTime(3000, 0);
    popBP.frequency.exponentialRampToValueAtTime(800, 0.04);
    popBP.Q.value = 0.5;
    const popG = off.createGain();
    popG.gain.setValueAtTime(0.5, 0);
    popG.gain.exponentialRampToValueAtTime(0.001, 0.06);
    pop.connect(popBP).connect(popG).connect(off.destination);
    pop.start(0);
    // Thump underneath the pop
    const thump = off.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(150, 0);
    thump.frequency.exponentialRampToValueAtTime(40, 0.08);
    const tg = off.createGain();
    tg.gain.setValueAtTime(0.2, 0);
    tg.gain.exponentialRampToValueAtTime(0.001, 0.08);
    thump.connect(tg).connect(off.destination);
    thump.start(0);
    // Confetti scatter — bright noise that blooms out from the pop
    const scatter = off.createBufferSource();
    scatter.buffer = $0f19a74577fb54d1$var$createNoiseBuffer(off, dur);
    const scatterHP = off.createBiquadFilter();
    scatterHP.type = 'highpass';
    scatterHP.frequency.setValueAtTime(4000, 0);
    scatterHP.frequency.exponentialRampToValueAtTime(8000, 0.5);
    const sg = off.createGain();
    sg.gain.setValueAtTime(0, 0);
    sg.gain.linearRampToValueAtTime(0.07, 0.05);
    sg.gain.setValueAtTime(0.07, 0.2);
    sg.gain.exponentialRampToValueAtTime(0.02, 0.8);
    sg.gain.exponentialRampToValueAtTime(0.001, dur);
    scatter.connect(scatterHP).connect(sg).connect(off.destination);
    scatter.start(0);
    return off.startRendering();
}
// ─── Initialization ─────────────────────────────────────────────────────────
/** Synthesise a sound and store its peak-normalised buffer. */ async function $0f19a74577fb54d1$var$synthesizeAndStore(name, fn) {
    const raw = await fn();
    $0f19a74577fb54d1$var$_buffers.set(name, $0f19a74577fb54d1$var$normalizeBuffer(raw));
}
function $0f19a74577fb54d1$export$19710ec455966cdd() {
    if ($0f19a74577fb54d1$var$isAutomatedTestMode()) return;
    try {
        $0f19a74577fb54d1$var$_ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch  {
        console.warn('Web Audio API not available');
        return;
    }
    // Pre-render all sounds into peak-normalised AudioBuffers
    $0f19a74577fb54d1$var$synthesizeAndStore('lift', $0f19a74577fb54d1$var$synthesizeLift);
    $0f19a74577fb54d1$var$synthesizeAndStore('place', $0f19a74577fb54d1$var$synthesizePlace);
    $0f19a74577fb54d1$var$synthesizeAndStore('draw', $0f19a74577fb54d1$var$synthesizeDraw);
    $0f19a74577fb54d1$var$synthesizeAndStore('flip', $0f19a74577fb54d1$var$synthesizeFlip);
    $0f19a74577fb54d1$var$synthesizeAndStore('autoFoundation', $0f19a74577fb54d1$var$synthesizeAutoFoundation);
    $0f19a74577fb54d1$var$synthesizeAndStore('undo', $0f19a74577fb54d1$var$synthesizeUndo);
    $0f19a74577fb54d1$var$synthesizeAndStore('recycle', $0f19a74577fb54d1$var$synthesizeRecycle);
    $0f19a74577fb54d1$var$synthesizeAndStore('shuffle', $0f19a74577fb54d1$var$synthesizeShuffle);
    $0f19a74577fb54d1$var$synthesizeAndStore('autocomplete', $0f19a74577fb54d1$var$synthesizeAutocomplete);
    $0f19a74577fb54d1$var$synthesizeAndStore('win', $0f19a74577fb54d1$var$synthesizeWin);
}
function $0f19a74577fb54d1$export$3f66b274a2143589() {
    if ($0f19a74577fb54d1$var$isAutomatedTestMode()) return;
    if ($0f19a74577fb54d1$var$_unlocked || !$0f19a74577fb54d1$var$_ctx) return;
    if ($0f19a74577fb54d1$var$_ctx.state === 'suspended') $0f19a74577fb54d1$var$_ctx.resume().then(()=>{
        $0f19a74577fb54d1$var$_unlocked = true;
    }).catch(()=>{
    // Will retry on next gesture
    });
    else $0f19a74577fb54d1$var$_unlocked = true;
}
function $0f19a74577fb54d1$export$80cb197f7ea4fe13(name) {
    if ($0f19a74577fb54d1$var$isAutomatedTestMode()) return;
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.soundEnabled) return;
    if (!$0f19a74577fb54d1$var$_ctx || $0f19a74577fb54d1$var$_ctx.state !== 'running') return;
    const buffer = $0f19a74577fb54d1$var$_buffers.get(name);
    if (!buffer) return;
    const source = $0f19a74577fb54d1$var$_ctx.createBufferSource();
    source.buffer = buffer;
    const gain = $0f19a74577fb54d1$var$_ctx.createGain();
    gain.gain.value = $0f19a74577fb54d1$var$_volumes[name];
    source.connect(gain).connect($0f19a74577fb54d1$var$_ctx.destination);
    source.start(0);
}





function $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, rx, ry, width, height) {
    return x > rx && x < rx + width && y > ry && y < ry + height;
}
/** Check whether point (x,y) falls on a card, using its rendered position. */ function $9cb90e4fa561e701$var$isPointOnCard(x, y, card) {
    const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, pos.x, pos.y, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight);
}
function $9cb90e4fa561e701$export$c500b3705dcc9e42(x, y) {
    // console.log({ x, y })
    // 1. Loop through all cards in the tableau
    const tCard = $9cb90e4fa561e701$var$findCardInTableau(x, y);
    if (tCard) return tCard;
    // 2. Loop through all cards in the hand,
    const wCard = $9cb90e4fa561e701$var$findCardInWaste(x, y);
    if (wCard) return wCard;
    const sCard = $9cb90e4fa561e701$var$findCardInStock(x, y);
    if (sCard) return sCard;
    // 3. Loop through all the cards on foundations
    const fCard = $9cb90e4fa561e701$var$findCardInFoundation(x, y);
    if (fCard) return fCard;
}
function $9cb90e4fa561e701$var$findCardInTableau(x, y) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    // Iterate backward through cards because cards are drawn in array order
    // (index 0 first, then 1, etc.), so the highest index is visually on top.
    // For overlapping cards, we want to return the topmost one.
    for(let i = currentState.tableau.length - 1; i >= 0; i--)for(let j = currentState.tableau[i].length - 1; j >= 0; j--){
        const card = currentState.tableau[i][j];
        if (card && $9cb90e4fa561e701$var$isPointOnCard(x, y, card)) return currentState.tableau[i][j];
    }
}
function $9cb90e4fa561e701$var$findCardInWaste(x, y) {
    const waste = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().waste;
    // Only allow interaction with the topmost card in waste
    if (waste.length === 0) return undefined;
    const topCardIndex = waste.length - 1;
    const card = waste[topCardIndex];
    if (card && $9cb90e4fa561e701$var$isPointOnCard(x, y, card)) return card;
}
function $9cb90e4fa561e701$var$findCardInFoundation(x, y) {
    for(let i = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation.length - 1; i >= 0; i--)for(let j = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation[i].length - 1; j >= 0; j--){
        const card = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation[i][j];
        if (card && $9cb90e4fa561e701$var$isPointOnCard(x, y, card)) return (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation[i][j];
    }
}
function $9cb90e4fa561e701$var$findCardInStock(x, y) {
    for(let i = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().stock.length - 1; i >= 0; i--){
        const card = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().stock[i];
        if (card && $9cb90e4fa561e701$var$isPointOnCard(x, y, card)) return (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().stock[i];
    }
}
function $9cb90e4fa561e701$export$e8403e1072ca4f4a(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$889074c399efa9cf)(), (0, $0f5b71ede2b50336$export$72029e8b50497416)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight);
}
function $9cb90e4fa561e701$export$4ab4da0db1842586(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$26fe8f1cbe2626e5)(), (0, $0f5b71ede2b50336$export$70c3227cdbebd1b8)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
}
function $9cb90e4fa561e701$export$6f5b1a8ba8c9cfbb(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$833ed0fe1212d409)(), (0, $0f5b71ede2b50336$export$f81ed66b3906beb3)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
}
function $9cb90e4fa561e701$export$98b91c36d85ad84d(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$1ecdc1be7bd1a749)(), (0, $0f5b71ede2b50336$export$d50ecd2fde22a762)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
}
function $9cb90e4fa561e701$export$9401726a7f13fe32(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$1f614aa49af839fa)(), (0, $0f5b71ede2b50336$export$6d939bb5033370b0)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
}
function $9cb90e4fa561e701$export$29d698a051b417(x, y) {
    return $9cb90e4fa561e701$export$3ed9b644ed65412a(x, y, (0, $0f5b71ede2b50336$export$51646a58b06b5693)(), (0, $0f5b71ede2b50336$export$ff0ff2fb1fcb2a95)(), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth);
}






/**
 * Compute the intersection area between two axis-aligned rectangles.
 * Returns 0 if they do not overlap.
 */ function $6fa52cac1c396f76$var$rectOverlapArea(ax, ay, aw, ah, bx, by, bw, bh) {
    const overlapX = Math.max(0, Math.min(ax + aw, bx + bw) - Math.max(ax, bx));
    const overlapY = Math.max(0, Math.min(ay + ah, by + bh) - Math.max(ay, by));
    return overlapX * overlapY;
}
function $6fa52cac1c396f76$export$7e38a4255f8186e3(incCard) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for(let i = currentState.tableau.length - 1; i >= 0; i--){
        if (currentState.tableau[i].length > 0) for(let j = currentState.tableau[i].length - 1; j >= 0; j--){
            const curCard = currentState.tableau[i][j];
            if (curCard && incCard && incCard.type === curCard.type && incCard.value === curCard.value) return [
                i,
                j
            ];
        }
    }
}
function $6fa52cac1c396f76$export$731eb6b057408330(incCard) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for(let i = currentState.foundation.length - 1; i >= 0; i--)if (currentState.foundation[i].length > 0) {
        for(let j = currentState.foundation[i].length - 1; j >= 0; j--)if (currentState.foundation[i][j]) {
            const curCard = currentState.foundation[i][j];
            if (curCard && incCard && incCard.type === curCard.type && incCard.value === curCard.value) return [
                i,
                j
            ];
        }
    }
}
function $6fa52cac1c396f76$export$eb0f5d8f2fddacc1(incCard) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    for(let i = currentState.waste.length - 1; i >= 0; i--){
        const curCard = currentState.waste[i];
        if (curCard && incCard && incCard.type === curCard.type && incCard.value === curCard.value) return i;
    }
}
function $6fa52cac1c396f76$export$a545b174d3590fa5(x, y) {
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_FOUNDATIONS; i++){
        if ((0, $9cb90e4fa561e701$export$3ed9b644ed65412a)(x, y, (0, $0f5b71ede2b50336$export$84ce64f2330f553a)(i), (0, $0f5b71ede2b50336$export$7cb2c3b01d2fdd74)(i), (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight)) return i;
    }
}
function $6fa52cac1c396f76$export$42985f28b70fda0f(x, y) {
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const isLeftHanded = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness === 'left';
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU; i++){
        const baseX = (0, $0f5b71ede2b50336$export$f8ddd168b43d671a)(i);
        const baseY = (0, $0f5b71ede2b50336$export$38cc5b0bc1d504c6)(i);
        if ((0, $0f5b71ede2b50336$export$72f5d9a44bd9a7f0)()) {
            const pile = currentState.tableau[i];
            let extentY = 0;
            if (pile.length > 0) for(let j = 0; j < pile.length - 1; j++)extentY += (0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(pile[j].isFaceDown);
            const minX = baseX;
            const maxX = baseX + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
            const minY = baseY;
            const maxY = baseY + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight + extentY;
            if (x >= minX && x <= maxX && y >= minY && y <= maxY) return i;
        } else {
            // Calculate the horizontal extent of this tableau column
            const pile = currentState.tableau[i];
            const extent = pile.length > 0 ? (0, $0f5b71ede2b50336$export$b20e22ecc0dfefa9)(pile) : 0;
            const farEdgeX = isLeftHanded ? baseX + extent : baseX - extent;
            // Determine the X bounds for this column
            const minX = isLeftHanded ? baseX : farEdgeX;
            const maxX = isLeftHanded ? farEdgeX + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth : baseX + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
            // Check if click is within the column bounds
            if (x >= minX && x <= maxX && y >= baseY && y <= baseY + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight) return i;
        }
    }
}
function $6fa52cac1c396f76$export$5f971a7f5e36a53a(draggedCards) {
    if (draggedCards.length === 0) return undefined;
    // Use the first (bottom) dragged card's rendered position as the bounding box
    const dragPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(draggedCards[0]);
    const dragX = dragPos.x;
    const dragY = dragPos.y;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    let bestIndex;
    let bestArea = 0;
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_FOUNDATIONS; i++){
        const fx = (0, $0f5b71ede2b50336$export$84ce64f2330f553a)(i);
        const fy = (0, $0f5b71ede2b50336$export$7cb2c3b01d2fdd74)(i);
        const area = $6fa52cac1c396f76$var$rectOverlapArea(dragX, dragY, cw, ch, fx, fy, cw, ch);
        if (area > bestArea) {
            bestArea = area;
            bestIndex = i;
        }
    }
    return bestIndex;
}
function $6fa52cac1c396f76$export$311462252effb6d(draggedCards) {
    if (draggedCards.length === 0) return undefined;
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const isLeft = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness === 'left';
    // Use the first (bottom) dragged card's rendered position
    const dragPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(draggedCards[0]);
    const dragX = dragPos.x;
    const dragY = dragPos.y;
    const cw = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth;
    const ch = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight;
    let bestIndex;
    let bestArea = 0;
    for(let i = 0; i < (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_TABLEAU; i++){
        const baseX = (0, $0f5b71ede2b50336$export$f8ddd168b43d671a)(i);
        const baseY = (0, $0f5b71ede2b50336$export$38cc5b0bc1d504c6)(i);
        if ((0, $0f5b71ede2b50336$export$72f5d9a44bd9a7f0)()) {
            const pile = currentState.tableau[i];
            let extentY = 0;
            if (pile.length > 0) for(let j = 0; j < pile.length - 1; j++)extentY += (0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(pile[j].isFaceDown);
            const colMinX = baseX;
            const colMinY = baseY;
            const colWidth = cw;
            const colHeight = ch + extentY;
            const area = $6fa52cac1c396f76$var$rectOverlapArea(dragX, dragY, cw, ch, colMinX, colMinY, colWidth, colHeight);
            if (area > bestArea) {
                bestArea = area;
                bestIndex = i;
            }
        } else {
            const pile = currentState.tableau[i];
            const extent = pile.length > 0 ? (0, $0f5b71ede2b50336$export$b20e22ecc0dfefa9)(pile) : 0;
            const farEdgeX = isLeft ? baseX + extent : baseX - extent;
            // Column bounding box
            const colMinX = isLeft ? baseX : farEdgeX;
            const colMaxX = isLeft ? farEdgeX + cw : baseX + cw;
            const colWidth = colMaxX - colMinX;
            const area = $6fa52cac1c396f76$var$rectOverlapArea(dragX, dragY, cw, ch, colMinX, baseY, colWidth, ch);
            if (area > bestArea) {
                bestArea = area;
                bestIndex = i;
            }
        }
    }
    return bestIndex;
}









const $abdcf456668d06cd$export$6ef67543309e3234 = [
    {
        type: 'S',
        value: 'K',
        numValue: 13,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: 'K',
        numValue: 13,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: 'K',
        numValue: 13,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: 'K',
        numValue: 13,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: 'Q',
        numValue: 12,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: 'Q',
        numValue: 12,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: 'Q',
        numValue: 12,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: 'Q',
        numValue: 12,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: 'J',
        numValue: 11,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: 'J',
        numValue: 11,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: 'J',
        numValue: 11,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: 'J',
        numValue: 11,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '10',
        numValue: 10,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '10',
        numValue: 10,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '10',
        numValue: 10,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '10',
        numValue: 10,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '9',
        numValue: 9,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '9',
        numValue: 9,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '9',
        numValue: 9,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '9',
        numValue: 9,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '8',
        numValue: 8,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '8',
        numValue: 8,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '8',
        numValue: 8,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '8',
        numValue: 8,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '7',
        numValue: 7,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '7',
        numValue: 7,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '7',
        numValue: 7,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '7',
        numValue: 7,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '6',
        numValue: 6,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '6',
        numValue: 6,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '6',
        numValue: 6,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '6',
        numValue: 6,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '5',
        numValue: 5,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '5',
        numValue: 5,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '5',
        numValue: 5,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '5',
        numValue: 5,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '4',
        numValue: 4,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '4',
        numValue: 4,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '4',
        numValue: 4,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '4',
        numValue: 4,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '3',
        numValue: 3,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '3',
        numValue: 3,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '3',
        numValue: 3,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '3',
        numValue: 3,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: '2',
        numValue: 2,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: '2',
        numValue: 2,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: '2',
        numValue: 2,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: '2',
        numValue: 2,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'S',
        value: 'A',
        numValue: 1,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'C',
        value: 'A',
        numValue: 1,
        color: 'black',
        isFaceDown: true
    },
    {
        type: 'D',
        value: 'A',
        numValue: 1,
        color: 'red',
        isFaceDown: true
    },
    {
        type: 'H',
        value: 'A',
        numValue: 1,
        color: 'red',
        isFaceDown: true
    }
];







/**
 * Heuristic filter for Klondike Solitaire deals.
 *
 * Klondike winnability is NP-hard — no fast algorithm exists to determine it
 * exactly. This module applies cheap structural checks to reject shuffles that
 * are *likely* unwinnable and favour deals with better opening prospects.
 *
 * The heuristics penalise:
 *   1. Aces buried deep in the tableau (face-down under many cards)
 *   2. Kings sitting directly on aces in the same column (trapping the ace)
 *   3. Same-suit order inversions in a single tableau column
 *      (e.g. 3♠ face-down beneath 2♠ — creates a deadlock)
 *   4. All four aces being completely inaccessible at the start
 *      (none face-up in tableau, none in the first stock cycle)
 *
 * A cumulative penalty score is compared against a threshold; deals that
 * exceed it are rejected and the caller should re-shuffle.
 */ /** Minimal card shape needed by heuristics — avoids requiring x/y coordinates. */ /**
 * Simulate the deal layout from a shuffled deck without mutating anything.
 *
 * Klondike deal: 28 cards go to tableau (7 columns: 1,2,3,4,5,6,7 cards),
 * remaining 24 go to stock. Within each column the last card is face-up.
 *
 * Returns tableau columns (bottom→top order) and the stock (top-of-stock first).
 */ function $8fa3866757eb7b37$var$simulateDeal(deck) {
    const tableau = [
        [],
        [],
        [],
        [],
        [],
        [],
        []
    ];
    let idx = 0;
    for(let col = 0; col < 7; col++)for(let row = 0; row <= col; row++)tableau[col].push(deck[idx++]);
    // Remaining cards form the stock (reversed in the real deal, but order
    // within the stock doesn't affect our column-based heuristics — we only
    // care about *which* cards are in the stock vs. tableau).
    const stock = deck.slice(idx);
    return {
        tableau: tableau,
        stock: stock
    };
}
// ─── Individual penalty checks ──────────────────────────────────────────────
/** Penalty for each ace based on how deeply it's buried (face-down) in tableau. */ function $8fa3866757eb7b37$var$aceDepthPenalty(tableau) {
    let penalty = 0;
    for (const col of tableau){
        const faceDownCount = col.length - 1; // all but top card are face-down
        for(let i = 0; i < faceDownCount; i++)if (col[i].numValue === 1) {
            // Deeper position → higher penalty. Position 0 in a 7-card column
            // means 6 cards on top of it — worst case.
            const depth = faceDownCount - i; // cards sitting on top of this ace
            penalty += depth * 3;
        }
    }
    return penalty;
}
/**
 * Penalty when a king sits directly on top of an ace in the same column.
 * This traps the ace until the king can be moved, which requires an empty column.
 */ function $8fa3866757eb7b37$var$kingOnAcePenalty(tableau) {
    let penalty = 0;
    for (const col of tableau){
        for(let i = 0; i < col.length - 1; i++)if (col[i].numValue === 1 && col[i + 1].numValue === 13) penalty += 8;
    }
    return penalty;
}
/**
 * Penalty for same-suit order inversions within a single tableau column.
 *
 * If a lower card of the same suit is buried beneath a higher card of that
 * suit in the same column, the lower card can never reach its foundation
 * before the higher card is moved — and the higher card needs the lower
 * card to be on the foundation first. Classic deadlock.
 *
 * Example: 3♠ at position 0, 2♠ at position 2 in the same column — the 2♠
 * needs to go to the foundation before the 3♠, but the 3♠ is blocking it.
 */ function $8fa3866757eb7b37$var$suitInversionPenalty(tableau) {
    let penalty = 0;
    for (const col of tableau){
        const faceDownCount = col.length - 1;
        // Only check face-down cards (indices 0 .. faceDownCount-1)
        for(let i = 0; i < faceDownCount; i++)for(let j = i + 1; j < faceDownCount; j++){
            const lower = col[i];
            const upper = col[j];
            // Same suit, and the deeper card has a HIGHER numValue than
            // the card above it — means the foundation needs the upper
            // card first, but it's buried deeper.
            if (lower.type === upper.type && lower.numValue > upper.numValue) penalty += 4;
        }
    }
    return penalty;
}
/**
 * Large penalty if no ace is immediately accessible at the start of the game.
 *
 * "Accessible" means either:
 *   - Face-up as the top card of a tableau column, OR
 *   - In the stock (reachable by cycling through it)
 *
 * If all 4 aces are face-down in the tableau, the game starts with zero
 * productive moves toward the foundations — a strong signal of a bad deal.
 */ function $8fa3866757eb7b37$var$noAccessibleAcePenalty(tableau, stock) {
    // Check face-up tableau tops
    for (const col of tableau){
        if (col.length > 0 && col[col.length - 1].numValue === 1) return 0; // At least one ace is face-up
    }
    // Check stock
    for (const card of stock){
        if (card.numValue === 1) return 0; // At least one ace is in the stock
    }
    // All aces are face-down in the tableau — very bad
    return 15;
}
// ─── Main entry point ───────────────────────────────────────────────────────
/** Rejection threshold — deals scoring above this are filtered out. */ const $8fa3866757eb7b37$var$REJECTION_THRESHOLD = 25;
function $8fa3866757eb7b37$export$23a759adff358a82(deck) {
    const { tableau: tableau, stock: stock } = $8fa3866757eb7b37$var$simulateDeal(deck);
    const score = $8fa3866757eb7b37$var$aceDepthPenalty(tableau) + $8fa3866757eb7b37$var$kingOnAcePenalty(tableau) + $8fa3866757eb7b37$var$suitInversionPenalty(tableau) + $8fa3866757eb7b37$var$noAccessibleAcePenalty(tableau, stock);
    return score <= $8fa3866757eb7b37$var$REJECTION_THRESHOLD;
}


/**
 * Mulberry32 — a fast 32-bit seeded PRNG.
 * Returns a function that produces deterministic floats in [0, 1) from a numeric seed.
 */ function $856e591c5d33ab08$var$mulberry32(seed) {
    let s = seed | 0;
    return ()=>{
        s = s + 0x6d2b79f5 | 0;
        let t = Math.imul(s ^ s >>> 15, 1 | s);
        t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
        return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
}
/**
 * Converts a string seed to a numeric hash (djb2).
 */ function $856e591c5d33ab08$var$hashSeed(str) {
    let hash = 5381;
    for(let i = 0; i < str.length; i++)hash = (hash << 5) + hash + str.charCodeAt(i) | 0;
    return hash >>> 0;
}
function $856e591c5d33ab08$export$a17f8b0554f18154(input) {
    if (typeof input === 'number') return Math.abs(Math.floor(input)) || 1;
    const n = Number(input);
    if (!isNaN(n) && isFinite(n)) return Math.abs(Math.floor(n)) || 1;
    return $856e591c5d33ab08$var$hashSeed(input);
}
function $856e591c5d33ab08$export$85698d8a2a9e7bc(deck) {
    const tableau = [
        [],
        [],
        [],
        [],
        [],
        [],
        []
    ];
    const stock = [
        ...deck
    ].reverse();
    // Move hand cards to tableau. Set the last card in each tableau to be face up
    for(let i = 0; i < 7; i++){
        if (!tableau[i]) tableau[i] = [];
        const activeTableau = tableau[i];
        for(let j = 0; j <= i; j++){
            activeTableau.push(stock.pop());
            if (j === i) activeTableau[activeTableau.length - 1].isFaceDown = false;
        }
    }
    // We now know the rest of the cards in hand will remain face up cards
    for(let k = 0; k < stock.length; k++)stock[k].isFaceDown = true;
    const deckWithCoordinates = (0, $f593745b1b99abdc$export$245b33143bc6e84d)({
        stock: stock,
        tableau: tableau
    });
    // hand contains all waste and stock
    (0, $13e781a56ac394f5$export$24db6d4500eb94db)(deckWithCoordinates);
}
function $856e591c5d33ab08$export$448332262467e042(seed, favorWinnable = true) {
    (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('shuffle');
    const MAX_ATTEMPTS = 50; // hard cap — never spin for more than this
    const rng = seed != null ? $856e591c5d33ab08$var$mulberry32(seed) : Math.random;
    for(let attempt = 0; attempt < MAX_ATTEMPTS; attempt++){
        const deck = (0, $abdcf456668d06cd$export$6ef67543309e3234).map((card)=>({
                ...card,
                isFaceDown: true
            }));
        for(let i = deck.length - 1; i > 0; i--){
            const randomIdx = Math.floor(rng() * (i + 1));
            const temp = deck[i];
            deck[i] = deck[randomIdx];
            deck[randomIdx] = temp;
        }
        // If heuristic filtering is off, or the deal passes, return immediately
        if (!favorWinnable || (0, $8fa3866757eb7b37$export$23a759adff358a82)(deck)) return deck;
    // Otherwise loop and try a fresh shuffle (seeded RNG advances state
    // automatically so even deterministic seeds won't repeat)
    }
    // Exhausted attempts — return the last shuffle rather than blocking forever
    const deck = (0, $abdcf456668d06cd$export$6ef67543309e3234).map((card)=>({
            ...card,
            isFaceDown: true
        }));
    for(let i = deck.length - 1; i > 0; i--){
        const randomIdx = Math.floor(rng() * (i + 1));
        const temp = deck[i];
        deck[i] = deck[randomIdx];
        deck[randomIdx] = temp;
    }
    return deck;
}
function $856e591c5d33ab08$export$44efdc0af4678c13() {
    // Setup Undo Button
    const undoImage = new Image();
    undoImage.src = `./assets/buttons/${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.buttonNames}/undo.svg`;
    // Setup New Game Button
    const newGameButton = new Image();
    newGameButton.src = `./assets/buttons/${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.buttonNames}/plus.svg`;
    // Setup Menu Button
    const menuButton = new Image();
    menuButton.src = `./assets/buttons/${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.buttonNames}/menu.svg`;
    (0, $13e781a56ac394f5$export$e799792a8af58316)(undoImage);
    (0, $13e781a56ac394f5$export$aa5277fabcde0f7c)(menuButton);
    (0, $13e781a56ac394f5$export$166429202d87929d)(newGameButton);
}
function $856e591c5d33ab08$export$ec4cc2ebfa55c9bd() {
    return new Promise((resolve)=>{
        const cardBackImage = new Image();
        cardBackImage.src = `./assets/cardbacks/${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.cardBackName}.svg`;
        cardBackImage.onload = ()=>{
            (0, $13e781a56ac394f5$export$b56adb0cf781e000)(cardBackImage);
            resolve();
        };
        cardBackImage.onerror = ()=>{
            console.error(`Failed to load card back image: ${cardBackImage.src}`);
            // Still set it even if it fails (will fall back to solid color)
            (0, $13e781a56ac394f5$export$b56adb0cf781e000)(cardBackImage);
            resolve();
        };
    });
}
function $856e591c5d33ab08$export$309ef6b7bd9750ee(seed) {
    // Stop any running win animation
    (0, $03f1cb60f93a62c7$export$cf37c3ca56de35b0)();
    // If the timer was running (player made at least one move), record a loss
    // (unless they already won — in which case the timer is already stopped)
    if ((0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $4115af5d1675323d$export$da739c209ac971a2)();
    (0, $d639f68dfbcef401$export$81b8579df16e1cc0)();
    // Reset app state to Playing (may be Completed or Menu)
    (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
    (0, $13e781a56ac394f5$export$45b2d7bf302c2b62)();
    // Seeded deals stay unfiltered so a seed always reproduces the same hand;
    // random deals always use the winnable-deal filter.
    const shuffledDeck = $856e591c5d33ab08$export$448332262467e042(seed, seed == null);
    $856e591c5d33ab08$export$85698d8a2a9e7bc(shuffledDeck);
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}







/**
 * Menu action handlers — shared logic for canvas menu interactions.
 *
 * Used by canvasMouseMethods.ts (when user taps a menu hit area)
 * and by testRunner.ts (when test actions interact with menu items).
 */ 










function $b72613e90817bb5b$export$4c22ac6b5cab8d25(x, y) {
    const hitAreas = (0, $41c329ef74cc3b7d$export$2b9dad546685a10e)();
    for (const area of hitAreas){
        if ((0, $9cb90e4fa561e701$export$3ed9b644ed65412a)(x, y, area.x, area.y, area.width, area.height)) return area;
    }
    return undefined;
}
function $b72613e90817bb5b$export$51adfcee1e25bd69(hitAreaId) {
    // ── Theme selection ─────────────────────────────────────────────────
    if (hitAreaId.startsWith('theme-')) {
        const themeName = hitAreaId.slice(6);
        $b72613e90817bb5b$var$setTheme(themeName);
        return true;
    }
    // ── Draw count ──────────────────────────────────────────────────────
    // Changing draw count starts a new game to avoid inconsistent stock/waste state
    if (hitAreaId === 'draw-1') {
        if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount !== 1) {
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED = 1;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount = 1;
            (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords = false;
            (0, $41c329ef74cc3b7d$export$e8dbc87925066f13)();
            (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)();
        }
        return true;
    }
    if (hitAreaId === 'draw-3') {
        if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount !== 3) {
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED = 3;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount = 3;
            (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords = false;
            (0, $41c329ef74cc3b7d$export$e8dbc87925066f13)();
            (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)();
        }
        return true;
    }
    // ── Auto-flip toggle ──────────────────────────────────────────────
    if (hitAreaId === 'flip-auto') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.autoFlipCards = true;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'flip-tap') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.autoFlipCards = false;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Handedness ──────────────────────────────────────────────────────
    if (hitAreaId === 'hand-left') {
        $b72613e90817bb5b$var$applySettingWithRelayout(()=>{
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness = 'left';
        });
        return true;
    }
    if (hitAreaId === 'hand-right') {
        $b72613e90817bb5b$var$applySettingWithRelayout(()=>{
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness = 'right';
        });
        return true;
    }
    // ── Card back design ────────────────────────────────────────────────
    if (hitAreaId === 'back-pattern') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.cardBackDesign = 'pattern';
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'back-solid') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.cardBackDesign = 'solid';
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Haptics ─────────────────────────────────────────────────────────
    if (hitAreaId === 'haptic-on') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.hapticsEnabled = true;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'haptic-off') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.hapticsEnabled = false;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Sound ───────────────────────────────────────────────────────────
    if (hitAreaId === 'sound-on') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.soundEnabled = true;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'sound-off') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.soundEnabled = false;
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── New Game ─────────────────────────────────────────────────────────
    if (hitAreaId === 'new-game') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords = false;
        (0, $41c329ef74cc3b7d$export$e8dbc87925066f13)();
        (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Fullscreen ──────────────────────────────────────────────────────
    if (hitAreaId === 'fullscreen') {
        $b72613e90817bb5b$var$toggleFullscreen();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Records toggle (menu leaderboard overlay) ───────────────────────
    if (hitAreaId === 'menu-records-toggle') {
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords = !(0, $13e781a56ac394f5$export$b72e390a9ebe1648).showMenuRecords;
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Menu sub-screen navigation ──────────────────────────────────────
    if (hitAreaId === 'menu-themes') {
        (0, $41c329ef74cc3b7d$export$fb5e9769ad3cff77)((0, $41c329ef74cc3b7d$export$14dc87dc5a0cc6ba).Themes);
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'menu-gamecenter') {
        (0, $7b135972b663a872$export$62e0ffbff3af272d)();
        return true;
    }
    if (hitAreaId === 'menu-tipjar') {
        (0, $41c329ef74cc3b7d$export$fb5e9769ad3cff77)((0, $41c329ef74cc3b7d$export$14dc87dc5a0cc6ba).TipJar);
        (0, $baf55d86e4270add$export$d8d61ef40d973c27)();
        (0, $baf55d86e4270add$export$cdd97182bd92cfbe)();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    if (hitAreaId === 'menu-back') {
        (0, $baf55d86e4270add$export$d8d61ef40d973c27)();
        (0, $41c329ef74cc3b7d$export$e8dbc87925066f13)();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        return true;
    }
    // ── Tip jar purchases ───────────────────────────────────────────────
    if (hitAreaId.startsWith('tip-')) {
        const productId = hitAreaId.slice(4);
        (0, $baf55d86e4270add$export$4a246440fe9ec9da)(productId);
        return true;
    }
    return false;
}
// ─── Internal helpers ───────────────────────────────────────────────────────
/** Apply a settings change, save, reassign card coordinates, and repaint. */ function $b72613e90817bb5b$var$applySettingWithRelayout(apply) {
    apply();
    (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
    const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const updatedState = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(currentState);
    (0, $13e781a56ac394f5$export$24db6d4500eb94db)(updatedState);
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}
/**
 * Set the active theme — reloads card images asynchronously.
 * Extracted from index.ts setTheme().
 */ function $b72613e90817bb5b$var$setTheme(themeName) {
    const themeEntry = Object.entries((0, $0e92ad87971fbdf7$export$d9a33280f07116d9)).find(([name])=>name === themeName);
    if (!themeEntry) {
        console.warn(`Theme ${themeName} not found`);
        return;
    }
    const theme = themeEntry[1];
    // Update app state with new theme
    (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme = theme;
    (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.themeName = themeName;
    // Update CSS variables immediately
    document.documentElement.style.setProperty('--currentBackgroundColor', theme.backgroundColor);
    document.documentElement.style.setProperty('--currentHighlightColor', theme.highlightColor);
    // Update theme-color meta tag for iOS status bar and browser UI
    const themeColorMeta = document.getElementById('theme-color-meta');
    if (themeColorMeta) themeColorMeta.content = theme.backgroundColor;
    // Reload button images for the new theme
    (0, $856e591c5d33ab08$export$44efdc0af4678c13)();
    // Save settings
    (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
    // Repaint immediately with the new theme (background, swatches, toggles)
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    (async ()=>{
        await (0, $856e591c5d33ab08$export$ec4cc2ebfa55c9bd)();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    })();
    // Update iOS app icon to match theme (native bridge, no-op in browser)
    $b72613e90817bb5b$var$setNativeAppIcon(themeName);
    // Update browser tab favicon to match theme
    $b72613e90817bb5b$export$968c68bd88b425a3(themeName);
}
/** Toggle browser fullscreen mode. */ function $b72613e90817bb5b$var$toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen().catch(()=>{});
    else document.documentElement.requestFullscreen().catch(()=>{});
}
/**
 * Tell iOS to switch the app icon to match the active theme.
 * Uses WKWebView message handler registered in ViewController.swift.
 * No-op in browsers / Android — the handler simply won't exist.
 */ function $b72613e90817bb5b$var$setNativeAppIcon(themeName) {
    // Never trigger iOS icon-change prompts during automated scenario runs
    // (test runner / screenshot capture). Those system alerts can block input
    // and contaminate screenshots.
    const nativeScenario = window.__nativeTestScenario;
    const urlParams = new URLSearchParams(window.location.search);
    const isAutomatedRun = typeof nativeScenario === 'string' && nativeScenario.trim().length > 0 || urlParams.get('test') != null || urlParams.get('screenshot') === '1';
    if (isAutomatedRun) return;
    try {
        var _wk$messageHandlers;
        const wk = window.webkit;
        if (wk !== null && wk !== void 0 && (_wk$messageHandlers = wk.messageHandlers) !== null && _wk$messageHandlers !== void 0 && _wk$messageHandlers.setAppIcon) wk.messageHandlers.setAppIcon.postMessage(themeName);
    } catch  {
    // Silently ignore — not running in WKWebView
    }
}
function $b72613e90817bb5b$export$968c68bd88b425a3(themeName) {
    const favicon = document.getElementById('favicon');
    if (favicon) favicon.href = `./res/favicons/${themeName}.svg`;
}




/**
 * Haptic feedback module — wraps cordova-plugin-taptic-engine for iOS.
 *
 * In Cordova context: uses the native Taptic Engine for crisp haptic feedback.
 * In browser context: falls back to navigator.vibrate when available.
 * Unsupported browsers (including iOS Safari) silently no-op.
 *
 * Usage:
 *   import { hapticLift, hapticDrop } from './haptics'
 *   hapticLift()   // light tap when picking up a card
 *   hapticDrop()   // light tap when placing a card
 */ 
// ─── Internal helper ────────────────────────────────────────────────────────
function $a4be2b3d1203b6f4$var$isAutomatedTestMode() {
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('test') !== null || navigator.webdriver === true;
}
function $a4be2b3d1203b6f4$var$triggerVibrate(pattern) {
    if (typeof navigator === 'undefined') return;
    if (typeof navigator.vibrate !== 'function') return;
    try {
        navigator.vibrate(pattern);
    } catch  {
    // Best-effort fallback only.
    }
}
function $a4be2b3d1203b6f4$var$isAndroidBrowser() {
    if (typeof navigator === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
}
function $a4be2b3d1203b6f4$var$triggerImpact(style) {
    if (!(0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.hapticsEnabled) return;
    if ($a4be2b3d1203b6f4$var$isAutomatedTestMode()) return;
    if (typeof window === 'undefined') return;
    const engine = window.TapticEngine;
    if (engine) {
        engine.impact({
            style: style
        });
        return;
    }
    // Browser fallback (best effort).
    // Android web typically needs slightly longer pulses to feel comparable.
    const durations = $a4be2b3d1203b6f4$var$isAndroidBrowser() ? {
        light: 12,
        medium: 18,
        heavy: 28
    } : {
        light: 8,
        medium: 12,
        heavy: 18
    };
    if (style === 'light') $a4be2b3d1203b6f4$var$triggerVibrate(durations.light);
    else if (style === 'medium') $a4be2b3d1203b6f4$var$triggerVibrate(durations.medium);
    else $a4be2b3d1203b6f4$var$triggerVibrate(durations.heavy);
}
function $a4be2b3d1203b6f4$export$59f2a6599d3cc2cb() {
    $a4be2b3d1203b6f4$var$triggerImpact('medium');
}
function $a4be2b3d1203b6f4$export$3ee073860f28a2ad() {
    $a4be2b3d1203b6f4$var$triggerImpact('medium');
}




let $9c0551e961641722$var$startY;
let $9c0551e961641722$var$startX;
let $9c0551e961641722$var$myLatestTap;
let $9c0551e961641722$var$lastTapX = 0;
let $9c0551e961641722$var$lastTapY = 0;
let $9c0551e961641722$var$mouseDownTime = 0;
let $9c0551e961641722$var$lastDoubleTapTime = 0; // Track when we last handled a double-tap
let $9c0551e961641722$var$_isProcessingDoubleTap = false; // Reentrant lock to prevent double-fire
function $9c0551e961641722$export$d52ce18bcf83fe76() {
    $9c0551e961641722$var$myLatestTap = 0;
    $9c0551e961641722$var$lastTapX = -9999;
    $9c0551e961641722$var$lastTapY = -9999;
    $9c0551e961641722$var$lastDoubleTapTime = 0;
}
/** Convert a MouseEvent into a minimal mock TouchEvent for the unified touch handlers. */ function $9c0551e961641722$var$createMockTouchEvent(e, type) {
    return {
        changedTouches: [
            {
                clientX: e.clientX,
                clientY: e.clientY,
                pageX: e.pageX,
                pageY: e.pageY,
                screenX: e.screenX,
                screenY: e.screenY
            }
        ],
        preventDefault: ()=>e.preventDefault(),
        type: type,
        touches: [
            {
                clientX: e.clientX,
                clientY: e.clientY
            }
        ]
    };
}
function $9c0551e961641722$export$7fb80c6ef4180b1d(e) {
    e.preventDefault();
    $9c0551e961641722$var$mouseDownTime = new Date().getTime();
    // @ts-ignore - Converting mouse event to touch event format
    $9c0551e961641722$export$78c9c5244ea20a82($9c0551e961641722$var$createMockTouchEvent(e, 'touchstart'));
}
function $9c0551e961641722$export$2c97df70bc0ca128(e) {
    e.preventDefault();
    // @ts-ignore - Converting mouse event to touch event format
    $9c0551e961641722$export$d6664ee70a10ed8($9c0551e961641722$var$createMockTouchEvent(e, 'touchmove'));
}
function $9c0551e961641722$export$c1df85069b48cecd(e) {
    e.preventDefault();
    // @ts-ignore - Converting mouse event to touch event format
    $9c0551e961641722$export$4f10243649651f2b($9c0551e961641722$var$createMockTouchEvent(e, 'touchend'));
}
function $9c0551e961641722$export$130c6f09e6943579(e) {
    e.preventDefault();
    // Skip if we already handled this double-tap via the touchstart timing detection
    const timeSinceLastDoubleTap = new Date().getTime() - $9c0551e961641722$var$lastDoubleTapTime;
    if (timeSinceLastDoubleTap < 200) return;
    // @ts-ignore - Converting mouse event to touch event format
    $9c0551e961641722$export$6a99faad2978514d($9c0551e961641722$var$createMockTouchEvent(e, 'touchend'));
}
function $9c0551e961641722$export$78c9c5244ea20a82(e) {
    // Unlock audio on first user gesture (required by iOS WebKit autoplay policy)
    (0, $0f19a74577fb54d1$export$3f66b274a2143589)();
    // Cancel any in-flight animations so the user can interact immediately
    if ((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()) (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
    const touch = e.changedTouches[0];
    // If menu is open, handle menu interactions via canvas hit areas
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Menu) {
        // Let the menu button toggle through (handled below)
        const selectedMenuBtn = (0, $9cb90e4fa561e701$export$9401726a7f13fe32)(touch.clientX, touch.clientY);
        if (!selectedMenuBtn) {
            e.preventDefault();
            // Check if we hit a menu control (including "New Game" text button)
            const hitArea = (0, $b72613e90817bb5b$export$4c22ac6b5cab8d25)(touch.clientX, touch.clientY);
            if (hitArea) {
                // Show press highlight immediately, then execute action after a short delay
                (0, $41c329ef74cc3b7d$export$ea7c355b59511c15)(hitArea.id);
                (0, $71e2b16f726ebff4$export$adff1037b5320216)();
                setTimeout(()=>{
                    (0, $41c329ef74cc3b7d$export$6733d6bdb345d597)();
                    (0, $b72613e90817bb5b$export$51adfcee1e25bd69)(hitArea.id);
                    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
                }, 80);
            }
            // Taps outside menu controls are ignored — menu only closes via X button
            return;
        }
    }
    e.preventDefault();
    const currentTap = new Date().getTime();
    const timeSince = currentTap - $9c0551e961641722$var$myLatestTap;
    const distX = Math.abs(touch.clientX - $9c0551e961641722$var$lastTapX);
    const distY = Math.abs(touch.clientY - $9c0551e961641722$var$lastTapY);
    const isNearLastTap = distX < 50 && distY < 50; // Within 50 pixels of last tap
    // Only treat as double-tap if there's a face-up card at this position
    // AND we're not on the stock pile (stock should always process individual taps).
    // Face-down cards are excluded: they should always go through normal tap handling
    // so they get flipped, even if the user taps quickly after a previous double-tap.
    const potentialCard = (0, $9cb90e4fa561e701$export$c500b3705dcc9e42)(touch.clientX, touch.clientY);
    const isOnStock = (0, $9cb90e4fa561e701$export$e8403e1072ca4f4a)(touch.clientX, touch.clientY);
    if (timeSince < 600 && timeSince > 0 && isNearLastTap && potentialCard !== undefined && !potentialCard.isFaceDown && !isOnStock) {
        // double tap - handle it and return to prevent pickup
        $9c0551e961641722$export$6a99faad2978514d(e);
        $9c0551e961641722$var$myLatestTap = new Date().getTime();
        $9c0551e961641722$var$lastDoubleTapTime = new Date().getTime(); // Mark that we just handled a double-tap
        return;
    }
    $9c0551e961641722$var$myLatestTap = new Date().getTime();
    $9c0551e961641722$var$lastTapX = touch.clientX;
    $9c0551e961641722$var$lastTapY = touch.clientY;
    $9c0551e961641722$var$startX = touch.clientX;
    $9c0551e961641722$var$startY = touch.clientY;
    const selectedCard = (0, $9cb90e4fa561e701$export$c500b3705dcc9e42)(touch.clientX, touch.clientY);
    // console.log({selectedCard})
    const selectedStock = (0, $9cb90e4fa561e701$export$e8403e1072ca4f4a)(touch.clientX, touch.clientY);
    // console.log({ selectedStock })
    const selectedUndo = (0, $9cb90e4fa561e701$export$4ab4da0db1842586)(touch.clientX, touch.clientY);
    // console.log({ selectedUndo })
    const selectedNewGameButton = (0, $9cb90e4fa561e701$export$6f5b1a8ba8c9cfbb)(touch.clientX, touch.clientY);
    const selectedRecordsToggleButton = (0, $9cb90e4fa561e701$export$98b91c36d85ad84d)(touch.clientX, touch.clientY);
    const selectedMenuButton = (0, $9cb90e4fa561e701$export$9401726a7f13fe32)(touch.clientX, touch.clientY);
    // console.log({ selectedMenuButton })
    const selectedAutoComplete = (0, $1a73a0108f314080$export$9a5d87b5cc49764d)() && (0, $9cb90e4fa561e701$export$29d698a051b417)(touch.clientX, touch.clientY);
    // If menu is open and user clicks on canvas (but not on menu button), close the menu
    // NOTE: This is now handled by the hit-area logic at the top of canvasTouchStart.
    // Keeping the menu button toggle handling below.
    // ── Buttons take priority over cards ──
    // When a long tableau cascade overlaps a button, the button wins.
    // This prevents accidentally picking up a card when the user taps
    // on a visually-on-top button.
    if (selectedMenuButton) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-menu');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            const newAppState = (0, $3f8e1d176bd8e73d$export$b7d58db314e0ac27)((0, $13e781a56ac394f5$export$b72e390a9ebe1648));
            if (newAppState.currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing) {
                newAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Menu;
                if ((0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $d639f68dfbcef401$export$8b6a3625bd310ba)();
                (0, $13e781a56ac394f5$export$8921d147d4d1b7)(newAppState);
            } else if (newAppState.currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Menu) {
                newAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
                newAppState.showMenuRecords = false;
                (0, $41c329ef74cc3b7d$export$e8dbc87925066f13)();
                if ((0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $d639f68dfbcef401$export$1cd74d15b99f2af2)();
                (0, $13e781a56ac394f5$export$8921d147d4d1b7)(newAppState);
            }
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Completed && selectedNewGameButton) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-newgame');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            const currentAppState = (0, $13e781a56ac394f5$export$b72e390a9ebe1648);
            currentAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Playing;
            currentAppState.showWinRecords = false;
            (0, $13e781a56ac394f5$export$8921d147d4d1b7)(currentAppState);
            (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)();
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Completed && selectedRecordsToggleButton) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-records');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords = !(0, $13e781a56ac394f5$export$b72e390a9ebe1648).showWinRecords;
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && selectedNewGameButton) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-newgame');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)();
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && selectedUndo) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-undo');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            (0, $13e781a56ac394f5$export$5c2ef27ba87739ef)();
            if ((0, $1a73a0108f314080$export$73473e2cce2a1c96)()) (0, $1a73a0108f314080$export$642b786406f37723)();
            else (0, $2b7dc820af08b312$export$161f7bf0207fa92f)();
            (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('undo');
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && selectedAutoComplete) {
        (0, $80cf1bdd1366d994$export$c892ad6354b65f11)('btn-autocomplete');
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        setTimeout(()=>{
            (0, $80cf1bdd1366d994$export$250a0b6d55b74a2f)();
            (0, $1a73a0108f314080$export$1ad46ac0dab7c48e)();
            (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        }, 80);
        return;
    }
    // ── Card & stock interactions (only if no button was hit) ──
    // 1. If the card is selected, make it the current dragging card
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && selectedCard !== undefined) {
        const tableauIndex = (0, $6fa52cac1c396f76$export$7e38a4255f8186e3)(selectedCard);
        const foundIndex = (0, $6fa52cac1c396f76$export$731eb6b057408330)(selectedCard);
        const wasteIndex = (0, $6fa52cac1c396f76$export$eb0f5d8f2fddacc1)(selectedCard);
        if (tableauIndex !== undefined) {
            const [ti, tj] = tableauIndex;
            if (!selectedCard.isFaceDown) {
                (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('lift');
                (0, $a4be2b3d1203b6f4$export$59f2a6599d3cc2cb)();
                try {
                    let picked;
                    (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                        picked = s.tableau[ti].slice(tj);
                        s.tableau[ti] = s.tableau[ti].slice(0, tj);
                    }, {
                        skipAutoFlip: true,
                        skipIntegrity: true
                    });
                    (0, $13e781a56ac394f5$export$d27a924e80184d2a)(picked);
                    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
                } catch (error) {
                    console.error('Error during deep clone or card state update:', error);
                    return;
                }
            } else if (selectedCard.isFaceDown && tj === (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().tableau[ti].length - 1) {
                (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('flip');
                (0, $a4be2b3d1203b6f4$export$59f2a6599d3cc2cb)();
                try {
                    (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                        s.tableau[ti][tj].isFaceDown = false;
                    });
                    // Reset tap tracking so the flip doesn't count as the "first tap"
                    // of a double-tap sequence. Without this, tapping the now-face-up
                    // card within 600ms would trigger an unwanted double-tap.
                    $9c0551e961641722$var$myLatestTap = 0;
                    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
                } catch (error) {
                    console.error('Error flipping face down card:', error);
                    return;
                }
            }
        } else if (foundIndex !== undefined) {
            const [fi, fj] = foundIndex;
            // Only allow picking up the top card from a foundation pile
            if (!selectedCard.isFaceDown && fj === (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation[fi].length - 1) {
                (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('lift');
                (0, $a4be2b3d1203b6f4$export$59f2a6599d3cc2cb)();
                let picked;
                (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    picked = [
                        s.foundation[fi][fj]
                    ];
                    s.foundation[fi] = s.foundation[fi].slice(0, fj);
                }, {
                    skipIntegrity: true
                });
                (0, $13e781a56ac394f5$export$d27a924e80184d2a)(picked);
                (0, $71e2b16f726ebff4$export$adff1037b5320216)();
            }
        } else if (wasteIndex !== undefined) {
            if (!selectedCard.isFaceDown) {
                (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('lift');
                (0, $a4be2b3d1203b6f4$export$59f2a6599d3cc2cb)();
                let picked;
                (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    picked = [
                        s.waste[wasteIndex]
                    ];
                    s.waste = s.waste.slice(0, wasteIndex);
                    (0, $299339de955f290d$export$101d5269b8ad7ea4)(s, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED);
                }, {
                    skipIntegrity: true
                });
                (0, $13e781a56ac394f5$export$d27a924e80184d2a)(picked);
                (0, $71e2b16f726ebff4$export$adff1037b5320216)();
            }
        }
    }
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && selectedStock) {
        // Don't allow drawing from stock while holding cards
        if ((0, $13e781a56ac394f5$export$7f3c49b37340bfce)().length > 0) return;
        // if we tapped on stock.... we can't grab one of those cards.. we flip it.
        const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        if (currentState.stock.length > 0) {
            // something in stock, pop and flip
            // Draw up to NUM_CARDS_FLIPPED cards, or however many remain
            const numCardsToDraw = Math.min((0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED, currentState.stock.length);
            // Save pre-move positions for animation (stock position)
            const stockFromPositions = currentState.stock.slice(-numCardsToDraw).map((c)=>{
                const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(c);
                return {
                    fromX: pos.x,
                    fromY: pos.y
                };
            });
            const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                const moveToWaste = s.stock.splice(-numCardsToDraw);
                moveToWaste.forEach((card)=>card.isFaceDown = false);
                s.waste.push(...moveToWaste);
                s.wasteVisibleCount = numCardsToDraw;
            });
            // Animate drawn cards from stock position to waste position
            const wasteLen = committed.waste.length;
            const animEntries = Array.from({
                length: numCardsToDraw
            }, (_, idx)=>({
                    card: committed.waste[wasteLen - numCardsToDraw + idx],
                    fromX: stockFromPositions[idx].fromX,
                    fromY: stockFromPositions[idx].fromY
                }));
            (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCards(animEntries, 200);
            (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('draw');
            (0, $a4be2b3d1203b6f4$export$3ee073860f28a2ad)();
        } else {
            // nothing in stock, reset the hand — move waste back to stock
            // Save pre-move positions of waste cards for animation
            const wasteFromPositions = currentState.waste.map((c)=>{
                const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(c);
                return {
                    fromX: pos.x,
                    fromY: pos.y
                };
            });
            const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                const moveToStock = s.waste.map((card)=>{
                    card.isFaceDown = true;
                    return card;
                });
                s.waste = [];
                s.stock = moveToStock.reverse();
            });
            // Animate the top few cards sliding from waste position to stock position
            // Only animate the last few visible cards (not all 24) to keep it snappy
            const numToAnimate = Math.min(3, committed.stock.length);
            const stockLen = committed.stock.length;
            const animEntries = [];
            for(let idx = 0; idx < numToAnimate; idx++){
                // The waste cards were reversed, so the last waste card is now first in stock
                // wasteFromPositions are in original waste order; reversed stock maps back
                const wasteIdx = wasteFromPositions.length - 1 - idx;
                animEntries.push({
                    card: committed.stock[stockLen - 1 - idx],
                    fromX: wasteFromPositions[wasteIdx].fromX,
                    fromY: wasteFromPositions[wasteIdx].fromY
                });
            }
            (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCards(animEntries, 200);
            (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('recycle');
            (0, $a4be2b3d1203b6f4$export$3ee073860f28a2ad)();
        }
    }
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}
function $9c0551e961641722$export$d6664ee70a10ed8(e) {
    // Cancel any in-flight animations when dragging
    if ((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()) (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
    e.preventDefault();
    const touch = e.changedTouches[0];
    // document.getElementById('mouseCoordinates').innerText = `X: ${touch.clientX}
    // Y: ${touch.clientY}`
    const touchX = touch.clientX;
    const touchY = touch.clientY;
    const distanceX = touchX - $9c0551e961641722$var$startX;
    const distanceY = touchY - $9c0551e961641722$var$startY;
    if ((0, $13e781a56ac394f5$export$7f3c49b37340bfce)().length > 0) {
        const movedCards = (0, $13e781a56ac394f5$export$7f3c49b37340bfce)().map((card)=>{
            const newX = card.originalX + distanceX;
            const newY = card.originalY + distanceY;
            // Write to the drag overlay instead of mutating card.x/y
            (0, $f08e1321bb9509a9$export$7b7576d93270f13a)(card, Math.floor(newX), Math.floor(newY));
            return card;
        });
        (0, $13e781a56ac394f5$export$d27a924e80184d2a)(movedCards);
    }
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}
function $9c0551e961641722$export$4f10243649651f2b(e) {
    // Cancel any in-flight animations so the drop processes immediately
    if ((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()) (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
    const touch = e.changedTouches[0];
    // If menu is open, ignore touch end on the canvas (menu handles its own taps in touchStart)
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Menu) return;
    e.preventDefault();
    $9c0551e961641722$var$startX = undefined;
    $9c0551e961641722$var$startY = undefined;
    const draggedCards = (0, $13e781a56ac394f5$export$7f3c49b37340bfce)();
    if (draggedCards && draggedCards.length > 0) {
        (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('place');
        // console.log(draggedCards)
        // figure out what we're dragged over.
        // We only care about being over a foundation or a tableau.
        // Use bounding-box overlap detection (card visual position vs target),
        // with fallback to touch-point detection for edge cases.
        const isOverFoundation = (0, $6fa52cac1c396f76$export$5f971a7f5e36a53a)(draggedCards) ?? (0, $6fa52cac1c396f76$export$a545b174d3590fa5)(touch.clientX, touch.clientY);
        // 1. are we over a foundation?
        // 2. can we place our card on the top card?
        const isOverTableau = (0, $6fa52cac1c396f76$export$311462252effb6d)(draggedCards) ?? (0, $6fa52cac1c396f76$export$42985f28b70fda0f)(touch.clientX, touch.clientY);
        // console.log({ isOverTableau })
        if (isOverFoundation !== undefined) {
            const bottomCardOnDraggedCards = draggedCards[0];
            const currentFoundation = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().foundation[isOverFoundation];
            // Foundation only accepts single cards, not stacks
            if (draggedCards.length === 1 && (0, $299339de955f290d$export$bdc75d649a90bd24)(bottomCardOnDraggedCards, currentFoundation)) {
                // Save pre-drop position for animation
                const dropPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(draggedCards[0]);
                const fromX = dropPos.x;
                const fromY = dropPos.y;
                const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    s.foundation[isOverFoundation].push(draggedCards[0]);
                }, {
                    popFirst: true,
                    skipIntegrity: true
                });
                // Clear dragged cards BEFORE animation starts — otherwise paint()
                // inside tick() draws the card twice (once from state, once from draggedCards)
                (0, $13e781a56ac394f5$export$d27a924e80184d2a)([]);
                // Animate card from drop position to final foundation position
                const fPile = committed.foundation[isOverFoundation];
                const movedCard = fPile[fPile.length - 1];
                (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCard(movedCard, fromX, fromY, 200);
                (0, $a4be2b3d1203b6f4$export$3ee073860f28a2ad)();
            } else (0, $2b7dc820af08b312$export$161f7bf0207fa92f)();
        } else if (isOverTableau !== undefined) {
            // Save pre-drop positions for animation
            const draggedFromPositions = draggedCards.map((c)=>{
                const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(c);
                return {
                    fromX: pos.x,
                    fromY: pos.y
                };
            });
            const bottomCardOnDraggedCards = draggedCards[0];
            const currentTableau = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)().tableau[isOverTableau];
            const canPlace = (0, $299339de955f290d$export$a0464b001ab0a988)(bottomCardOnDraggedCards, currentTableau);
            if (canPlace) {
                const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    s.tableau[isOverTableau] = [
                        ...s.tableau[isOverTableau],
                        ...draggedCards
                    ];
                }, {
                    popFirst: true,
                    skipIntegrity: true
                });
                // Animate dragged cards from drop position to final tableau position
                const tableauPile = committed.tableau[isOverTableau];
                const startIdx = tableauPile.length - draggedCards.length;
                const animEntries = draggedCards.map((_, idx)=>({
                        card: tableauPile[startIdx + idx],
                        fromX: draggedFromPositions[idx].fromX,
                        fromY: draggedFromPositions[idx].fromY
                    }));
                // Clear dragged cards BEFORE animation starts — otherwise paint()
                // inside tick() draws the card twice (once from state, once from draggedCards)
                (0, $13e781a56ac394f5$export$d27a924e80184d2a)([]);
                (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCards(animEntries, 200);
                (0, $a4be2b3d1203b6f4$export$3ee073860f28a2ad)();
            } else (0, $2b7dc820af08b312$export$161f7bf0207fa92f)();
        } else // Pop the intermediate pickup state to revert the card to its original position
        (0, $2b7dc820af08b312$export$161f7bf0207fa92f)();
    }
    // setDraggedCards([]) is now called before each animation path above,
    // and undo() inside animatedUndo() also clears draggedCards.
    // This final call is a safety net for any edge case paths.
    (0, $13e781a56ac394f5$export$d27a924e80184d2a)([]);
    (0, $f08e1321bb9509a9$export$ee060b92c30dc31a)();
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing) (0, $46568836d2d04244$export$d64b2f494e2adc0b)();
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}
function $9c0551e961641722$export$6a99faad2978514d(e) {
    // Reentrant lock — prevents double-fire from timing-based detection + native dblclick
    if ($9c0551e961641722$var$_isProcessingDoubleTap) return;
    $9c0551e961641722$var$_isProcessingDoubleTap = true;
    try {
        // Cancel any in-flight animations so the double-tap processes immediately
        if ((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()) (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
        e.preventDefault();
        const touch = e.changedTouches[0];
        const selectedCard = (0, $9cb90e4fa561e701$export$c500b3705dcc9e42)(touch.clientX, touch.clientY);
        // The gate in canvasTouchStart guarantees: selectedCard is defined, face-up, and not stock.
        if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState !== (0, $7629cc56581bca5c$export$812e1401122ad355).Playing || selectedCard === undefined) return;
        const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        const tableauIndex = (0, $6fa52cac1c396f76$export$7e38a4255f8186e3)(selectedCard);
        const wasteIndex = (0, $6fa52cac1c396f76$export$eb0f5d8f2fddacc1)(selectedCard);
        // Determine if this is a valid top card (tableau top or waste top)
        let isTopCard = false;
        if (tableauIndex !== undefined) {
            const [ti, tj] = tableauIndex;
            isTopCard = tj === currentState.tableau[ti].length - 1;
        } else if (wasteIndex !== undefined) isTopCard = true; // waste hit-detection already only returns the top card
        if (!isTopCard) return;
        // Try to place on any foundation
        const fromPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(selectedCard);
        for(let i = 0; i < currentState.foundation.length; i++)if ((0, $299339de955f290d$export$bdc75d649a90bd24)(selectedCard, currentState.foundation[i])) {
            if (tableauIndex !== undefined) {
                const [ti, tj] = tableauIndex;
                const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    const card = s.tableau[ti][tj];
                    s.foundation[i].push(card);
                    s.tableau[ti] = s.tableau[ti].slice(0, tj);
                });
                const movedCard = committed.foundation[i][committed.foundation[i].length - 1];
                (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCard(movedCard, fromPos.x, fromPos.y);
            } else {
                const committed = (0, $13e781a56ac394f5$export$17eacbc67b15eb5c)((s)=>{
                    const card = s.waste.pop();
                    s.foundation[i].push(card);
                    (0, $299339de955f290d$export$101d5269b8ad7ea4)(s, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED);
                });
                const movedCard = committed.foundation[i][committed.foundation[i].length - 1];
                (0, $2b7dc820af08b312$export$9c3b3a49659bb096).animateCard(movedCard, fromPos.x, fromPos.y);
            }
            break;
        }
        // Check for win after double-tap moves a card to foundation
        if (!(0, $46568836d2d04244$export$d64b2f494e2adc0b)()) // Only play the lighter sound when it's not a win
        (0, $0f19a74577fb54d1$export$80cb197f7ea4fe13)('autoFoundation');
        (0, $a4be2b3d1203b6f4$export$3ee073860f28a2ad)();
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    } finally{
        $9c0551e961641722$var$_isProcessingDoubleTap = false;
    }
}
function $9c0551e961641722$export$7b185ad29836087() {
    // Cancel any in-flight animations
    if ((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()) (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
    // If cards were being dragged, undo the pickup
    if ((0, $13e781a56ac394f5$export$7f3c49b37340bfce)().length > 0) (0, $2b7dc820af08b312$export$161f7bf0207fa92f)();
    (0, $13e781a56ac394f5$export$d27a924e80184d2a)([]);
    (0, $f08e1321bb9509a9$export$ee060b92c30dc31a)();
    $9c0551e961641722$var$startX = undefined;
    $9c0551e961641722$var$startY = undefined;
    // Reset double-tap tracking to prevent stale state
    $9c0551e961641722$var$myLatestTap = 0;
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
}










/**
 * Pre-computed test scenarios from the solver.
 * Generated by: npx tsx src/testSolver.ts
 *
 * Each scenario contains a seed, draw count, and an array of TestActions
 * that the TestRunner replays to exercise every interaction type and verify
 * the game reaches a winning state.
 */ /**
 * Draw-1, Seed 1: 207 actions (200 game moves + undo test + auto-complete + win check)
 * Foundation moves alternate between doubleTap and drag-to-foundation to exercise both code paths.
 */ const $7f9b59b6f74cfc3d$var$draw1Scenario = {
    name: 'Draw-1 Seed 1',
    seed: 1,
    drawCount: 1,
    actions: [
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock should have 24 cards after deal'
        },
        {
            type: 'assert',
            condition: 'state.waste.length === 0',
            description: 'Waste should be empty after deal'
        },
        {
            type: 'drag',
            cardId: '8H',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '6H',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: 'JH',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: '5D',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '7S',
            target: 'tableau6'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'assert',
            condition: 'true',
            description: 'Undo reversed last move'
        },
        {
            type: 'drag',
            cardId: '7S',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '2S',
            target: 'tableau2'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '4S',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '3H',
            target: 'tableau3'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: 'AH'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'AS',
            target: 'foundation0'
        },
        {
            type: 'doubleTap',
            cardId: '2S'
        },
        {
            type: 'drag',
            cardId: '3S',
            target: 'foundation0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: '2H'
        },
        {
            type: 'drag',
            cardId: '3H',
            target: 'foundation1'
        },
        {
            type: 'doubleTap',
            cardId: '4S'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '4H',
            target: 'foundation1'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: '5S'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '6S',
            target: 'foundation0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '7C',
            target: 'tableau4'
        },
        {
            type: 'drag',
            cardId: '6H',
            target: 'tableau4'
        },
        {
            type: 'doubleTap',
            cardId: '7S'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '8S',
            target: 'foundation0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'KD',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: 'QS',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '9S',
            target: 'tableau5'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '10C',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '9D',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '8C',
            target: 'tableau1'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '7D',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '6C',
            target: 'tableau1'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'JC',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '10D',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: 'JS',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '5C',
            target: 'tableau4'
        },
        {
            type: 'doubleTap',
            cardId: 'AC'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '10H',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '9C',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '8D',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '5H',
            target: 'foundation1'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'QC',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: 'JD',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: '4C',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: 'KC',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: 'QH',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: '5C',
            target: 'tableau6'
        },
        {
            type: 'doubleTap',
            cardId: '6H'
        },
        {
            type: 'drag',
            cardId: '6D',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: '4D',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: '2C',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: '7H'
        },
        {
            type: 'drag',
            cardId: '8H',
            target: 'foundation1'
        },
        {
            type: 'doubleTap',
            cardId: '9S'
        },
        {
            type: 'drag',
            cardId: '10S',
            target: 'foundation0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: '9H'
        },
        {
            type: 'drag',
            cardId: '9C',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '10H',
            target: 'foundation1'
        },
        {
            type: 'doubleTap',
            cardId: 'JS'
        },
        {
            type: 'drag',
            cardId: '10C',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: 'JH',
            target: 'foundation1'
        },
        {
            type: 'doubleTap',
            cardId: 'QH'
        },
        {
            type: 'drag',
            cardId: 'QS',
            target: 'foundation0'
        },
        {
            type: 'drag',
            cardId: 'QD',
            target: 'tableau0'
        },
        {
            type: 'doubleTap',
            cardId: '3C'
        },
        {
            type: 'drag',
            cardId: 'AD',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '4C'
        },
        {
            type: 'drag',
            cardId: 'QC',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: 'KH',
            target: 'foundation1'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: '2D'
        },
        {
            type: 'drag',
            cardId: '3D',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '4D'
        },
        {
            type: 'drag',
            cardId: '5C',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: '5D'
        },
        {
            type: 'drag',
            cardId: '6D',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '6C'
        },
        {
            type: 'drag',
            cardId: '7C',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: '7D'
        },
        {
            type: 'drag',
            cardId: '8D',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '8C'
        },
        {
            type: 'drag',
            cardId: '9C',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: '9D'
        },
        {
            type: 'drag',
            cardId: '10D',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '10C'
        },
        {
            type: 'drag',
            cardId: 'JC',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: 'JD'
        },
        {
            type: 'drag',
            cardId: 'QD',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: 'QC'
        },
        {
            type: 'drag',
            cardId: 'KC',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: 'KD'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'KS',
            target: 'foundation0'
        },
        {
            type: 'tapAutoComplete'
        },
        {
            type: 'waitForWin'
        }
    ]
};
/**
 * Draw-3, Seed 1: 117 actions (110 game moves + undo test + auto-complete + win check)
 * Foundation moves alternate between doubleTap and drag-to-foundation to exercise both code paths.
 */ const $7f9b59b6f74cfc3d$var$draw3Scenario = {
    name: 'Draw-3 Seed 1',
    seed: 1,
    drawCount: 3,
    actions: [
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock should have 24 cards after deal'
        },
        {
            type: 'assert',
            condition: 'state.waste.length === 0',
            description: 'Waste should be empty after deal'
        },
        {
            type: 'drag',
            cardId: '8H',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '6H',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: 'JH',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: '5D',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '7S',
            target: 'tableau6'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'assert',
            condition: 'true',
            description: 'Undo reversed last move'
        },
        {
            type: 'drag',
            cardId: '7S',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '2S',
            target: 'tableau2'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '4C',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '3H',
            target: 'tableau3'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '10C',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '2D',
            target: 'tableau4'
        },
        {
            type: 'doubleTap',
            cardId: 'AS'
        },
        {
            type: 'drag',
            cardId: '2S',
            target: 'foundation0'
        },
        {
            type: 'drag',
            cardId: '9D',
            target: 'tableau2'
        },
        {
            type: 'doubleTap',
            cardId: 'AH'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '2H',
            target: 'foundation3'
        },
        {
            type: 'doubleTap',
            cardId: '3H'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '4H',
            target: 'foundation3'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'KD',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: 'QS',
            target: 'tableau0'
        },
        {
            type: 'drag',
            cardId: '9S',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: '8C',
            target: 'tableau2'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'JC',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: '10D',
            target: 'tableau6'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '7D',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '6C',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: 'JS',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '5C',
            target: 'tableau6'
        },
        {
            type: 'doubleTap',
            cardId: 'AC'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '10S',
            target: 'tableau0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '9H',
            target: 'tableau0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'QC',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: 'JD',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: 'KC',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: 'QD',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '2D',
            target: 'tableau3'
        },
        {
            type: 'drag',
            cardId: '3S',
            target: 'foundation0'
        },
        {
            type: 'doubleTap',
            cardId: '4S'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '5S',
            target: 'foundation0'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'doubleTap',
            cardId: '6S'
        },
        {
            type: 'drag',
            cardId: '10H',
            target: 'tableau2'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: '9C',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '8D',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '5H',
            target: 'foundation3'
        },
        {
            type: 'drag',
            cardId: '3D',
            target: 'tableau5'
        },
        {
            type: 'drag',
            cardId: '7C',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '6H',
            target: 'tableau2'
        },
        {
            type: 'doubleTap',
            cardId: '7S'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'drag',
            cardId: 'KS',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '8S',
            target: 'foundation0'
        },
        {
            type: 'drag',
            cardId: 'QH',
            target: 'tableau1'
        },
        {
            type: 'drag',
            cardId: '5C',
            target: 'tableau6'
        },
        {
            type: 'doubleTap',
            cardId: '6H'
        },
        {
            type: 'drag',
            cardId: '6D',
            target: 'tableau2'
        },
        {
            type: 'drag',
            cardId: '3C',
            target: 'tableau6'
        },
        {
            type: 'drag',
            cardId: 'AD',
            target: 'foundation2'
        },
        {
            type: 'doubleTap',
            cardId: '2D'
        },
        {
            type: 'drag',
            cardId: '3D',
            target: 'foundation2'
        },
        {
            type: 'drag',
            cardId: '4D',
            target: 'tableau2'
        },
        {
            type: 'doubleTap',
            cardId: '2C'
        },
        {
            type: 'tapAutoComplete'
        },
        {
            type: 'waitForWin'
        }
    ]
};
/**
 * UI Settings Test — exercises all menu/settings interactions.
 *
 * This test opens the menu, toggles every setting, verifies each change takes
 * effect, then restores defaults. It also tests the new game button and undo
 * button in a non-solving context.
 *
 * Seed 1, draw-1 (default) — the specific deal doesn't matter, we just need
 * a game on screen to interact with the UI elements.
 */ const $7f9b59b6f74cfc3d$var$uiSettingsScenario = {
    name: 'UI Settings',
    seed: 1,
    drawCount: 1,
    actions: [
        // ── 1. Verify initial state ──
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Game should start in Playing state'
        },
        {
            type: 'assertSetting',
            key: 'drawCount',
            expected: 1,
            description: 'Default draw count is 1'
        },
        {
            type: 'assertSetting',
            key: 'handedness',
            expected: 'right',
            description: 'Default handedness is right'
        },
        {
            type: 'assertSetting',
            key: 'cardBackDesign',
            expected: 'pattern',
            description: 'Default card back is pattern'
        },
        // ── 2. Test menu open/close ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Menu',
            description: 'Menu should be open after tap'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Menu should close after second tap'
        },
        // ── 3. Test draw count toggle ──
        // Changing draw count closes the menu and starts a new game
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'draw-3'
        },
        {
            type: 'assertSetting',
            key: 'drawCount',
            expected: 3,
            description: 'Draw count should be 3 after tapping draw-3'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Menu should close after draw count change'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'draw-1'
        },
        {
            type: 'assertSetting',
            key: 'drawCount',
            expected: 1,
            description: 'Draw count should be 1 after tapping draw-1'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Menu should close after draw count change'
        },
        // ── 4. Test handedness toggle ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'hand-left'
        },
        {
            type: 'assertSetting',
            key: 'handedness',
            expected: 'left',
            description: 'Handedness should be left after tapping hand-left'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'hand-right'
        },
        {
            type: 'assertSetting',
            key: 'handedness',
            expected: 'right',
            description: 'Handedness should be right after tapping hand-right'
        },
        // ── 5. Test card back toggle ──
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'back-solid'
        },
        {
            type: 'assertSetting',
            key: 'cardBackDesign',
            expected: 'solid',
            description: 'Card back should be solid after tapping back-solid'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'back-pattern'
        },
        {
            type: 'assertSetting',
            key: 'cardBackDesign',
            expected: 'pattern',
            description: 'Card back should be pattern after tapping back-pattern'
        },
        // ── 6a. Test haptics toggle ──
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'haptic-off'
        },
        {
            type: 'assertSetting',
            key: 'hapticsEnabled',
            expected: false,
            description: 'Haptics should be disabled after tapping haptic-off'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'haptic-on'
        },
        {
            type: 'assertSetting',
            key: 'hapticsEnabled',
            expected: true,
            description: 'Haptics should be enabled after tapping haptic-on'
        },
        // ── 6b. Test sound toggle ──
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'sound-off'
        },
        {
            type: 'assertSetting',
            key: 'soundEnabled',
            expected: false,
            description: 'Sound should be disabled after tapping sound-off'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'sound-on'
        },
        {
            type: 'assertSetting',
            key: 'soundEnabled',
            expected: true,
            description: 'Sound should be enabled after tapping sound-on'
        },
        // ── 6. Test ALL 6 themes with color verification ──
        // Navigate to Themes sub-screen first
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        // Theme 1: HighContrastTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HighContrastTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HighContrastTheme',
            description: 'Theme should be HighContrastTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === 'black'",
            description: 'HighContrastTheme backgroundColor should be black'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#2A2A3A'",
            description: 'HighContrastTheme cardBackColor should be #2A2A3A'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardFaceColor === '#E8E8E8'",
            description: 'HighContrastTheme cardFaceColor should be #E8E8E8 (not white)'
        },
        // Theme 2: ClassicGreenTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-ClassicGreenTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'ClassicGreenTheme',
            description: 'Theme should be ClassicGreenTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#1B5E20'",
            description: 'ClassicGreenTheme backgroundColor should be #1B5E20'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#6B1C2A'",
            description: 'ClassicGreenTheme cardBackColor should be #6B1C2A'
        },
        // Theme 3: PleasedPinkTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-PleasedPinkTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'PleasedPinkTheme',
            description: 'Theme should be PleasedPinkTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#C12849'",
            description: 'PleasedPinkTheme backgroundColor should be #C12849'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#8B2040'",
            description: 'PleasedPinkTheme cardBackColor should be #8B2040'
        },
        // Theme 4: SoftBlueTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-SoftBlueTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'SoftBlueTheme',
            description: 'Theme should be SoftBlueTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#268bd2'",
            description: 'SoftBlueTheme backgroundColor should be #268bd2'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#1A3A5C'",
            description: 'SoftBlueTheme cardBackColor should be #1A3A5C'
        },
        // Theme 5: HappyPurpleTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HappyPurpleTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HappyPurpleTheme',
            description: 'Theme should be HappyPurpleTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#292D3E'",
            description: 'HappyPurpleTheme backgroundColor should be #292D3E'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#5E35A0'",
            description: 'HappyPurpleTheme cardBackColor should be #5E35A0'
        },
        // Theme 6: ForestTheme
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-ForestTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'ForestTheme',
            description: 'Theme should be ForestTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#33402E'",
            description: 'ForestTheme backgroundColor should be #33402E'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.cardBackColor === '#6B3A28'",
            description: 'ForestTheme cardBackColor should be #6B3A28'
        },
        // Restore default theme (HighContrastTheme is the initial default)
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HighContrastTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HighContrastTheme',
            description: 'Theme should be restored to HighContrastTheme'
        },
        // Navigate back to main menu from Themes sub-screen
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        // ── 7. Close menu and test undo ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Back to Playing after closing menu'
        },
        // Make a move (tap stock to draw a card), then undo it
        {
            type: 'tapStock'
        },
        {
            type: 'assert',
            condition: 'state.waste.length > 0',
            description: 'Waste should have cards after drawing from stock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'assert',
            condition: 'state.waste.length === 0',
            description: 'Waste should be empty after undoing stock draw'
        },
        // ── 8. Test new game button (now lives inside the menu) ──
        // First draw a card so state is dirty
        {
            type: 'tapStock'
        },
        // Open the menu, then tap new game button
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Menu',
            description: 'Menu should be open before tapping New Game'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'new-game'
        },
        // After new game, stock should be full (24 cards) and waste empty
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock should have 24 cards after new game'
        },
        {
            type: 'assert',
            condition: 'state.waste.length === 0',
            description: 'Waste should be empty after new game'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Should be in Playing state after new game'
        }
    ]
};
/**
 * Draw-1 Left-Handed: Reuses draw1Scenario's action sequence with left-handed layout.
 * Same seed, same moves — validates that left-handed coordinate mirroring works correctly.
 * The test runner resolves card positions by cardId at runtime, so the same solve
 * sequence exercises the mirrored coordinate system end-to-end.
 */ const $7f9b59b6f74cfc3d$var$draw1lhScenario = {
    name: 'Draw-1 Seed 1 (Left-Handed)',
    seed: $7f9b59b6f74cfc3d$var$draw1Scenario.seed,
    drawCount: $7f9b59b6f74cfc3d$var$draw1Scenario.drawCount,
    handedness: 'left',
    actions: $7f9b59b6f74cfc3d$var$draw1Scenario.actions
};
/**
 * Draw-3 Left-Handed: Reuses draw3Scenario's action sequence with left-handed layout.
 * Same seed, same moves — validates that left-handed coordinate mirroring works correctly.
 */ const $7f9b59b6f74cfc3d$var$draw3lhScenario = {
    name: 'Draw-3 Seed 1 (Left-Handed)',
    seed: $7f9b59b6f74cfc3d$var$draw3Scenario.seed,
    drawCount: $7f9b59b6f74cfc3d$var$draw3Scenario.drawCount,
    handedness: 'left',
    actions: $7f9b59b6f74cfc3d$var$draw3Scenario.actions
};
/**
 * Theme Smoke Test: Opens menu, switches through all 6 themes, and for each:
 * - Verifies theme is applied (backgroundColor, cardBackColor)
 * - Closes menu, plays a few stock taps + undo to exercise rendering
 * - Re-opens menu for next theme
 *
 * Uses seed 1 draw-1 for a consistent initial deal.
 * This catches rendering regressions where a theme's colors aren't applied correctly.
 */ const $7f9b59b6f74cfc3d$var$themesSmokeScenario = {
    name: 'Theme Smoke Test',
    seed: 1,
    drawCount: 1,
    actions: [
        // Verify initial deal
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock should have 24 cards after deal'
        },
        // ── Theme 1: HighContrastTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Menu',
            description: 'Menu should be open'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HighContrastTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HighContrastTheme',
            description: 'Theme should be HighContrastTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === 'black'",
            description: 'HighContrastTheme bg should be black'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Back to playing'
        },
        // Play a few moves to exercise rendering with this theme
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Theme 2: ClassicGreenTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-ClassicGreenTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'ClassicGreenTheme',
            description: 'Theme should be ClassicGreenTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#1B5E20'",
            description: 'ClassicGreenTheme bg should be #1B5E20'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Theme 3: PleasedPinkTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-PleasedPinkTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'PleasedPinkTheme',
            description: 'Theme should be PleasedPinkTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#C12849'",
            description: 'PleasedPinkTheme bg should be #C12849'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Theme 4: SoftBlueTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-SoftBlueTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'SoftBlueTheme',
            description: 'Theme should be SoftBlueTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#268bd2'",
            description: 'SoftBlueTheme bg should be #268bd2'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Theme 5: HappyPurpleTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HappyPurpleTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HappyPurpleTheme',
            description: 'Theme should be HappyPurpleTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#292D3E'",
            description: 'HappyPurpleTheme bg should be #292D3E'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Theme 6: ForestTheme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-ForestTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'ForestTheme',
            description: 'Theme should be ForestTheme'
        },
        {
            type: 'assert',
            condition: "appState.currentTheme.backgroundColor === '#33402E'",
            description: 'ForestTheme bg should be #33402E'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapUndo'
        },
        {
            type: 'tapUndo'
        },
        // ── Restore default theme ──
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-HighContrastTheme'
        },
        {
            type: 'assertSetting',
            key: 'themeName',
            expected: 'HighContrastTheme',
            description: 'Theme restored to default HighContrastTheme'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Back to playing after theme test'
        },
        // Final state check — game should still be intact
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock still has 24 cards (all moves were undone)'
        },
        {
            type: 'assert',
            condition: 'state.waste.length === 0',
            description: 'Waste is empty (all moves were undone)'
        }
    ]
};
/**
 * Return all actions up to (but excluding) the first tapAutoComplete action.
 * Useful for manual verification runs where we want the game staged right
 * before auto-complete and let the user tap it themselves.
 */ function $7f9b59b6f74cfc3d$var$actionsBeforeAutoComplete(actions) {
    const autoIndex = actions.findIndex((action)=>action.type === 'tapAutoComplete');
    if (autoIndex === -1) return actions.slice();
    return actions.slice(0, autoIndex);
}
/** Return the first N actions from a scenario action list. */ function $7f9b59b6f74cfc3d$var$takeActions(actions, count) {
    return actions.slice(0, Math.max(0, count));
}
/**
 * Manual setup scenario: replays Draw-1 to the point where auto-complete is available,
 * then stops so the user can tap the auto-complete button manually.
 */ const $7f9b59b6f74cfc3d$var$draw1setupScenario = {
    name: 'Draw-1 Pre Auto-Complete (Manual Setup)',
    seed: $7f9b59b6f74cfc3d$var$draw1Scenario.seed,
    drawCount: $7f9b59b6f74cfc3d$var$draw1Scenario.drawCount,
    actions: $7f9b59b6f74cfc3d$var$actionsBeforeAutoComplete($7f9b59b6f74cfc3d$var$draw1Scenario.actions)
};
/**
 * Manual setup scenario: replays Draw-3 to the point where auto-complete is available,
 * then stops so the user can tap the auto-complete button manually.
 */ const $7f9b59b6f74cfc3d$var$draw3setupScenario = {
    name: 'Draw-3 Pre Auto-Complete (Manual Setup)',
    seed: $7f9b59b6f74cfc3d$var$draw3Scenario.seed,
    drawCount: $7f9b59b6f74cfc3d$var$draw3Scenario.drawCount,
    actions: $7f9b59b6f74cfc3d$var$actionsBeforeAutoComplete($7f9b59b6f74cfc3d$var$draw3Scenario.actions)
};
/**
 * Screenshot scenario: mid-game board in default Twilight theme.
 * Uses the opening portion of Draw-1 to create visible progress quickly.
 */ const $7f9b59b6f74cfc3d$var$screenshotGameplayScenario = {
    name: 'Screenshot - Gameplay Midgame',
    seed: $7f9b59b6f74cfc3d$var$draw1Scenario.seed,
    drawCount: $7f9b59b6f74cfc3d$var$draw1Scenario.drawCount,
    actions: $7f9b59b6f74cfc3d$var$takeActions($7f9b59b6f74cfc3d$var$draw1Scenario.actions, 40)
};
/**
 * Screenshot scenario: menu open with theme controls visible.
 */ const $7f9b59b6f74cfc3d$var$screenshotMenuScenario = {
    name: 'Screenshot - Menu Open',
    seed: 1,
    drawCount: 1,
    actions: [
        {
            type: 'assert',
            condition: 'state.stock.length === 24',
            description: 'Stock should have 24 cards after deal'
        },
        {
            type: 'tapMenuButton'
        },
        {
            type: 'assertAppState',
            expected: 'Menu',
            description: 'Menu should be open for screenshot'
        }
    ]
};
/**
 * Screenshot scenario: left-handed Classic theme in Draw-3 with visible waste.
 */ const $7f9b59b6f74cfc3d$var$screenshotClassicLeftScenario = {
    name: 'Screenshot - Classic Left Handed',
    seed: 1,
    drawCount: 1,
    actions: [
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-ClassicGreenTheme'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'hand-left'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'draw-3'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Draw mode change should return to playing state'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'assert',
            condition: 'state.waste.length > 0',
            description: 'Waste should be visible for gameplay screenshot'
        }
    ]
};
/**
 * Screenshot scenario: Ocean theme, right-handed, Draw-3 gameplay state.
 */ const $7f9b59b6f74cfc3d$var$screenshotOceanRightScenario = {
    name: 'Screenshot - Ocean Right Handed',
    seed: 1,
    drawCount: 1,
    actions: [
        {
            type: 'tapMenuButton'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-themes'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'theme-SoftBlueTheme'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'menu-back'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'hand-right'
        },
        {
            type: 'tapMenuHitArea',
            hitAreaId: 'draw-3'
        },
        {
            type: 'assertAppState',
            expected: 'Playing',
            description: 'Draw mode change should return to playing state'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'tapStock'
        },
        {
            type: 'assert',
            condition: 'state.waste.length > 0',
            description: 'Waste should be visible for gameplay screenshot'
        }
    ]
};
/**
 * Screenshot scenario: completed win screen.
 *
 * Instead of replaying the full draw3 scenario (~120 actions that can fail
 * silently in the native simulator), this directly seeds a won-game state
 * with all 52 cards in foundations. Completes in milliseconds and is 100% reliable.
 */ const $7f9b59b6f74cfc3d$var$screenshotWinScenario = {
    name: 'Screenshot - Win State',
    seed: 1,
    drawCount: 1,
    actions: [
        {
            type: 'seedWinState'
        },
        {
            type: 'assertAppState',
            expected: 'Completed',
            description: 'Win state should be active'
        }
    ]
};
const $7f9b59b6f74cfc3d$export$22f2203758701f7f = {
    draw1: $7f9b59b6f74cfc3d$var$draw1Scenario,
    draw1lh: $7f9b59b6f74cfc3d$var$draw1lhScenario,
    draw3: $7f9b59b6f74cfc3d$var$draw3Scenario,
    draw3lh: $7f9b59b6f74cfc3d$var$draw3lhScenario,
    draw1setup: $7f9b59b6f74cfc3d$var$draw1setupScenario,
    draw3setup: $7f9b59b6f74cfc3d$var$draw3setupScenario,
    'shot-gameplay': $7f9b59b6f74cfc3d$var$screenshotGameplayScenario,
    'shot-menu': $7f9b59b6f74cfc3d$var$screenshotMenuScenario,
    'shot-classic-left': $7f9b59b6f74cfc3d$var$screenshotClassicLeftScenario,
    'shot-ocean-right': $7f9b59b6f74cfc3d$var$screenshotOceanRightScenario,
    'shot-win': $7f9b59b6f74cfc3d$var$screenshotWinScenario,
    ui: $7f9b59b6f74cfc3d$var$uiSettingsScenario,
    themes: $7f9b59b6f74cfc3d$var$themesSmokeScenario
};




/**
 * TestRunner — Automated UI replay engine for Vertical Solitaire.
 *
 * Replays a sequence of high-level actions (tapStock, drag, doubleTap, tapUndo,
 * tapAutoComplete, assert, waitForWin) by resolving card/button positions at
 * runtime and firing real mock touch events through the existing canvas handlers.
 *
 * This exercises the exact same code paths as a human player — no shortcuts.
 *
 * Triggered via URL params: ?test=draw1 or ?test=draw3
 */ 











// ──────────────────────────────────────────────────────────────────────────────
// Mock touch event creation
// ──────────────────────────────────────────────────────────────────────────────
function $67c86676dfc84be7$var$createMockTouch(x, y, type) {
    return {
        changedTouches: [
            {
                clientX: x,
                clientY: y,
                pageX: x,
                pageY: y,
                screenX: x,
                screenY: y
            }
        ],
        preventDefault: ()=>{},
        type: type,
        touches: [
            {
                clientX: x,
                clientY: y
            }
        ]
    };
}
// ──────────────────────────────────────────────────────────────────────────────
// Card lookup helpers
// ──────────────────────────────────────────────────────────────────────────────
/** Parse a card ID like "AH", "10D", "KS" into { value, type }. */ function $67c86676dfc84be7$var$parseCardId(id) {
    // Last character is suit type; everything before is the value
    const type = id.slice(-1); // 'H', 'D', 'S', 'C'
    const value = id.slice(0, -1); // 'A', '2'..'10', 'J', 'Q', 'K'
    return {
        value: value,
        type: type
    };
}
/** Convert a card value string ('A','2'..'10','J','Q','K') to numeric (1..13). */ function $67c86676dfc84be7$var$valueToNum(value) {
    const map = {
        A: 1,
        J: 11,
        Q: 12,
        K: 13
    };
    return map[value] ?? parseInt(value, 10);
}
/** Find a card object in the current game state by its ID (e.g. "8H"). */ function $67c86676dfc84be7$var$findCard(cardId) {
    const { value: value, type: type } = $67c86676dfc84be7$var$parseCardId(cardId);
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    // Search tableau
    for(let i = 0; i < state.tableau.length; i++)for(let j = 0; j < state.tableau[i].length; j++){
        const card = state.tableau[i][j];
        if (card.value === value && card.type === type) return {
            card: card,
            location: `tableau[${i}][${j}]`
        };
    }
    // Search waste (top card only for interaction, but search all for finding)
    for(let i = state.waste.length - 1; i >= 0; i--){
        const card = state.waste[i];
        if (card.value === value && card.type === type) return {
            card: card,
            location: `waste[${i}]`
        };
    }
    // Search foundation
    for(let i = 0; i < state.foundation.length; i++)for(let j = state.foundation[i].length - 1; j >= 0; j--){
        const card = state.foundation[i][j];
        if (card.value === value && card.type === type) return {
            card: card,
            location: `foundation[${i}][${j}]`
        };
    }
    // Search stock
    for(let i = state.stock.length - 1; i >= 0; i--){
        const card = state.stock[i];
        if (card.value === value && card.type === type) return {
            card: card,
            location: `stock[${i}]`
        };
    }
    return null;
}
/**
 * Get a pickup point that uniquely targets a specific card, even when it's partially
 * covered by overlapping cards in a tableau cascade.
 *
 * For tableau cards that aren't the top card in their pile, the center may be
 * covered by the next card (which `findCardInTableau` would return instead, since
 * it iterates backward). This function targets the visible (uncovered) sliver.
 *
 * - Portrait: cascades horizontally; handedness decides which edge is visible.
 * - Landscape: cascades vertically; visible sliver is at the top of the card.
 *
 * For non-tableau cards (waste, foundation, stock) or the top card in a tableau
 * pile, the regular center is returned.
 */ function $67c86676dfc84be7$var$getCardPickupPoint(card, location) {
    const pos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(card);
    const centerY = pos.y + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight / 2;
    // Check if this card is in a tableau pile and not the top card
    const tableauMatch = location.match(/^tableau\[(\d+)\]\[(\d+)\]$/);
    if (tableauMatch) {
        const pileIndex = parseInt(tableauMatch[1], 10);
        const cardIndex = parseInt(tableauMatch[2], 10);
        const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        const pile = state.tableau[pileIndex];
        if (cardIndex < pile.length - 1) {
            // This card is covered by the next card in the cascade.
            // Target the visible sliver instead of the center.
            const step = (0, $0f5b71ede2b50336$export$77ae4c367ef625c2)(card.isFaceDown);
            let targetX;
            let targetY;
            if ((0, $0f5b71ede2b50336$export$72f5d9a44bd9a7f0)()) {
                targetX = pos.x + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2;
                targetY = pos.y + step / 2;
            } else if ((0, $0f5b71ede2b50336$export$e79bc7cac697f199)()) {
                // Left-handed: cascade goes RIGHT, visible sliver is on the LEFT edge
                targetX = pos.x + step / 2;
                targetY = centerY;
            } else {
                // Right-handed: cascade goes LEFT, visible sliver is on the RIGHT edge
                targetX = pos.x + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth - step / 2;
                targetY = centerY;
            }
            console.log(`[TestRunner]   pickup targeting visible sliver: card at index ${cardIndex}/${pile.length - 1} in tableau[${pileIndex}], ` + `step=${step.toFixed(1)}, targeting (${Math.round(targetX)}, ${Math.round(targetY)}) instead of center (${Math.round(pos.x + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2)}, ${Math.round(centerY)})`);
            return {
                x: targetX,
                y: targetY
            };
        }
    }
    // Default: center of card (top card in tableau, or waste/foundation/stock)
    return {
        x: pos.x + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: centerY
    };
}
/** Get the center of the stock pile area. */ function $67c86676dfc84be7$var$getStockCenter() {
    return {
        x: (0, $0f5b71ede2b50336$export$889074c399efa9cf)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$72029e8b50497416)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight / 2
    };
}
/** Get the center of the undo button. */ function $67c86676dfc84be7$var$getUndoCenter() {
    return {
        x: (0, $0f5b71ede2b50336$export$26fe8f1cbe2626e5)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$70c3227cdbebd1b8)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2 // Buttons are square: cardWidth × cardWidth
    };
}
/** Get the center of the auto-complete button. */ function $67c86676dfc84be7$var$getAutoCompleteCenter() {
    return {
        x: (0, $0f5b71ede2b50336$export$51646a58b06b5693)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$ff0ff2fb1fcb2a95)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2
    };
}
/** Get the center of the menu button. */ function $67c86676dfc84be7$var$getMenuButtonCenter() {
    return {
        x: (0, $0f5b71ede2b50336$export$1f614aa49af839fa)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$6d939bb5033370b0)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2
    };
}
/** Get the center of the new game button. */ function $67c86676dfc84be7$var$getNewGameButtonCenter() {
    return {
        x: (0, $0f5b71ede2b50336$export$833ed0fe1212d409)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$f81ed66b3906beb3)() + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2
    };
}
/**
 * Get the drop target center for a tableau pile.
 * If the pile has cards, target just past the last card's horizontal cascade.
 * If empty, target the center of the placeholder.
 */ function $67c86676dfc84be7$var$getTableauDropTarget(tableauIndex) {
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const pile = state.tableau[tableauIndex];
    const baseX = (0, $0f5b71ede2b50336$export$f8ddd168b43d671a)(tableauIndex);
    const baseY = (0, $0f5b71ede2b50336$export$38cc5b0bc1d504c6)(tableauIndex);
    if (pile.length === 0) // Empty pile — center of placeholder
    return {
        x: baseX + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: baseY + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight / 2
    };
    // Target the center of the top (last) card in the pile
    const topCard = pile[pile.length - 1];
    const topPos = (0, $f08e1321bb9509a9$export$1a73458087cc41c4)(topCard);
    return {
        x: topPos.x + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: topPos.y + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight / 2
    };
}
/**
 * Get the drop target center for a foundation pile.
 */ function $67c86676dfc84be7$var$getFoundationDropTarget(foundationIndex) {
    return {
        x: (0, $0f5b71ede2b50336$export$84ce64f2330f553a)(foundationIndex) + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth / 2,
        y: (0, $0f5b71ede2b50336$export$7cb2c3b01d2fdd74)(foundationIndex) + (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight / 2
    };
}
// ──────────────────────────────────────────────────────────────────────────────
// Wait helpers
// ──────────────────────────────────────────────────────────────────────────────
/** Wait for a fixed number of milliseconds. */ function $67c86676dfc84be7$var$delay(ms) {
    return new Promise((resolve)=>setTimeout(resolve, ms));
}
/** Wait until all animations have completed, with a timeout. */ async function $67c86676dfc84be7$var$waitForAnimations(timeoutMs = 5000) {
    const startTime = Date.now();
    while((0, $2b7dc820af08b312$export$9c3b3a49659bb096).isAnimating()){
        if (Date.now() - startTime > timeoutMs) {
            console.warn("[TestRunner] Animation timeout \u2014 forcing clear");
            (0, $2b7dc820af08b312$export$9c3b3a49659bb096).clearAll();
            return;
        }
        await $67c86676dfc84be7$var$delay(16); // ~1 frame
    }
}
// ──────────────────────────────────────────────────────────────────────────────
// Face-down card auto-flip
// ──────────────────────────────────────────────────────────────────────────────
// Auto-flip of exposed face-down cards is now handled by commitMove() in state.ts.
// The flipAnyExposedFaceDownCards() helper that used to live here has been removed.
// ──────────────────────────────────────────────────────────────────────────────
// Action executors
// ──────────────────────────────────────────────────────────────────────────────
async function $67c86676dfc84be7$var$executeTapStock() {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    // Snapshot before
    const before = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const beforeStockLen = before.stock.length;
    const beforeWasteLen = before.waste.length;
    const beforeWasteTop = beforeWasteLen > 0 ? `${before.waste[beforeWasteLen - 1].value}${before.waste[beforeWasteLen - 1].type}` : '(empty)';
    const center = $67c86676dfc84be7$var$getStockCenter();
    const touchEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchstart');
    // @ts-ignore — mock touch event
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(touchEvent);
    await $67c86676dfc84be7$var$delay(50);
    const endEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(50);
    // Validate the draw happened
    const after = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const afterStockLen = after.stock.length;
    const afterWasteLen = after.waste.length;
    const afterWasteTop = afterWasteLen > 0 ? `${after.waste[afterWasteLen - 1].value}${after.waste[afterWasteLen - 1].type}` : '(empty)';
    if (beforeStockLen > 0) {
        // Should have drawn cards: stock decreases, waste increases
        const numDrawn = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED;
        const expectedDraw = Math.min(numDrawn, beforeStockLen);
        if (afterStockLen !== beforeStockLen - expectedDraw) throw new Error(`tapStock VALIDATION FAILED: Expected stock ${beforeStockLen} \u{2192} ${beforeStockLen - expectedDraw}, got ${afterStockLen}. ` + `Waste: ${beforeWasteLen} \u{2192} ${afterWasteLen}. Waste top: ${beforeWasteTop} \u{2192} ${afterWasteTop}`);
    } else {
        // Stock was empty — should have reset waste to stock
        if (afterWasteLen !== 0) throw new Error(`tapStock VALIDATION FAILED (reset): Expected waste to be empty after reset, got ${afterWasteLen}. ` + `Stock: ${beforeStockLen} \u{2192} ${afterStockLen}`);
    }
}
async function $67c86676dfc84be7$var$executeDrag(cardId, target) {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    const found = $67c86676dfc84be7$var$findCard(cardId);
    if (!found) throw new Error(`Card ${cardId} not found in current state`);
    // Check if card is face-down (can't drag face-down cards)
    if (found.card.isFaceDown) throw new Error(`Card ${cardId} is face-down at ${found.location} \u{2014} cannot drag`);
    const sourceLocation = found.location;
    const cardCenter = $67c86676dfc84be7$var$getCardPickupPoint(found.card, found.location);
    let dropTarget;
    let targetPileType;
    let targetIndex;
    if (target.startsWith('tableau')) {
        targetPileType = 'tableau';
        targetIndex = parseInt(target.replace('tableau', ''), 10);
        dropTarget = $67c86676dfc84be7$var$getTableauDropTarget(targetIndex);
    } else if (target.startsWith('foundation')) {
        targetPileType = 'foundation';
        // The solver assigns foundation indices based on its own tracking, but
        // doubleTap moves may have placed cards on different foundations than the
        // solver expected.  Dynamically find the correct foundation pile that can
        // accept this card based on current game state.
        const { value: value, type: cardType } = $67c86676dfc84be7$var$parseCardId(cardId);
        const currentState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        const solverIndex = parseInt(target.replace('foundation', ''), 10);
        let resolvedIndex = solverIndex;
        // Check if the solver's target foundation can actually accept this card
        const solverPile = currentState.foundation[solverIndex];
        const cardNumValue = $67c86676dfc84be7$var$valueToNum(value);
        const canUseSolverPile = solverPile.length === 0 && cardNumValue === 1 || solverPile.length > 0 && solverPile[solverPile.length - 1].type === cardType && solverPile[solverPile.length - 1].numValue === cardNumValue - 1;
        if (!canUseSolverPile) // Find the actual foundation that can accept this card
        for(let fi = 0; fi < currentState.foundation.length; fi++){
            const pile = currentState.foundation[fi];
            const canPlace = pile.length === 0 && cardNumValue === 1 || pile.length > 0 && pile[pile.length - 1].type === cardType && pile[pile.length - 1].numValue === cardNumValue - 1;
            if (canPlace) {
                resolvedIndex = fi;
                console.log(`[TestRunner]   foundation redirect: solver wanted ${target} but using foundation${fi} (suit mismatch)`);
                break;
            }
        }
        targetIndex = resolvedIndex;
        dropTarget = $67c86676dfc84be7$var$getFoundationDropTarget(targetIndex);
    } else throw new Error(`Unknown drag target: ${target}`);
    // 1. Touch start on the card
    console.log(`[TestRunner]   drag debug: pickup at (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)}), drop at (${Math.round(dropTarget.x)}, ${Math.round(dropTarget.y)})`);
    const startEvent = $67c86676dfc84be7$var$createMockTouch(cardCenter.x, cardCenter.y, 'touchstart');
    // @ts-ignore
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(startEvent);
    // Verify card was picked up (should be in draggedCards)
    const draggedAfterPickup = (0, $13e781a56ac394f5$export$7f3c49b37340bfce)();
    console.log(`[TestRunner]   drag debug: after pickup, draggedCards=${draggedAfterPickup.length}`);
    if (draggedAfterPickup.length === 0) throw new Error(`drag PICKUP FAILED: ${cardId} was not picked up. ` + `Source: ${sourceLocation}. Card position: (${Math.round(found.card.x)}, ${Math.round(found.card.y)}). ` + `Touch: (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)})`);
    await $67c86676dfc84be7$var$delay(30);
    // 2. Touch move — interpolate a few steps for realism
    const steps = 5;
    for(let i = 1; i <= steps; i++){
        const t = i / steps;
        const moveX = cardCenter.x + (dropTarget.x - cardCenter.x) * t;
        const moveY = cardCenter.y + (dropTarget.y - cardCenter.y) * t;
        const moveEvent = $67c86676dfc84be7$var$createMockTouch(moveX, moveY, 'touchmove');
        // @ts-ignore
        (0, $9c0551e961641722$export$d6664ee70a10ed8)(moveEvent);
        await $67c86676dfc84be7$var$delay(16);
    }
    // 3. Touch end at the drop target
    const endEvent = $67c86676dfc84be7$var$createMockTouch(dropTarget.x, dropTarget.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(50);
    // Validate the card arrived at the target pile
    const { value: value, type: type } = $67c86676dfc84be7$var$parseCardId(cardId);
    const afterState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const targetPile = targetPileType === 'tableau' ? afterState.tableau[targetIndex] : afterState.foundation[targetIndex];
    const cardInTarget = targetPile.some((c)=>c.value === value && c.type === type);
    if (!cardInTarget) {
        // Card didn't arrive — check where it ended up
        const afterFound = $67c86676dfc84be7$var$findCard(cardId);
        const afterLocation = afterFound ? afterFound.location : 'NOT FOUND';
        // Debug: log the drop coordinates and hit detection results
        const dbgIsOverTableau = (0, $6fa52cac1c396f76$export$42985f28b70fda0f)(dropTarget.x, dropTarget.y);
        const dbgIsOverFoundation = (0, $6fa52cac1c396f76$export$a545b174d3590fa5)(dropTarget.x, dropTarget.y);
        throw new Error(`drag VALIDATION FAILED: ${cardId} was NOT placed on ${target}. ` + `Source was: ${sourceLocation}. Now at: ${afterLocation}. ` + `Drop coords: (${Math.round(dropTarget.x)}, ${Math.round(dropTarget.y)}). ` + `isOverTableau=${dbgIsOverTableau}, isOverFoundation=${dbgIsOverFoundation}. ` + `Card pickup at: (${Math.round(cardCenter.x)}, ${Math.round(cardCenter.y)}). ` + `Target pile has ${targetPile.length} cards: [${targetPile.map((c)=>c.value + c.type).join(', ')}]`);
    }
}
async function $67c86676dfc84be7$var$executeDoubleTap(cardId) {
    const found = $67c86676dfc84be7$var$findCard(cardId);
    if (!found) throw new Error(`Card ${cardId} not found in current state`);
    const sourceLocation = found.location;
    const cardCenter = $67c86676dfc84be7$var$getCardPickupPoint(found.card, found.location);
    const event = $67c86676dfc84be7$var$createMockTouch(cardCenter.x, cardCenter.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$6a99faad2978514d)(event);
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(50);
    // Validate the card moved to a foundation
    const { value: value, type: type } = $67c86676dfc84be7$var$parseCardId(cardId);
    const afterState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    let foundInFoundation = false;
    for(let fi = 0; fi < afterState.foundation.length; fi++)if (afterState.foundation[fi].some((c)=>c.value === value && c.type === type)) {
        foundInFoundation = true;
        break;
    }
    if (!foundInFoundation) {
        const afterFound = $67c86676dfc84be7$var$findCard(cardId);
        const afterLocation = afterFound ? afterFound.location : 'NOT FOUND';
        throw new Error(`doubleTap VALIDATION FAILED: ${cardId} was NOT moved to foundation. ` + `Source was: ${sourceLocation}. Now at: ${afterLocation}`);
    }
}
async function $67c86676dfc84be7$var$executeTapUndo() {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    const center = $67c86676dfc84be7$var$getUndoCenter();
    const touchEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchstart');
    // @ts-ignore
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(touchEvent);
    await $67c86676dfc84be7$var$delay(50);
    const endEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(50);
}
async function $67c86676dfc84be7$var$executeTapAutoComplete() {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    // If the game is already won, skip auto-complete gracefully
    const preState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const preFoundationTotal = preState.foundation.reduce((s, p)=>s + p.length, 0);
    if (preFoundationTotal === 52) {
        console.log("[TestRunner]   tapAutoComplete: game already won (52 in foundations) \u2014 skipping");
        return;
    }
    // First verify auto-complete is available
    if (!(0, $1a73a0108f314080$export$9a5d87b5cc49764d)()) {
        // Diagnose why auto-complete isn't available
        const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        const diag = [];
        if (state.stock.length > 0) diag.push(`stock has ${state.stock.length} cards`);
        if (state.waste.length > 0) diag.push(`waste has ${state.waste.length} cards`);
        for(let i = 0; i < state.tableau.length; i++){
            const faceDown = state.tableau[i].filter((c)=>c.isFaceDown);
            if (faceDown.length > 0) diag.push(`tableau[${i}] has ${faceDown.length} face-down`);
        }
        const fTotal = state.foundation.reduce((s, p)=>s + p.length, 0);
        if (fTotal === 52) diag.push('already won (foundation=52)');
        throw new Error(`Auto-complete is not available \u{2014} conditions not met: ${diag.join('; ')}`);
    }
    const center = $67c86676dfc84be7$var$getAutoCompleteCenter();
    const touchEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchstart');
    // @ts-ignore
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(touchEvent);
    await $67c86676dfc84be7$var$delay(50);
    const endEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    // Auto-complete chains many animations with 50ms delays between them.
    // Wait generously for all of them to finish.
    await $67c86676dfc84be7$var$waitForAutoComplete(30000);
    await $67c86676dfc84be7$var$delay(200);
}
/** Wait for auto-complete to finish (all foundations at 13). */ async function $67c86676dfc84be7$var$waitForAutoComplete(timeoutMs = 30000) {
    const startTime = Date.now();
    while(Date.now() - startTime < timeoutMs){
        const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
        const totalFoundation = state.foundation.reduce((sum, pile)=>sum + pile.length, 0);
        if (totalFoundation === 52) return;
        await $67c86676dfc84be7$var$delay(100);
    }
    console.warn('[TestRunner] Auto-complete timeout');
}
async function $67c86676dfc84be7$var$executeAssert(condition, description) {
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    try {
        // Evaluate condition in context with state and appState available.
        // SECURITY NOTE: `new Function()` is used intentionally here to evaluate
        // dynamic assertion strings from test scenario definitions (which are
        // developer-authored, not user-supplied). This code only runs in the
        // in-app test runner, never in normal gameplay. The CSP includes
        // 'unsafe-eval' to permit this usage.
        const result = new Function('state', 'appState', `return (${condition})`)(state, (0, $13e781a56ac394f5$export$b72e390a9ebe1648));
        if (!result) {
            console.error(`[TestRunner] ASSERT FAILED: ${description} (condition: ${condition})`);
            return false;
        }
        return true;
    } catch (err) {
        console.error(`[TestRunner] ASSERT ERROR: ${description} \u{2014} ${err}`);
        return false;
    }
}
async function $67c86676dfc84be7$var$executeWaitForWin() {
    // Check immediately first
    if ((0, $13e781a56ac394f5$export$7601ebd7d9b3975b)()) return true;
    // Wait a bit in case auto-complete is still finishing
    for(let i = 0; i < 100; i++){
        await $67c86676dfc84be7$var$delay(100);
        if ((0, $13e781a56ac394f5$export$7601ebd7d9b3975b)()) return true;
    }
    console.error("[TestRunner] WIN CHECK FAILED \u2014 game not won after waiting");
    return false;
}
// ──────────────────────────────────────────────────────────────────────────────
// Direct state seeding (for reliable screenshot scenarios)
// ──────────────────────────────────────────────────────────────────────────────
/**
 * Build a complete won-game state: all 52 cards in 4 foundation piles (A→K),
 * all other piles empty. Then transition to Completed app state.
 *
 * This bypasses the full game replay, making win-screen screenshots 100% reliable.
 */ async function $67c86676dfc84be7$var$executeSeedWinState() {
    const suits = [
        {
            type: 'S',
            color: 'black'
        },
        {
            type: 'C',
            color: 'black'
        },
        {
            type: 'D',
            color: 'red'
        },
        {
            type: 'H',
            color: 'red'
        }
    ];
    const values = [
        {
            value: 'A',
            numValue: 1
        },
        {
            value: '2',
            numValue: 2
        },
        {
            value: '3',
            numValue: 3
        },
        {
            value: '4',
            numValue: 4
        },
        {
            value: '5',
            numValue: 5
        },
        {
            value: '6',
            numValue: 6
        },
        {
            value: '7',
            numValue: 7
        },
        {
            value: '8',
            numValue: 8
        },
        {
            value: '9',
            numValue: 9
        },
        {
            value: '10',
            numValue: 10
        },
        {
            value: 'J',
            numValue: 11
        },
        {
            value: 'Q',
            numValue: 12
        },
        {
            value: 'K',
            numValue: 13
        }
    ];
    // Build 4 foundation piles: Ace through King for each suit
    const foundation = [
        [],
        [],
        [],
        []
    ];
    for(let si = 0; si < suits.length; si++)for (const v of values)foundation[si].push({
        type: suits[si].type,
        color: suits[si].color,
        value: v.value,
        numValue: v.numValue,
        isFaceDown: false,
        x: 0,
        y: 0
    });
    const winState = {
        foundation: foundation,
        stock: [],
        waste: [],
        tableau: [
            [],
            [],
            [],
            [],
            [],
            [],
            []
        ],
        draggedCards: [],
        wasteVisibleCount: 0
    };
    // Disable integrity check since we're seeding state directly, then commit
    (0, $6cec517bcba19c3d$export$9bce854bb9edf084)();
    const withCoords = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(winState);
    (0, $13e781a56ac394f5$export$24db6d4500eb94db)(withCoords, {
        skipIntegrity: true
    });
    // Transition to Completed state (triggers win animation on next paint)
    (0, $13e781a56ac394f5$export$f4a51cc8ad86d45d)();
    (0, $13e781a56ac394f5$export$8921d147d4d1b7)({
        ...(0, $13e781a56ac394f5$export$b72e390a9ebe1648),
        currentAppState: (0, $7629cc56581bca5c$export$812e1401122ad355).Completed
    });
    // Allow a paint cycle so the win animation starts
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    await $67c86676dfc84be7$var$delay(200);
    console.log('[TestRunner] seedWinState: state seeded, app state set to Completed');
}
// ──────────────────────────────────────────────────────────────────────────────
// UI / menu interaction executors
// ──────────────────────────────────────────────────────────────────────────────
async function $67c86676dfc84be7$var$executeTapMenuButton() {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    const center = $67c86676dfc84be7$var$getMenuButtonCenter();
    const touchEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchstart');
    // @ts-ignore — mock touch event
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(touchEvent);
    await $67c86676dfc84be7$var$delay(50);
    const endEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(100); // extra delay for DOM menu to show/hide
}
async function $67c86676dfc84be7$var$executeTapNewGameButton() {
    (0, $9c0551e961641722$export$d52ce18bcf83fe76)();
    const center = $67c86676dfc84be7$var$getNewGameButtonCenter();
    // Snapshot state before (to verify a new game actually started)
    const beforeState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    const beforeStockLen = beforeState.stock.length;
    const touchEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchstart');
    // @ts-ignore
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(touchEvent);
    await $67c86676dfc84be7$var$delay(50);
    const endEvent = $67c86676dfc84be7$var$createMockTouch(center.x, center.y, 'touchend');
    // @ts-ignore
    (0, $9c0551e961641722$export$4f10243649651f2b)(endEvent);
    await $67c86676dfc84be7$var$waitForAnimations();
    // startNewGame() is async (loads images), wait for it to settle
    await $67c86676dfc84be7$var$delay(500);
}
async function $67c86676dfc84be7$var$executeTapMenuHitArea(hitAreaId) {
    // Ensure a paint has run so hit areas are populated
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    await $67c86676dfc84be7$var$delay(100);
    // Execute the menu action directly (same as what canvasMouseMethods does)
    const handled = (0, $b72613e90817bb5b$export$51adfcee1e25bd69)(hitAreaId);
    if (!handled) throw new Error(`tapMenuHitArea: executeMenuAction returned false for '${hitAreaId}'. ` + `Is the menu open? appState.currentAppState = ${(0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState}`);
    // Theme changes and new-game are async (image reload) — wait generously
    if (hitAreaId.startsWith('theme-') || hitAreaId === 'new-game') await $67c86676dfc84be7$var$delay(500);
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    await $67c86676dfc84be7$var$waitForAnimations();
    await $67c86676dfc84be7$var$delay(100);
}
function $67c86676dfc84be7$var$executeAssertAppState(expected, description) {
    const stateMap = {
        Playing: (0, $7629cc56581bca5c$export$812e1401122ad355).Playing,
        Menu: (0, $7629cc56581bca5c$export$812e1401122ad355).Menu,
        Completed: (0, $7629cc56581bca5c$export$812e1401122ad355).Completed
    };
    const expectedValue = stateMap[expected];
    const actual = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState;
    if (actual !== expectedValue) {
        const reverseMap = [
            'Playing',
            'Menu',
            'Completed'
        ];
        console.error(`[TestRunner] ASSERT APP STATE FAILED: ${description}. ` + `Expected ${expected} (${expectedValue}), got ${reverseMap[actual]} (${actual})`);
        return false;
    }
    return true;
}
function $67c86676dfc84be7$var$executeAssertSetting(key, expected, description) {
    const actual = (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings[key];
    if (actual !== expected) {
        console.error(`[TestRunner] ASSERT SETTING FAILED: ${description}. ` + `Expected settings.${key} = ${JSON.stringify(expected)}, got ${JSON.stringify(actual)}`);
        return false;
    }
    return true;
}
// ──────────────────────────────────────────────────────────────────────────────
// Visual status overlay (visible on screen for simulator/device testing)
// ──────────────────────────────────────────────────────────────────────────────
let $67c86676dfc84be7$var$statusOverlay = null;
function $67c86676dfc84be7$var$isOverlaySuppressedForScreenshots() {
    const nativeScreenshotMode = window.__nativeScreenshotMode === true;
    if (nativeScreenshotMode) return true;
    const params = new URLSearchParams(window.location.search);
    const screenshotParam = params.get('screenshot');
    const overlayParam = params.get('overlay');
    return screenshotParam === '1' || overlayParam === '0' || overlayParam === 'off';
}
function $67c86676dfc84be7$var$createStatusOverlay() {
    if ($67c86676dfc84be7$var$isOverlaySuppressedForScreenshots()) return;
    $67c86676dfc84be7$var$statusOverlay = document.createElement('div');
    $67c86676dfc84be7$var$statusOverlay.id = 'test-runner-status';
    $67c86676dfc84be7$var$statusOverlay.style.cssText = [
        'position: fixed',
        'top: 40px',
        'left: 8px',
        'right: 8px',
        'padding: 8px 12px',
        'background: rgba(0, 0, 0, 0.85)',
        'color: #00ff88',
        'font-family: monospace',
        'font-size: 9px',
        'line-height: 1.4',
        'border-radius: 8px',
        'z-index: 99999',
        'pointer-events: none',
        'white-space: pre-wrap',
        'max-height: 60px',
        'overflow: hidden'
    ].join('; ');
    document.body.appendChild($67c86676dfc84be7$var$statusOverlay);
}
function $67c86676dfc84be7$var$updateStatus(text, color = '#00ff88') {
    if ($67c86676dfc84be7$var$isOverlaySuppressedForScreenshots()) return;
    if (!$67c86676dfc84be7$var$statusOverlay) $67c86676dfc84be7$var$createStatusOverlay();
    $67c86676dfc84be7$var$statusOverlay.style.color = color;
    $67c86676dfc84be7$var$statusOverlay.textContent = text;
    // On failure (red), expand overlay to show full error + logs, and make it scrollable
    if (color === '#ff4444') {
        $67c86676dfc84be7$var$statusOverlay.style.maxHeight = '80vh';
        $67c86676dfc84be7$var$statusOverlay.style.overflow = 'auto';
        $67c86676dfc84be7$var$statusOverlay.style.pointerEvents = 'auto';
    }
}
function $67c86676dfc84be7$var$removeStatusOverlay() {
    if ($67c86676dfc84be7$var$statusOverlay) {
        $67c86676dfc84be7$var$statusOverlay.remove();
        $67c86676dfc84be7$var$statusOverlay = null;
    }
}
// ──────────────────────────────────────────────────────────────────────────────
// Debug log capture — captures console.log output during test for display
// ──────────────────────────────────────────────────────────────────────────────
let $67c86676dfc84be7$var$debugLogBuffer = [];
let $67c86676dfc84be7$var$originalConsoleLog = null;
function $67c86676dfc84be7$var$startCapturingLogs() {
    $67c86676dfc84be7$var$debugLogBuffer = [];
    $67c86676dfc84be7$var$originalConsoleLog = console.log;
    console.log = (...args)=>{
        const msg = args.map((a)=>typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
        $67c86676dfc84be7$var$debugLogBuffer.push(msg);
        $67c86676dfc84be7$var$originalConsoleLog(...args);
    };
}
function $67c86676dfc84be7$var$stopCapturingLogs() {
    if ($67c86676dfc84be7$var$originalConsoleLog) {
        console.log = $67c86676dfc84be7$var$originalConsoleLog;
        $67c86676dfc84be7$var$originalConsoleLog = null;
    }
}
function $67c86676dfc84be7$var$getRecentLogs(count = 20) {
    return $67c86676dfc84be7$var$debugLogBuffer.slice(-count).join('\n');
}
async function $67c86676dfc84be7$export$3b6f84c32428faac(actions, testName) {
    const startTime = Date.now();
    console.log(`
[TestRunner] \u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}`);
    console.log(`[TestRunner] Starting: ${testName}`);
    console.log(`[TestRunner] Actions: ${actions.length}`);
    console.log(`[TestRunner] \u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}
`);
    $67c86676dfc84be7$var$updateStatus(`TEST: ${testName}\n0/${actions.length} actions...`);
    $67c86676dfc84be7$var$startCapturingLogs();
    let completedActions = 0;
    for(let i = 0; i < actions.length; i++){
        const action = actions[i];
        const actionDesc = $67c86676dfc84be7$var$formatAction(action);
        const progressPct = Math.floor((i + 1) / actions.length * 100);
        try {
            switch(action.type){
                case 'tapStock':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapStock`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapStock`);
                    await $67c86676dfc84be7$var$executeTapStock();
                    break;
                case 'drag':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} drag ${action.cardId} \u{2192} ${action.target}`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: drag ${action.cardId} \u{2192} ${action.target}`);
                    await $67c86676dfc84be7$var$executeDrag(action.cardId, action.target);
                    break;
                case 'doubleTap':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} doubleTap ${action.cardId}`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: doubleTap ${action.cardId}`);
                    await $67c86676dfc84be7$var$executeDoubleTap(action.cardId);
                    break;
                case 'tapUndo':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapUndo`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapUndo`);
                    await $67c86676dfc84be7$var$executeTapUndo();
                    break;
                case 'tapAutoComplete':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapAutoComplete`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapAutoComplete...`);
                    await $67c86676dfc84be7$var$executeTapAutoComplete();
                    break;
                case 'assert':
                    {
                        console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} assert: ${action.description}`);
                        $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assert ${action.description}`);
                        const assertPassed = await $67c86676dfc84be7$var$executeAssert(action.condition, action.description);
                        if (!assertPassed) {
                            const result = {
                                passed: false,
                                totalActions: actions.length,
                                completedActions: completedActions,
                                failedAction: {
                                    index: i,
                                    action: action,
                                    error: `Assertion failed: ${action.description}`
                                },
                                duration: Date.now() - startTime
                            };
                            $67c86676dfc84be7$var$updateStatus(`FAILED at ${i + 1}/${actions.length}\nassert: ${action.description}`, '#ff4444');
                            return result;
                        }
                        break;
                    }
                case 'waitForWin':
                    {
                        console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} waitForWin`);
                        $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: waitForWin...`);
                        const won = await $67c86676dfc84be7$var$executeWaitForWin();
                        if (!won) {
                            const result = {
                                passed: false,
                                totalActions: actions.length,
                                completedActions: completedActions,
                                failedAction: {
                                    index: i,
                                    action: action,
                                    error: 'Game not won'
                                },
                                duration: Date.now() - startTime
                            };
                            $67c86676dfc84be7$var$updateStatus(`FAILED at ${i + 1}/${actions.length}\nGame not won!`, '#ff4444');
                            return result;
                        }
                        break;
                    }
                case 'seedWinState':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} seedWinState`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: seedWinState...`);
                    await $67c86676dfc84be7$var$executeSeedWinState();
                    break;
                // ── UI / menu interaction actions ──
                case 'tapMenuButton':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapMenuButton`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapMenuButton`);
                    await $67c86676dfc84be7$var$executeTapMenuButton();
                    break;
                case 'tapNewGameButton':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapNewGameButton`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tapNewGameButton`);
                    await $67c86676dfc84be7$var$executeTapNewGameButton();
                    break;
                case 'tapMenuHitArea':
                    console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} tapMenuHitArea ${action.hitAreaId}`);
                    $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: tap ${action.hitAreaId}`);
                    await $67c86676dfc84be7$var$executeTapMenuHitArea(action.hitAreaId);
                    break;
                case 'assertAppState':
                    {
                        console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} assertAppState: ${action.description}`);
                        $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assertAppState ${action.expected}`);
                        const appStatePassed = $67c86676dfc84be7$var$executeAssertAppState(action.expected, action.description);
                        if (!appStatePassed) {
                            const result = {
                                passed: false,
                                totalActions: actions.length,
                                completedActions: completedActions,
                                failedAction: {
                                    index: i,
                                    action: action,
                                    error: `App state assertion failed: ${action.description}`
                                },
                                duration: Date.now() - startTime
                            };
                            $67c86676dfc84be7$var$updateStatus(`FAILED at ${i + 1}/${actions.length}\nassertAppState: ${action.description}`, '#ff4444');
                            return result;
                        }
                        break;
                    }
                case 'assertSetting':
                    {
                        console.log(`[TestRunner] ${i + 1}/${actions.length} \u{2014} assertSetting: ${action.description}`);
                        $67c86676dfc84be7$var$updateStatus(`${testName} [${progressPct}%]\n${i + 1}/${actions.length}: assertSetting ${action.key}=${JSON.stringify(action.expected)}`);
                        const settingPassed = $67c86676dfc84be7$var$executeAssertSetting(action.key, action.expected, action.description);
                        if (!settingPassed) {
                            const result = {
                                passed: false,
                                totalActions: actions.length,
                                completedActions: completedActions,
                                failedAction: {
                                    index: i,
                                    action: action,
                                    error: `Setting assertion failed: ${action.description}`
                                },
                                duration: Date.now() - startTime
                            };
                            $67c86676dfc84be7$var$updateStatus(`FAILED at ${i + 1}/${actions.length}\nassertSetting: ${action.description}`, '#ff4444');
                            return result;
                        }
                        break;
                    }
            }
            completedActions++;
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : String(err);
            console.error(`[TestRunner] FAILED at action ${i + 1}: ${actionDesc}`);
            console.error(`[TestRunner] Error: ${errorMsg}`);
            // Log game state for debugging
            $67c86676dfc84be7$var$logGameState();
            $67c86676dfc84be7$var$stopCapturingLogs();
            const recentLogs = $67c86676dfc84be7$var$getRecentLogs(30);
            $67c86676dfc84be7$var$updateStatus(`FAILED at ${i + 1}/${actions.length}\n${actionDesc}\n${errorMsg}\n\n--- Recent Logs ---\n${recentLogs}`, '#ff4444');
            return {
                passed: false,
                totalActions: actions.length,
                completedActions: completedActions,
                failedAction: {
                    index: i,
                    action: action,
                    error: errorMsg
                },
                duration: Date.now() - startTime
            };
        }
    }
    const duration = Date.now() - startTime;
    $67c86676dfc84be7$var$stopCapturingLogs();
    console.log(`
[TestRunner] \u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}`);
    console.log(`[TestRunner] PASSED: ${testName}`);
    console.log(`[TestRunner] ${completedActions}/${actions.length} actions in ${(duration / 1000).toFixed(1)}s`);
    console.log(`[TestRunner] \u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}\u{2550}
`);
    $67c86676dfc84be7$var$updateStatus(`PASSED: ${testName}\n${completedActions}/${actions.length} actions in ${(duration / 1000).toFixed(1)}s`, '#00ff88');
    // Remove overlay after 60 seconds on success (long enough for screenshots)
    setTimeout($67c86676dfc84be7$var$removeStatusOverlay, 60000);
    return {
        passed: true,
        totalActions: actions.length,
        completedActions: completedActions,
        duration: duration
    };
}
// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────
function $67c86676dfc84be7$var$formatAction(action) {
    switch(action.type){
        case 'tapStock':
            return 'tapStock';
        case 'drag':
            return `drag ${action.cardId} \u{2192} ${action.target}`;
        case 'doubleTap':
            return `doubleTap ${action.cardId}`;
        case 'tapUndo':
            return 'tapUndo';
        case 'tapAutoComplete':
            return 'tapAutoComplete';
        case 'assert':
            return `assert: ${action.description}`;
        case 'waitForWin':
            return 'waitForWin';
        case 'seedWinState':
            return 'seedWinState';
        case 'tapMenuButton':
            return 'tapMenuButton';
        case 'tapNewGameButton':
            return 'tapNewGameButton';
        case 'tapMenuHitArea':
            return `tapMenuHitArea: ${action.hitAreaId}`;
        case 'assertAppState':
            return `assertAppState: ${action.expected} \u{2014} ${action.description}`;
        case 'assertSetting':
            return `assertSetting: ${action.key}=${JSON.stringify(action.expected)} \u{2014} ${action.description}`;
    }
}
function $67c86676dfc84be7$var$logGameState() {
    const state = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22)();
    console.log('[TestRunner] Current game state:');
    console.log(`  Stock: ${state.stock.length} cards`);
    console.log(`  Waste: ${state.waste.map((c)=>c.value + c.type).join(', ') || '(empty)'}`);
    for(let i = 0; i < state.tableau.length; i++){
        const pile = state.tableau[i];
        const display = pile.map((c)=>c.isFaceDown ? `[${c.value}${c.type}]` : c.value + c.type).join(' ');
        console.log(`  Tableau ${i}: ${display || '(empty)'}`);
    }
    for(let i = 0; i < state.foundation.length; i++){
        const pile = state.foundation[i];
        const top = pile.length > 0 ? pile[pile.length - 1].value + pile[pile.length - 1].type : '(empty)';
        console.log(`  Foundation ${i}: ${pile.length} cards, top: ${top}`);
    }
}













// ── Splash screen theme colouring ──────────────────────────────────
// Theme colours for the splash are now applied by an inline <script> in
// index.html so they take effect before any external resource loads.
// The applySplashTheme() IIFE that was here has been moved there.
// Cached safe area insets — read once to avoid DOM thrashing and resize loops
let $4e2df5caa97661a7$var$_cachedSafeAreaInsets = null;
/**
 * Resolve requested in-app test scenario key.
 * Supports either URL param (?test=draw1) or native injection from iOS simulator
 * launch env (window.__nativeTestScenario).
 */ function $4e2df5caa97661a7$var$getRequestedTestScenarioKey() {
    const urlParams = new URLSearchParams(window.location.search);
    const testParam = urlParams.get('test');
    if (testParam) return testParam;
    const nativeScenario = window.__nativeTestScenario;
    if (typeof nativeScenario === 'string' && nativeScenario.trim().length > 0) return nativeScenario.trim();
    return null;
}
// Read CSS env(safe-area-inset-*) values via a temporary DOM element.
// Results are cached; call invalidateSafeAreaCache() on orientation change to re-read.
function $4e2df5caa97661a7$var$getSafeAreaInsets() {
    if ($4e2df5caa97661a7$var$_cachedSafeAreaInsets) return $4e2df5caa97661a7$var$_cachedSafeAreaInsets;
    // Prefer native safe-area values injected by ViewController.swift (Cordova).
    // CSS env(safe-area-inset-*) returns 0 in Cordova's WKWebView, so the native
    // side bridges the real device insets via window.__nativeSafeArea.
    const native = window.__nativeSafeArea;
    if (native && (native.top > 0 || native.bottom > 0)) {
        $4e2df5caa97661a7$var$_cachedSafeAreaInsets = {
            top: native.top,
            bottom: native.bottom,
            left: native.left ?? 0,
            right: native.right ?? 0
        };
        return $4e2df5caa97661a7$var$_cachedSafeAreaInsets;
    }
    // Fallback: read CSS env() values (works in PWA / standalone mode).
    const div = document.createElement('div');
    div.style.cssText = "position:fixed;top:0;left:0;right:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px);";
    document.documentElement.appendChild(div);
    const cs = getComputedStyle(div);
    const result = {
        top: parseFloat(cs.paddingTop) || 0,
        bottom: parseFloat(cs.paddingBottom) || 0,
        left: parseFloat(cs.paddingLeft) || 0,
        right: parseFloat(cs.paddingRight) || 0
    };
    div.remove();
    $4e2df5caa97661a7$var$_cachedSafeAreaInsets = result;
    return result;
}
function $4e2df5caa97661a7$var$invalidateSafeAreaCache() {
    $4e2df5caa97661a7$var$_cachedSafeAreaInsets = null;
}
// Helper function to calculate card dimensions and spacing
function $4e2df5caa97661a7$var$calculateCardDimensions(windowHeight, windowWidth) {
    const isPortrait = windowHeight > windowWidth;
    // Read actual device safe area insets (Dynamic Island, home indicator, etc.)
    // In Cordova, these come from native Swift via window.__nativeSafeArea.
    // In PWA/standalone mode, they come from CSS env(safe-area-inset-*).
    // In Safari browser, the browser chrome already insets the viewport, so 0 is correct.
    const safeArea = $4e2df5caa97661a7$var$getSafeAreaInsets();
    // Top padding: use safe area inset if present, otherwise a minimal aesthetic buffer.
    const topPadding = safeArea.top > 0 ? safeArea.top + 5 : 10;
    // Bottom padding: similarly, safe area > 0 means we need to avoid the home indicator.
    const bottomPadding = safeArea.bottom > 0 ? safeArea.bottom + 5 : 10;
    if (isPortrait) {
        const usableHeight = windowHeight - topPadding - bottomPadding;
        // Uniform 9-row grid: all rows (buttons and cards) are the same height.
        // 9 rows of cardHeight + 8 gaps of cardSpacing = usableHeight
        const cardHeight = Math.floor(usableHeight / 9.5);
        const cardWidth = Math.floor(cardHeight * 0.75);
        const cardSpacing = Math.max(2, Math.floor((usableHeight - 9 * cardHeight) / 8));
        // cardYOffset is the top margin — first row starts here
        const cardXOffset = Math.max(topPadding, Math.floor(cardSpacing * 0.5));
        const foundationXOffset = cardHeight * 2 + cardSpacing * 3;
        return {
            cardHeight: cardHeight,
            cardWidth: cardWidth,
            cardSpacing: cardSpacing,
            cardXOffset: cardXOffset,
            foundationXOffset: foundationXOffset
        };
    } else {
        // Landscape/Desktop: optimize based on available space
        const availableHeight = windowHeight - topPadding - bottomPadding;
        const availableWidth = windowWidth * 0.95;
        // Calculate card size based on height constraint
        const maxCardHeightFromScreen = Math.floor(availableHeight / 8.56);
        const maxCardWidthFromScreen = Math.floor(availableWidth / 8);
        const maxCardHeightFromWidth = Math.floor(maxCardWidthFromScreen / 0.75);
        // Use the smaller of the two to ensure everything fits
        const cardHeight = Math.min(maxCardHeightFromScreen, maxCardHeightFromWidth);
        const cardWidth = Math.floor(cardHeight * 0.75);
        const totalGapSpace = availableHeight - 8 * cardHeight;
        const cardSpacing = Math.max(2, Math.floor(totalGapSpace / 7));
        const cardXOffset = Math.max(topPadding, Math.floor(cardSpacing * 3.75));
        const foundationXOffset = cardHeight * 2 + cardSpacing * 3;
        return {
            cardHeight: cardHeight,
            cardWidth: cardWidth,
            cardSpacing: cardSpacing,
            cardXOffset: cardXOffset,
            foundationXOffset: foundationXOffset
        };
    }
}
(function loadCordova() {
    const script = document.createElement('script');
    script.src = 'cordova.js';
    script.onerror = ()=>{
    // Expected to fail in browser dev mode — cordova.js only exists in Cordova builds
    };
    document.head.appendChild(script);
    // Dismiss the native Cordova splash screen once cordova.js is ready.
    // AutoHideSplashScreen=false keeps the native splash visible during WKWebView
    // cold-start. We dismiss it here (after deviceready) so our HTML splash is
    // already painted underneath — seamless transition, no blank gap.
    document.addEventListener('deviceready', ()=>{
        // Double-rAF ensures the browser has composited a frame with the HTML splash
        // before we pull the native splash away.
        requestAnimationFrame(()=>{
            requestAnimationFrame(()=>{
                var _cordova;
                // cordova-ios 8 built-in CDVLaunchScreen plugin
                if ((_cordova = window.cordova) !== null && _cordova !== void 0 && _cordova.exec) window.cordova.exec(null, null, 'LaunchScreen', 'hide', []);
                else if (navigator.splashscreen) navigator.splashscreen.hide();
            });
        });
    });
})();
window.__onNativeSafeArea = ()=>{
    $4e2df5caa97661a7$var$invalidateSafeAreaCache();
    // If pageSetup hasn't run yet the new values will be picked up naturally.
    // If layout has already been calculated, recalculate now.
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight > 0) {
        const dimensions = $4e2df5caa97661a7$var$calculateCardDimensions((0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight, (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth);
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth = dimensions.cardWidth;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight = dimensions.cardHeight;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing = dimensions.cardSpacing;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardYOffset = dimensions.cardXOffset;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).foundationXOffset = dimensions.foundationXOffset;
        for(let i = 0; i < (0, $13e781a56ac394f5$export$f524c3b49520a9b3).length; i++)(0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i] = (0, $f593745b1b99abdc$export$245b33143bc6e84d)((0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i]);
        (0, $d7e89e4a237bfcb6$export$384cd953322e3851)((0, $13e781a56ac394f5$export$f524c3b49520a9b3));
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    }
};
window.addEventListener('load', async ()=>{
    // Record the moment the HTML splash is actually on-screen.
    // In Cordova the native splash may run for seconds before the webview
    // renders, so performance.now() at module-eval time is unreliable.
    $4e2df5caa97661a7$var$_splashVisibleAt = performance.now();
    // ── Debug & test globals ──────────────────────────────────────────
    // Exposed on window so e2e tests (Playwright) and the in-app test runner
    // can inspect / manipulate game state programmatically.
    // These carry no security risk in a single-player offline card game.
    //@ts-ignore
    window.draw = (0, $71e2b16f726ebff4$export$4e0651d2e7bf59d0);
    //@ts-ignore
    window.paint = (0, $71e2b16f726ebff4$export$4e0651d2e7bf59d0);
    //@ts-ignore
    window.cardsState = (0, $13e781a56ac394f5$export$f524c3b49520a9b3);
    //@ts-ignore
    window.cardState = (0, $13e781a56ac394f5$export$f524c3b49520a9b3);
    //@ts-ignore
    window.appState = (0, $13e781a56ac394f5$export$b72e390a9ebe1648);
    //@ts-ignore
    window.getDraggedCards = (0, $13e781a56ac394f5$export$7f3c49b37340bfce);
    //@ts-ignore
    window.getCurrentCardState = (0, $13e781a56ac394f5$export$23a3c12b66b5ee22);
    //@ts-ignore
    window.setCardState = (0, $13e781a56ac394f5$export$24db6d4500eb94db);
    //@ts-ignore — e2e tests: seed partial states, disabling integrity checks for the session
    window.seedCardState = (state)=>{
        (0, $6cec517bcba19c3d$export$9bce854bb9edf084)();
        (0, $13e781a56ac394f5$export$24db6d4500eb94db)(state, {
            skipIntegrity: true
        });
    };
    //@ts-ignore — e2e tests: manually disable/enable integrity if needed
    window.disableCardIntegrity = (0, $6cec517bcba19c3d$export$9bce854bb9edf084);
    //@ts-ignore
    window.assignCardCoordinates = (0, $f593745b1b99abdc$export$245b33143bc6e84d);
    //@ts-ignore
    window.menuButtonX = (0, $0f5b71ede2b50336$export$1f614aa49af839fa);
    //@ts-ignore
    window.menuButtonY = (0, $0f5b71ede2b50336$export$6d939bb5033370b0);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.undoButtonX = (0, $0f5b71ede2b50336$export$26fe8f1cbe2626e5);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.undoButtonY = (0, $0f5b71ede2b50336$export$70c3227cdbebd1b8);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.newGameButtonX = (0, $0f5b71ede2b50336$export$833ed0fe1212d409);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.newGameButtonY = (0, $0f5b71ede2b50336$export$f81ed66b3906beb3);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.autoCompleteButtonX = (0, $0f5b71ede2b50336$export$51646a58b06b5693);
    //@ts-ignore — e2e tests: button hitbox coordinates
    window.autoCompleteButtonY = (0, $0f5b71ede2b50336$export$ff0ff2fb1fcb2a95);
    //@ts-ignore — e2e tests use this to execute menu actions without tapping canvas
    window.executeMenuAction = (0, $b72613e90817bb5b$export$51adfcee1e25bd69);
    //@ts-ignore — e2e tests: inspect current canvas menu hit areas
    window.getMenuHitAreas = (0, $41c329ef74cc3b7d$export$2b9dad546685a10e);
    //@ts-ignore — e2e tests: overlap detection debugging
    window.findOverlapTableau = (0, $6fa52cac1c396f76$export$311462252effb6d);
    //@ts-ignore — e2e tests: point-based tableau detection
    window.isCardOverTableau = (0, $6fa52cac1c396f76$export$42985f28b70fda0f);
    //@ts-ignore — e2e tests: get card rendered position (including drag overlay)
    window.getCardPosition = (0, $f08e1321bb9509a9$export$1a73458087cc41c4);
    //@ts-ignore — e2e tests: programmatic double-tap (bypasses pickup/undo race)
    window.canvasTouchDoubleTap = (0, $9c0551e961641722$export$6a99faad2978514d);
    //@ts-ignore — e2e tests: clear animation overlays before programmatic interactions
    window.clearAllAnimationPositions = (0, $f08e1321bb9509a9$export$60916053becc6a4);
    //@ts-ignore — e2e tests: clear drag overlays before programmatic interactions
    window.clearAllDragPositions = (0, $f08e1321bb9509a9$export$ee060b92c30dc31a);
    //@ts-ignore — dev: get current timer elapsed time in ms
    window.timerGetElapsed = (0, $d639f68dfbcef401$export$611ce66014572f0c);
    //@ts-ignore — dev: format timer time
    window.formatTime = (0, $d639f68dfbcef401$export$3203edd9e5edd663);
    //@ts-ignore — dev: load game statistics
    window.loadStatistics = (0, $4115af5d1675323d$export$ef8a82cde02bb06f);
    //@ts-ignore — e2e debug: number of leaderboard draw calls
    window.getDrawRecordsDebugCounter = (0, $d7974594ecddd420$export$55eda3bb7df9efea);
    //@ts-ignore — e2e debug: reset leaderboard draw counter
    window.resetDrawRecordsDebugCounter = (0, $d7974594ecddd420$export$83718a232b40ec35);
    //@ts-ignore — e2e debug: latest render telemetry snapshot
    window.getRenderDebugSnapshot = (0, $de009be455ddb921$export$c4bbdd756e8fe93b);
    //@ts-ignore — e2e debug: reset render telemetry frame counter
    window.resetRenderDebugFrameCounter = (0, $de009be455ddb921$export$ba8727a6700971b9);
    //@ts-ignore
    window.undo = ()=>{
        (0, $13e781a56ac394f5$export$21f930c44940fd98)();
        (0, $71e2b16f726ebff4$export$4e0651d2e7bf59d0)();
    };
    //@ts-ignore — dev command: start a new game with a deterministic seed
    window.newGameWithSeed = (seed)=>{
        (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)((0, $856e591c5d33ab08$export$a17f8b0554f18154)(seed));
    };
    //@ts-ignore — dev command: run a test scenario by name ('draw1' or 'draw3')
    window.runTest = async (name)=>{
        const scenario = (0, $7f9b59b6f74cfc3d$export$22f2203758701f7f)[name];
        if (!scenario) {
            console.error(`Unknown test scenario: ${name}. Available: ${Object.keys((0, $7f9b59b6f74cfc3d$export$22f2203758701f7f)).join(', ')}`);
            return;
        }
        // Set draw count and start seeded game, then run the test
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED = scenario.drawCount;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount = scenario.drawCount;
        await (0, $856e591c5d33ab08$export$309ef6b7bd9750ee)(scenario.seed);
        // Wait for paint
        await new Promise((r)=>setTimeout(r, 500));
        return (0, $67c86676dfc84be7$export$3b6f84c32428faac)(scenario.actions, scenario.name);
    };
    await $4e2df5caa97661a7$var$pageSetup();
});
// Debounced resize handler to recalculate layout and repaint
let $4e2df5caa97661a7$var$resizeTimeout = null;
window.addEventListener('resize', ()=>{
    // Clear previous timeout
    if ($4e2df5caa97661a7$var$resizeTimeout) clearTimeout($4e2df5caa97661a7$var$resizeTimeout);
    // Set new timeout to trigger layout recalculation after resize stops
    $4e2df5caa97661a7$var$resizeTimeout = window.setTimeout(()=>{
        // Update window dimensions
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        // Skip recalculation if dimensions haven't actually changed
        // (prevents unnecessary reflows from spurious resize events on iOS)
        if (windowHeight === (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight && windowWidth === (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth) {
            $4e2df5caa97661a7$var$resizeTimeout = null;
            return;
        }
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowWidth = windowWidth;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).windowHeight = windowHeight;
        // Orientation may have changed — invalidate cached safe area insets
        $4e2df5caa97661a7$var$invalidateSafeAreaCache();
        // Recalculate card dimensions using shared function
        const dimensions = $4e2df5caa97661a7$var$calculateCardDimensions(windowHeight, windowWidth);
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardWidth = dimensions.cardWidth;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardHeight = dimensions.cardHeight;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardSpacing = dimensions.cardSpacing;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).cardYOffset = dimensions.cardXOffset;
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).foundationXOffset = dimensions.foundationXOffset;
        // Reassign coordinates for ALL states in history (not just current)
        // so undo doesn't reveal states with stale positions from old dimensions
        for(let i = 0; i < (0, $13e781a56ac394f5$export$f524c3b49520a9b3).length; i++){
            const state = (0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i];
            (0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i] = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(state);
        }
        // Persist updated positions without adding a new history entry
        (0, $d7e89e4a237bfcb6$export$384cd953322e3851)((0, $13e781a56ac394f5$export$f524c3b49520a9b3));
        // Repaint with new layout
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
        $4e2df5caa97661a7$var$resizeTimeout = null;
    }, 300); // 300ms debounce delay
});
window.addEventListener('keydown', (e)=>{
    if (e.code === 'KeyS') console.log({
        cardsState: (0, $13e781a56ac394f5$export$f524c3b49520a9b3)
    });
});
window.addEventListener('touchstart', (e)=>{
    (0, $9c0551e961641722$export$78c9c5244ea20a82)(e);
}, {
    passive: false
});
window.addEventListener('touchend', (e)=>{
    (0, $9c0551e961641722$export$4f10243649651f2b)(e);
}, {
    passive: false
});
window.addEventListener('touchmove', (e)=>{
    (0, $9c0551e961641722$export$d6664ee70a10ed8)(e);
}, {
    passive: false
});
window.addEventListener('touchcancel', ()=>{
    (0, $9c0551e961641722$export$7b185ad29836087)();
}, {
    passive: false
});
window.addEventListener('gestureend', (e)=>{
    e.preventDefault();
}, {
    passive: false
});
window.addEventListener('mousedown', (e)=>{
    (0, $9c0551e961641722$export$7fb80c6ef4180b1d)(e);
}, {
    passive: false
});
window.addEventListener('mousemove', (e)=>{
    (0, $9c0551e961641722$export$2c97df70bc0ca128)(e);
}, {
    passive: false
});
window.addEventListener('mouseup', (e)=>{
    (0, $9c0551e961641722$export$c1df85069b48cecd)(e);
}, {
    passive: false
});
window.addEventListener('dblclick', (e)=>{
    (0, $9c0551e961641722$export$130c6f09e6943579)(e);
}, {
    passive: false
});
// Ensure settings are saved before page unload
window.addEventListener('beforeunload', ()=>{
    (0, $d7e89e4a237bfcb6$export$44d8bb90d8f68c33)();
    (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
    // Pause timer to persist accumulated time (includes current session)
    if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing) (0, $d639f68dfbcef401$export$8b6a3625bd310ba)();
});
// Save settings and pause timer when tab becomes hidden (mobile/desktop tab switching)
document.addEventListener('visibilitychange', ()=>{
    if (document.visibilityState === 'hidden') {
        (0, $d7e89e4a237bfcb6$export$44d8bb90d8f68c33)();
        (0, $b66bebf5d76faeef$export$4b80e08960f15b4f)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings);
        if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && (0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $d639f68dfbcef401$export$8b6a3625bd310ba)();
    } else if (document.visibilityState === 'visible') // Resume timer when returning to the tab (only if game is actively playing)
    {
        if ((0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentAppState === (0, $7629cc56581bca5c$export$812e1401122ad355).Playing && (0, $d639f68dfbcef401$export$58037dfed0af18fd)()) (0, $d639f68dfbcef401$export$1cd74d15b99f2af2)();
    }
});
// Repaint when fullscreen state changes so the button icon updates
document.addEventListener('fullscreenchange', ()=>{
    (0, $71e2b16f726ebff4$export$adff1037b5320216)();
});
async function $4e2df5caa97661a7$var$pageSetup() {
    try {
        var _document, _document$documentEle, _document$documentEle2, _document2, _document2$documentEl, _document2$documentEl2;
        // Calculate the card info
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const dimensions = $4e2df5caa97661a7$var$calculateCardDimensions(windowHeight, windowWidth);
        (0, $0f19a74577fb54d1$export$19710ec455966cdd)();
        (0, $7b135972b663a872$export$1cb815828ffae716)();
        (0, $baf55d86e4270add$export$cdd97182bd92cfbe)();
        (0, $856e591c5d33ab08$export$44efdc0af4678c13)();
        await (0, $856e591c5d33ab08$export$ec4cc2ebfa55c9bd)();
        (0, $13e781a56ac394f5$export$c84b80ff4ece2444)(windowHeight);
        (0, $13e781a56ac394f5$export$316dc132b4fb9db7)(windowWidth);
        (0, $13e781a56ac394f5$export$b24d7405fdca8614)(dimensions.cardHeight);
        (0, $13e781a56ac394f5$export$c0ce2c2b7e283ee9)(dimensions.cardWidth);
        (0, $13e781a56ac394f5$export$d6589dee5c518fd4)(dimensions.cardSpacing);
        (0, $13e781a56ac394f5$export$c8e45da93f45c61a)(dimensions.cardXOffset);
        (0, $13e781a56ac394f5$export$7e3aaf3eaa60fb9)(dimensions.foundationXOffset);
        // Detect standalone/PWA/Cordova mode — hides fullscreen button
        (0, $13e781a56ac394f5$export$b72e390a9ebe1648).isStandalone = window.matchMedia('(display-mode: standalone)').matches || // @ts-ignore — iOS Safari standalone property
        window.navigator.standalone === true || document.referrer.includes('android-app://') || // @ts-ignore — Cordova global
        window.cordova !== undefined;
        // Check for automated test scenario request from URL (?test=...)
        // or native simulator launch env (injected into window.__nativeTestScenario)
        const requestedScenarioKey = $4e2df5caa97661a7$var$getRequestedTestScenarioKey();
        const urlParams = new URLSearchParams(window.location.search);
        const testScenario = requestedScenarioKey ? (0, $7f9b59b6f74cfc3d$export$22f2203758701f7f)[requestedScenarioKey] : null;
        // Reset settings to known defaults for test runs so tests don't depend
        // on localStorage state left behind by previous test runs
        if (testScenario) {
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings = {
                ...(0, $544aeff89210c458$export$62680b65e63e14ec)
            };
            // Reset theme to match default settings
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme = (0, $0e92ad87971fbdf7$export$d9a33280f07116d9)[(0, $544aeff89210c458$export$62680b65e63e14ec).themeName] ?? (0, $0e92ad87971fbdf7$export$d9a33280f07116d9).HappyPurpleTheme;
        }
        // If the test scenario specifies a handedness, apply it before the URL param
        // override so ?hand= can still take precedence for debugging
        if (testScenario !== null && testScenario !== void 0 && testScenario.handedness) (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness = testScenario.handedness;
        // A ?hand=left or ?hand=right URL param overrides handedness (dev/testing)
        const handParam = urlParams.get('hand');
        if (handParam === 'left' || handParam === 'right') (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.handedness = handParam;
        // A ?seed= URL param forces a fresh deterministic deal (dev/testing),
        // bypassing any saved state from localStorage.
        const urlSeed = urlParams.get('seed');
        // If running a test scenario, override the seed and draw count
        const effectiveSeed = testScenario ? testScenario.seed : urlSeed != null ? (0, $856e591c5d33ab08$export$a17f8b0554f18154)(urlSeed) : null;
        if (testScenario) {
            // Configure draw count for the test scenario
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).NUM_CARDS_FLIPPED = testScenario.drawCount;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.drawCount = testScenario.drawCount;
        }
        const stateRestored = effectiveSeed == null ? (0, $13e781a56ac394f5$export$812268ebd28ecdbb)() : false;
        if (effectiveSeed != null) {
            // Seeded game (or test): ignore saved state, deal a deterministic hand
            (0, $d639f68dfbcef401$export$81b8579df16e1cc0)();
            const shuffledDeck = (0, $856e591c5d33ab08$export$448332262467e042)(effectiveSeed, false);
            (0, $856e591c5d33ab08$export$85698d8a2a9e7bc)(shuffledDeck);
        } else if (stateRestored) {
            // State was restored from localStorage
            // Restore the timer so elapsed time persists across page refreshes
            (0, $d639f68dfbcef401$export$7cdbdaf1080d01f1)();
            // Reassign coordinates for ALL states in history, not just the current one.
            // Older states may have positions from a different screen size / orientation.
            // If undo() reveals one of those states, stale positions would cause cards to jump.
            for(let i = 0; i < (0, $13e781a56ac394f5$export$f524c3b49520a9b3).length; i++){
                const state = (0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i];
                const updatedState = (0, $f593745b1b99abdc$export$245b33143bc6e84d)(state);
                (0, $13e781a56ac394f5$export$f524c3b49520a9b3)[i] = updatedState;
            }
            // Persist the updated positions
            (0, $d7e89e4a237bfcb6$export$384cd953322e3851)((0, $13e781a56ac394f5$export$f524c3b49520a9b3));
            // Check if the restored game was already won
            if ((0, $13e781a56ac394f5$export$7601ebd7d9b3975b)()) {
                (0, $13e781a56ac394f5$export$f4a51cc8ad86d45d)();
                const newAppState = {
                    ...(0, $13e781a56ac394f5$export$b72e390a9ebe1648)
                };
                newAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Completed;
                (0, $13e781a56ac394f5$export$8921d147d4d1b7)(newAppState);
            } else // Game is still in progress — resume the timer
            (0, $d639f68dfbcef401$export$4f359d78fc926e81)();
        } else {
            // No saved state, start a new game with random shuffle
            (0, $d639f68dfbcef401$export$81b8579df16e1cc0)();
            const shuffledDeck = (0, $856e591c5d33ab08$export$448332262467e042)();
            (0, $856e591c5d33ab08$export$85698d8a2a9e7bc)(shuffledDeck);
        }
        (_document = document) === null || _document === void 0 || (_document$documentEle = _document.documentElement) === null || _document$documentEle === void 0 || (_document$documentEle2 = _document$documentEle.style) === null || _document$documentEle2 === void 0 || _document$documentEle2.setProperty('--currentBackgroundColor', (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.backgroundColor);
        (_document2 = document) === null || _document2 === void 0 || (_document2$documentEl = _document2.documentElement) === null || _document2$documentEl === void 0 || (_document2$documentEl2 = _document2$documentEl.style) === null || _document2$documentEl2 === void 0 || _document2$documentEl2.setProperty('--currentHighlightColor', (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme.highlightColor);
        // Set favicon to match the active theme
        (0, $b72613e90817bb5b$export$968c68bd88b425a3)((0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.themeName);
        // A ?menu=1 URL param opens the menu immediately (for screenshots/dev)
        if (urlParams.get('menu') === '1') {
            const newAppState = {
                ...(0, $13e781a56ac394f5$export$b72e390a9ebe1648)
            };
            newAppState.currentAppState = (0, $7629cc56581bca5c$export$812e1401122ad355).Menu;
            (0, $13e781a56ac394f5$export$8921d147d4d1b7)(newAppState);
        }
        // A ?theme=ThemeName URL param overrides the active theme (dev/testing)
        const themeParam = urlParams.get('theme');
        if (themeParam && themeParam in (0, $0e92ad87971fbdf7$export$d9a33280f07116d9)) {
            var _document3, _document3$documentEl, _document3$documentEl2, _document4, _document4$documentEl, _document4$documentEl2;
            const theme = (0, $0e92ad87971fbdf7$export$d9a33280f07116d9)[themeParam];
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).currentTheme = theme;
            (0, $13e781a56ac394f5$export$b72e390a9ebe1648).settings.themeName = themeParam;
            (_document3 = document) === null || _document3 === void 0 || (_document3$documentEl = _document3.documentElement) === null || _document3$documentEl === void 0 || (_document3$documentEl2 = _document3$documentEl.style) === null || _document3$documentEl2 === void 0 || _document3$documentEl2.setProperty('--currentBackgroundColor', theme.backgroundColor);
            (_document4 = document) === null || _document4 === void 0 || (_document4$documentEl = _document4.documentElement) === null || _document4$documentEl === void 0 || (_document4$documentEl2 = _document4$documentEl.style) === null || _document4$documentEl2 === void 0 || _document4$documentEl2.setProperty('--currentHighlightColor', theme.highlightColor);
            (0, $856e591c5d33ab08$export$44efdc0af4678c13)();
            (0, $856e591c5d33ab08$export$ec4cc2ebfa55c9bd)();
            (0, $b72613e90817bb5b$export$968c68bd88b425a3)(themeParam);
        }
        (0, $71e2b16f726ebff4$export$adff1037b5320216)();
    } catch (error) {
        console.error('pageSetup() failed:', error);
    } finally{
        // Always hide loading screen, even if setup fails —
        // a broken game is better than an eternal loading screen
        $4e2df5caa97661a7$var$hideLoadingScreen();
        // If a test scenario was requested, start the test runner after a short delay
        // to allow the first paint to render
        const scenarioKey = $4e2df5caa97661a7$var$getRequestedTestScenarioKey();
        const scenario = scenarioKey ? (0, $7f9b59b6f74cfc3d$export$22f2203758701f7f)[scenarioKey] : null;
        if (scenario) setTimeout(async ()=>{
            const result = await (0, $67c86676dfc84be7$export$3b6f84c32428faac)(scenario.actions, scenario.name);
            // Log final result prominently
            if (result.passed) console.log(`%c TEST PASSED: ${scenario.name} \u{2014} ${result.completedActions} actions in ${(result.duration / 1000).toFixed(1)}s`, 'color: #00cc00; font-size: 16px; font-weight: bold');
            else {
                var _result$failedAction, _result$failedAction2;
                console.error(`%c TEST FAILED: ${scenario.name} at action ${(_result$failedAction = result.failedAction) === null || _result$failedAction === void 0 ? void 0 : _result$failedAction.index}: ${(_result$failedAction2 = result.failedAction) === null || _result$failedAction2 === void 0 ? void 0 : _result$failedAction2.error}`, 'color: #ff0000; font-size: 16px; font-weight: bold');
            }
        }, 500);
    }
}
// Minimum time (ms) the HTML splash must be visible *after* the window 'load'
// event fires.  In Cordova the native splash screen may display for several
// seconds before the webview starts, so performance.now() at module-evaluation
// time can already exceed any absolute threshold.  Instead we record a
// timestamp when 'load' fires (= when our HTML splash is first painted) and
// measure relative to that.
let $4e2df5caa97661a7$var$_splashVisibleAt = 0;
const $4e2df5caa97661a7$var$MINIMUM_SPLASH_MS = 2000;
function $4e2df5caa97661a7$var$hideLoadingScreen() {
    const loadingScreen = document.getElementById('loading-screen');
    if (!loadingScreen) return;
    // ?splash=hold keeps it visible indefinitely (dev/screenshot tool)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('splash') === 'hold') return;
    // Skip minimum display time for automated tests — no need to wait 2s
    const isTestRun = $4e2df5caa97661a7$var$getRequestedTestScenarioKey() != null;
    const elapsed = performance.now() - $4e2df5caa97661a7$var$_splashVisibleAt;
    const remaining = isTestRun ? 0 : Math.max(0, $4e2df5caa97661a7$var$MINIMUM_SPLASH_MS - elapsed);
    setTimeout(()=>{
        loadingScreen.classList.add('fade-out');
        // Remove from DOM after fade-out animation completes
        setTimeout(()=>{
            loadingScreen.remove();
        }, 600);
    }, remaining);
}


//# sourceMappingURL=repo.0f996b11.js.map
